import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  NZ_DRAWER_DATA,
  NzDrawerRef,
  NzDrawerService,
} from 'ng-zorro-antd/drawer';
import {
  Company,
  ConfirmationModalComponent,
  CrmTag,
  GetParams,
  GetResponse,
  PostResponse,
  TableColumn,
  ToastConfig,
} from '@app/shared';
import {
  Activity,
  ActivityService,
  BUDGET_STAGES,
  BudgetStage,
  CommunicationService,
  DocumentService,
  DRAFT_BUDGET_STAGE,
  DraftBudgetStage,
  DrawerEscapeBlockerService,
  findBudgetStageBlock,
  getSafeImageUrl,
  isExpirationAlert,
  OpportunityService,
  PARENT_ENTITY_TYPE,
  PartnerService,
  ProjectService,
  ScreenService,
} from '@app/core';
import { GlobalValuesService } from '@app/core';
import { resolveNewQuotationCurrencyId } from '@app/core';
import { AuthService } from '../../../../core/services/auth.service';
import { FormBuilder, FormGroup } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { OpportunitiesUtilsService } from '../../services/opportunities-utils.service';
import { OpportunityDetailsHelperService } from '../../services/opportunity-details-helper.service';
import { OpportunityStageService } from '../../services/opportunity-stage.service';
import { OpportunityPayloadBuilderService } from '../../services/opportunity-payload-builder.service';
import { isProjectAvailableForOpportunity } from '../../services/project-availability.util';
import {
  buildSaleOrderHeaderPayload,
  isSuccessfulSaleOrderUpdate,
} from './sale-order-update-payload.utils';
import { buildLeadDetailsGetParams } from './lead-details-request.utils';
import {
  buildPastExpirationConfirmation,
  buildIssueCurrencyIgnoredMessage,
  canGenerateBudgetVersion,
  formatBudgetVersionGeneratedMessage,
  getPastBudgetExpiration,
  mapBudgetVersionGenerationError,
  wasIssueCurrencyIgnored,
} from '../../components/budget-pdf-versions/budget-version-generation.utils';
import {
  BudgetIssueCurrencyPickerData,
  openBudgetIssueCurrencyPicker,
} from '../../components/budget-issue-currency-picker/budget-issue-currency-picker.component';
import { openSourTrackQuotationPicker } from '../../components/sourtrack-sync/sourtrack-quotation-picker.component';
import {
  SourTrackSeverity,
  canRetrySourTrackSync,
  hasSendableQuotation,
  listCandidateQuotations,
} from '../../components/sourtrack-sync/sourtrack-sync.utils';
import {
  SourTrackFlowOutcome,
  SourTrackSyncFlowService,
} from '../../services/sourtrack-sync-flow.service';
import {
  resolveSaleOrderName,
  resolveSaleOrderNavigationReference,
} from '../../quotation-name-display.util';
import {
  catchError,
  combineLatest,
  filter,
  finalize,
  forkJoin,
  map,
  Observable,
  of,
  take,
  timeout,
} from 'rxjs';
import { OportunitiesAddDrawerComponent } from '../oportunities-add-drawer/oportunities-add-drawer.component';
import { CreateProjectDrawerComponent } from '../../../project/drawers/create-project-drawer/create-project-drawer.component';
import { ConvertLeadDrawerComponent } from '../convert-lead-drawer/convert-lead-drawer.component';
import {
  ActivatedRoute,
  NavigationEnd,
  NavigationStart,
  Router,
} from '@angular/router';
import { Location } from '@angular/common';
import { OportunitiesDetailsActivityDrawerComponent } from '../oportunities-details-activity-drawer/oportunities-details-activity-drawer.component';
import { SendBudgetDrawerComponent } from '../send-budget-drawer/send-budget-drawer.component';

/** CEC-692. Un guardado de términos con su cotización y su payload congelados en el momento de pedirlo. */
interface BudgetTermsSaveRequest {
  budgetId: number;
  payload: any;
  value: string;
  previousValue?: string;
}

@Component({
  selector: 'app-oportunities-details-drawer',
  standalone: false,
  templateUrl: './oportunities-details-drawer.component.html',
  styleUrl: './oportunities-details-drawer.component.less',
})
export class OportunitiesDetailsDrawerComponent
  implements OnInit, AfterViewInit, OnChanges
{
  nzData: {
    columnsData: TableColumn[];
    element: any; // Using 'any' to handle possible differences in the Partner model
    maestroName: string;
    editPermissions: boolean;
    opportunityList?: any[]; // Lista de oportunidades para navegación
    openedFromActivities?: boolean; // Flag para indicar que viene de activities
  } = inject(NZ_DRAWER_DATA);

  opportunity: any = null;
  columnsData: TableColumn[] = [];
  documents: any[] = [];
  stages: any[] = [];
  companies: any[] = [];
  costCenters: any[] = [];
  tags: CrmTag[] = [];
  lostReasons: any[] = [];

  loading: boolean = false;
  markingAsLost: boolean = false;
  recoveringLead: boolean = false;
  markAsLostModalRef: NzModalRef | null = null;
  lostForm!: FormGroup;
  isLost: boolean = false;
  isWon: boolean = false;
  afterViewInit: boolean = false;
  activeTabIndex: number = 0;
  isLead: boolean = false;
  private opportunityBackup: any = null;
  private originalOpportunityName: string = '';

  // Actividades
  activities: Activity[] = [];
  activitiesGroupedByDate: {
    date: string;
    label: string;
    activities: Activity[];
  }[] = [];
  loadingActivities: boolean = false;
  private activitiesLoaded = false;
  canCreateQuote: boolean = false;

  // View mode: 'opportunity' | 'lead' | 'budget'
  viewMode: 'opportunity' | 'lead' | 'budget' = 'opportunity';

  // Budget view data
  budget: any = null;
  budgetList: any[] = [];
  currentBudgetIndex: number = 0;
  private budgetModeOpportunityBackup: any = null;
  private budgetBackup: any = null;
  private _budgetsNeedReload: boolean = false;
  private _pendingBudgetId: number | null = null;

  // Flag para evitar navegaciones durante la inicialización
  private isInitializing: boolean = true;

  // Flag para manejar navegación del botón atrás del navegador (no usado con popstate ahora)
  private handlingBrowserBack: boolean = false;

  // Control de visibilidad del panel de actividades en móvil
  showActivitiesPanel: boolean = false;

  // Propiedades computadas (en lugar de funciones en la vista)
  opportunityName: string = '-';
  currentOpportunityId: number = 0;
  opportunityUsers: any[] = [];
  budgetName: string = '-';
  saleOrderName: string = '-';
  budgetNavigationReference: string = '-';
  hasBudgetQuotationLines: boolean = false;
  hasMultipleOpportunities: boolean = false;
  hasMultipleBudgets: boolean = false;

  // Stepper properties
  stagesForTeam: any[] = [];
  currentStageName: string = 'Nuevo';
  budgetStagesToDisplay: Array<BudgetStage | DraftBudgetStage> = [];
  currentBudgetStageName: string = 'Presupuesto';
  currentBudgetStageValue: string = 'Budget'; // Valor del backend

  // Edición inline - similar a list-key-value: cada campo se edita individualmente
  backupFieldValues: { [key: string]: any } = {}; // Valores de respaldo por campo (para revertir cambios en caso de error)

  // Mapa de nombres de campos en español para mensajes de error
  private fieldLabels: { [key: string]: string } = {
    name: 'Nombre',
    opportunityType: 'Tipo de oportunidad',
    intermediaryCompanyId: 'Empresa Intermediaria',
    clientCompany: 'Empresa Cliente',
    partnerId: 'Contacto',
    email: 'Correo electrónico',
    phone: 'Teléfono',
    website: 'Sitio web',
    teamId: 'Equipo de ventas',
    userId: 'Comercial',
    costCenterId: 'Centro de costos',
    stageId: 'Etapa',
    priority: 'Prioridad',
    expectedClosingDate: 'Cierre previsto',
    expectedRevenue: 'Ingresos esperados',
    probability: 'Probabilidad',
    description: 'Descripción',
    street: 'Dirección',
    city: 'Ciudad',
    province: 'Provincia',
    zip: 'Código postal',
    countryId: 'País',
    stateId: 'Estado',
    jobTitle: 'Puesto de trabajo',
    tags: 'Etiquetas',
  };
  isEditModeEnabled: boolean = false; // Modo de edición general (activado por el botón)
  editModeAction: 'none' | 'save' | 'cancel' = 'none'; // Acción actual en modo edición
  private payloadToSave: any = null;
  private pendingUnsavedBudgetLeaveAction: (() => void) | null = null;
  private bypassUnsavedBudgetLeaveGuard: boolean = false;
  private unsavedBudgetLeaveModalRef: NzModalRef | null = null;

  // Edición de presupuesto
  isBudgetEditModeEnabled: boolean = false; // Modo de edición para presupuesto
  budgetEditModeAction: 'none' | 'save' | 'cancel' = 'none'; // Acción actual en modo edición de presupuesto
  savingBudget: boolean = false; // Loading específico para guardar presupuesto
  cancelingBudget: boolean = false; // Loading específico para cancelar presupuesto
  recoveringBudget: boolean = false; // Loading específico para recuperar presupuesto
  requestingApproval: boolean = false; // Loading específico para solicitar aprobación
  confirmingBudget: boolean = false; // Loading específico para confirmar presupuesto
  adoptingBudget: boolean = false; // Loading específico para adoptar presupuesto
  previewingBudget: boolean = false; // Loading específico para vista previa de presupuesto
  generatingVersion: boolean = false; // Loading específico para generar una versión PDF del presupuesto
  pdfVersionsRefreshKey: number = 0; // Se incrementa para recargar la pestaña "Versiones"

  isBudgetCurrencyConversionValid: boolean = true;

  // Notificaciones
  notRef: any;
  @ViewChild('notificationTmpl') notificationTmpl: any;
  @ViewChild('markAsLostModalTmpl') markAsLostModalTmpl!: TemplateRef<any>;
  @ViewChild('completionModalTmpl') completionModalTmpl!: TemplateRef<any>;
  @ViewChild('addFollowerModalTmpl') addFollowerModalTmpl!: TemplateRef<any>;
  @ViewChild('linkProjectModalTmpl') linkProjectModalTmpl!: TemplateRef<any>;
  @ViewChild('viewAllFollowersModalTmpl')
  viewAllFollowersModalTmpl!: TemplateRef<any>;
  @ViewChild('unsavedBudgetLeaveModalTmpl')
  unsavedBudgetLeaveModalTmpl!: TemplateRef<any>;
  @ViewChild('followersHeader') followersHeader!: TemplateRef<any>;
  @ViewChild('cancelBudgetConfirmationModal')
  cancelBudgetConfirmationModal!: ConfirmationModalComponent;
  @ViewChild('cancelEditModeConfirmationModal')
  cancelEditModeConfirmationModal!: ConfirmationModalComponent;
  @ViewChild('removeFollowerConfirmationModal')
  removeFollowerConfirmationModal!: ConfirmationModalComponent;
  pendingRemoveFollowerId: number | null = null;
  toastConfig: ToastConfig = {
    icon: 'check_circle',
    iconColor: '#6CDA75',
    content: 'Oportunidad actualizada.',
    showUndo: false,
  };

  opportunityStages = [
    { name: 'Nuevo', order: 1 },
    { name: 'Evaluación', order: 2 },
    { name: 'Presupuesto', order: 3 },
    { name: 'Pausada', order: 4 },
    { name: 'Adjudicada', order: 5 },
    { name: 'Cancelada', order: 6 },
  ];

  @Output() openEditDrawerEvent = new EventEmitter<number>();
  @Output() changeOpportunityEvent = new EventEmitter<{
    isNext: boolean;
    isPrev: boolean;
    id: number;
  }>();
  @Output() createNewOpportunityEvent = new EventEmitter<void>();
  @Output() deleteEvent = new EventEmitter<number>();
  @Output() updateEvent = new EventEmitter<any>();
  protected readonly getSafeImageUrl = getSafeImageUrl;

  private destroyRef = inject(DestroyRef);

  /**
   * CEC-692. Los guardados automáticos de términos van de uno en uno. El editor guarda tras 500 ms de pausa;
   * si un guardado sigue en vuelo cuando llega otro, se aparca solo el último de cada cotización y se envía
   * al terminar. Así no compiten dos PUT en el servidor, y la respuesta de un guardado que ya no es el último
   * no pisa lo que se escribió después (eso repintaba el editor con texto viejo y hacía saltar el cursor).
   * Cada petición lleva su cotización y su payload congelados al pedirla: si el usuario pasa a otra
   * cotización con un guardado aparcado, ese guardado va a la suya, no a la que está en pantalla.
   */
  private termsSaveInFlight = false;
  private readonly pendingTermsSaves = new Map<number, BudgetTermsSaveRequest>();
  private readonly latestTermsByBudget = new Map<number, string>();
  protected readonly globalValuesService = inject(GlobalValuesService);
  protected readonly authService = inject(AuthService);

  /** Si el usuario navegó a /projects/ (ej. "Ver detalles" del proyecto), no redirigir a /opportunities al cerrar */
  private navigatingToProjects = false;

  // Verificar si el usuario puede asignar seguidores (admin o manager)
  get canAssignFollowers(): boolean {
    const loggedUser = this.authService.loggedUser$.value;
    if (!loggedUser) return false;
    // Admin: roleId === 1 o roleId === 7
    // Manager: roleId === 3
    return (
      loggedUser.roleId === 1 ||
      loggedUser.roleId === 7 ||
      loggedUser.roleId === 3
    );
  }

  // Verificar si el usuario logueado es el comercial o contacto de la oportunidad
  get isLoggedUserCommercialOrContact(): boolean {
    const loggedUser = this.authService.loggedUser$.value;
    if (!loggedUser || !this.opportunity) return false;

    const loggedUserPartnerId = loggedUser.partnerId;
    const loggedUserId = loggedUser.id;

    // Verificar si es el comercial
    if (this.opportunity?.comercialUser?.id === loggedUserPartnerId) {
      return true;
    }
    if (this.opportunity?.userId === loggedUserId) {
      return true;
    }

    // Verificar si es el contacto
    if (this.opportunity?.contactPartner?.id === loggedUserPartnerId) {
      return true;
    }
    if (this.opportunity?.partnerId === loggedUserPartnerId) {
      return true;
    }

    return false;
  }

  // Verificar si debe mostrar botón "Dejar de seguir" (usuario es comercial/contacto pero NO admin/manager)
  get shouldShowUnfollowButton(): boolean {
    return this.isLoggedUserCommercialOrContact && !this.canAssignFollowers;
  }

  // Obtener el ID del usuario logueado como seguidor (para poder eliminarse a sí mismo)
  get loggedUserFollowerId(): number | null {
    const loggedUser = this.authService.loggedUser$.value;
    if (!loggedUser) return null;

    // Buscar si el usuario logueado está en la lista de seguidores
    const loggedUserPartnerId = loggedUser.partnerId;
    const loggedUserId = loggedUser.id;

    // Buscar en opportunityUsers por partnerId o userId
    const follower = this.opportunityUsers.find((u) => {
      // Si es un mailFollower, puede tener userId o partnerId
      if (u.isMailFollower) {
        return (
          u.userId === loggedUserId ||
          u.partnerId === loggedUserPartnerId ||
          u.id === loggedUserPartnerId
        );
      }
      // Si es systemUser, comparar por id (partnerId)
      return u.id === loggedUserPartnerId;
    });

    return follower ? follower.id : null;
  }

  // Dejar de seguir (para el usuario logueado que es comercial/contacto)
  unfollowOpportunity(): void {
    const loggedUser = this.authService.loggedUser$.value;
    if (!loggedUser) return;

    const followerId = this.loggedUserFollowerId;
    if (followerId === null) {
      // Si no está en la lista, significa que no está siguiendo como mailFollower
      // Solo mostrar mensaje informativo
      this.toastConfig.icon = 'info';
      this.toastConfig.iconColor = '#1890ff';
      this.toastConfig.content =
        'No estás siguiendo esta oportunidad como seguidor adicional.';
      this.toastConfig.showUndo = false;
      this.notRef = this.notificationService.create(
        '',
        '',
        this.notificationTmpl,
        {
          nzPlacement: 'bottomRight',
          nzDuration: 3000,
        },
      );
      return;
    }

    // Usar el mismo método de eliminación
    this.openRemoveFollowerConfirmation(followerId);
  }

  // Seguir la oportunidad (añadir usuario actual como seguidor)
  followOpportunity(): void {
    const loggedUser = this.authService.loggedUser$.value;
    if (!loggedUser) return;

    const opportunityId = this.opportunity?.id || this.opportunity?.originalId;
    if (!opportunityId) {
      console.warn('No se encontró el ID de la oportunidad');
      return;
    }

    const entityType = PARENT_ENTITY_TYPE.OPPORTUNITY; // 49 para Lead/Opportunity

    const payload: any = {
      entityType: entityType,
      entityId: opportunityId,
      userId: loggedUser.id,
    };

    this.addingFollowers = true;

    this.communicationService
      .addMailFollower(payload)
      .pipe(
        finalize(() => {
          this.addingFollowers = false;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          // Después de añadir, hacer un GET para obtener todos los seguidores actualizados
          this.loadMailFollowers();

          // Mostrar notificación de éxito
          this.toastConfig.icon = 'check_circle';
          this.toastConfig.iconColor = '#6CDA75';
          this.toastConfig.content = 'Ahora estás siguiendo esta oportunidad.';
          this.toastConfig.showUndo = false;
          this.notRef = this.notificationService.create(
            '',
            '',
            this.notificationTmpl,
            {
              nzPlacement: 'bottomRight',
              nzDuration: 3000,
            },
          );
        },
        error: (error) => {
          console.error('Error al seguir la oportunidad:', error);
          this.toastConfig.icon = 'error';
          this.toastConfig.iconColor = '#ff4d4f';
          this.toastConfig.content =
            'Error al seguir la oportunidad. Inténtalo de nuevo.';
          this.toastConfig.showUndo = false;
          this.notRef = this.notificationService.create(
            '',
            '',
            this.notificationTmpl,
            {
              nzPlacement: 'bottomRight',
              nzDuration: 3000,
            },
          );
        },
      });
  }

  constructor(
    private drawerRef: NzDrawerRef<string>,
    private documentService: DocumentService,
    private projectService: ProjectService,
    private communicationService: CommunicationService,
    private opportunityService: OpportunityService,
    private activityService: ActivityService,
    private partnerService: PartnerService,
    private fb: FormBuilder,
    private notificationService: NzNotificationService,

    private modalService: NzModalService,
    private opportunitiesUtilsService: OpportunitiesUtilsService,
    private cdr: ChangeDetectorRef,
    private elementRef: ElementRef,
    private drawerService: NzDrawerService,
    public screenService: ScreenService,
    private router: Router,
    private route: ActivatedRoute,
    private location: Location,
    private detailsHelper: OpportunityDetailsHelperService,
    private stageService: OpportunityStageService,
    private payloadBuilder: OpportunityPayloadBuilderService,
    private drawerEscapeBlocker: DrawerEscapeBlockerService,
    private sourTrackSyncFlow: SourTrackSyncFlowService,
  ) {
    this.initLostForm();
  }

  ngOnInit(): void {
    console.log(
      '🔵 [drawer] ngOnInit - opportunity inicial:',
      this.opportunity,
    );
    console.log('🔵 [drawer] nzData.element:', this.nzData.element);
    this.loading = true;
    this.activitiesLoaded = false;
    this.opportunity = this.nzData.element;
    this.columnsData = this.nzData.columnsData;
    if ((this.nzData as any).activeTabIndex != null) {
      this.activeTabIndex = (this.nzData as any).activeTabIndex;
    }

    // Al abrir el drawer, recargar la oportunidad/lead desde backend con métricas incluidas
    // para asegurar que projects y metrics estén disponibles en la vista de detalle.
    const initialOpportunityId =
      this.opportunity?.id || this.opportunity?.originalId;
    console.log('🔵 [drawer] initialOpportunityId:', initialOpportunityId);
    console.log('🔵 [drawer] isLead:', this.isLead);
    console.log('🔵 [drawer] nzData.maestroName:', this.nzData.maestroName);

    if (initialOpportunityId) {
      console.log('🔵 [drawer] Cargando datos iniciales...');

      // Determinar qué endpoint usar según si es lead u oportunidad
      const isLeadRequest =
        this.nzData.maestroName === 'Leads' ||
        this.opportunity?.type === 'Lead' ||
        this.isLead;
      console.log('🔵 [drawer] isLeadRequest:', isLeadRequest);

      let request$;

      if (isLeadRequest) {
        // El detalle necesita la descripción, pero mantiene las métricas fuera del payload.
        const getParams = buildLeadDetailsGetParams(initialOpportunityId);
        console.log('🔵 [drawer] Usando getAdvancedLead (lead)');
        request$ = this.opportunityService.getAdvancedLead(getParams);
      } else {
        // Para oportunidades usar getOpportunityById con includeMetrics
        console.log(
          '🔵 [drawer] Usando getOpportunityById con includeMetrics=true (oportunidad)',
        );
        request$ = this.opportunityService.getOpportunityById(
          initialOpportunityId,
          undefined,
          undefined,
          true,
        );
      }

      request$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (response: any) => {
          console.log(
            '🔵 [drawer] Respuesta COMPLETA del API:',
            JSON.stringify(response, null, 2),
          );
          console.log('🔵 [drawer] response.data:', response?.data);
          console.log('🔵 [drawer] response.data.rows:', response?.data?.rows);

          // Verificar si hay un array de rows dentro de data
          if (response?.data?.rows && Array.isArray(response.data.rows)) {
            console.log(
              '🔵 [drawer] Número de rows:',
              response.data.rows.length,
            );
            console.log('🔵 [drawer] Primera row:', response.data.rows[0]);
          }

          let updatedOpportunity = null;

          if (
            response?.data?.rows &&
            Array.isArray(response.data.rows) &&
            response.data.rows.length > 0
          ) {
            console.log('🔵 [drawer] Extrayendo de response.data.rows[0]');
            updatedOpportunity = response.data.rows[0];
          } else if (
            response?.data &&
            Array.isArray(response.data) &&
            response.data.length > 0
          ) {
            console.log('🔵 [drawer] Extrayendo de response.data[0]');
            updatedOpportunity = response.data[0];
          } else if (
            response?.data &&
            typeof response.data === 'object' &&
            !Array.isArray(response.data)
          ) {
            console.log('🔵 [drawer] Usando response.data directamente');
            updatedOpportunity = response.data;
          }

          console.log(
            '🔵 [drawer] updatedOpportunity extraída:',
            updatedOpportunity,
          );

          if (updatedOpportunity) {
            console.log('🔵 Datos recibidos del API:', updatedOpportunity);

            // Para leads: normalizar contactPartner para que apunte a partner si no existe
            if (
              isLeadRequest &&
              updatedOpportunity.partner &&
              !updatedOpportunity.contactPartner
            ) {
              updatedOpportunity.contactPartner = updatedOpportunity.partner;
            }

            // Para leads: intentar obtener el partner completo con parentId
            if (
              isLeadRequest &&
              updatedOpportunity.partnerId &&
              !updatedOpportunity.contactPartner?.parentId
            ) {
              // Primero intentar en globalValuesService.partnerlist
              let partnerFromGlobal =
                this.globalValuesService.partnerlist?.find(
                  (p: any) => p.id === updatedOpportunity.partnerId,
                );

              if (partnerFromGlobal) {
                console.log(
                  '🔵 [Lead] Partner encontrado en globalValuesService:',
                  partnerFromGlobal,
                );
                console.log(
                  '🔵 [Lead] Partner tiene parentId:',
                  partnerFromGlobal.parentId,
                );
                // Si el partner global tiene parentId, actualizar contactPartner
                if (partnerFromGlobal.parentId) {
                  updatedOpportunity.contactPartner = partnerFromGlobal;
                }
              } else {
                console.log(
                  '⚠️ [Lead] Partner NO encontrado en globalValuesService.partnerlist',
                );
                console.log(
                  '🔵 [Lead] Intentando buscar directamente con PartnerService...',
                );

                // Si no está en globalValuesService, buscar directamente con el servicio
                // Esto asegura que obtengamos el partner con parentId
                const getParams: GetParams = {
                  startRow: 0,
                  endRow: 1,
                  sortDirection: 'asc',
                  sortProperty: 'id',
                  filterProperty: 'id',
                  filterValue: updatedOpportunity.partnerId.toString(),
                };

                this.partnerService
                  .getPartner(getParams)
                  .pipe(takeUntilDestroyed(this.destroyRef))
                  .subscribe({
                    next: (partnerResponse: GetResponse) => {
                      console.log(
                        '🔵 [Lead] Partner obtenido del servicio:',
                        partnerResponse,
                      );
                      if (
                        partnerResponse?.data?.rows &&
                        partnerResponse.data.rows.length > 0
                      ) {
                        const partnerData = partnerResponse.data.rows[0];
                        updatedOpportunity.contactPartner = partnerData;
                        console.log(
                          '🔵 [Lead] Partner actualizado con parentId:',
                          partnerData.parentId,
                        );

                        // Forzar actualización del opportunity para que el componente hijo se refresque
                        this.opportunity = {
                          ...this.opportunity,
                          contactPartner: partnerData,
                        };
                        this.cdr.detectChanges();
                      }
                    },
                    error: (err: any) => {
                      console.error('❌ [Lead] Error al obtener partner:', err);
                    },
                  });
              }
            }

            const merged = this.mergeOpportunityFromApi(
              updatedOpportunity,
              this.opportunity,
            );
            console.log('🔵 Opportunity después del merge:', merged);

            // Crear nueva referencia para forzar detección de cambios en componentes hijos
            this.opportunity = { ...merged };

            if (this._budgetsNeedReload && !this.isLead) {
              this._budgetsNeedReload = false;
              this.loadBudgets();
            }

            if (this._pendingBudgetId !== null && !this.isLead) {
              const budgetId = this._pendingBudgetId;
              this._pendingBudgetId = null;
              this.loadSpecificBudget(budgetId);
            }

            console.log('🔵 Llamando updateStagesForTeam...');
            this.updateStagesForTeam();
            console.log(
              '🔵 stagesForTeam después de update:',
              this.stagesForTeam,
            );

            console.log('🔵 Llamando updateCurrentStageName...');
            this.updateCurrentStageName();
            console.log(
              '🔵 currentStageName después de update:',
              this.currentStageName,
            );

            // Actualizar propiedades computadas después de recibir los datos
            console.log('🔵 Llamando updateComputedProperties...');
            this.updateComputedProperties();
            console.log(
              '🔵 OpportunityName después de updateComputedProperties:',
              this.opportunityName,
            );

            // Cargar el chat después del merge si existe chatId
            if (this.opportunity?.chatId) {
              console.log(
                '🔵 Cargando chat con chatId:',
                this.opportunity.chatId,
              );
              this.loadOpportunityChat();
            }

            // Forzar detección de cambios para actualizar la vista
            this.cdr.detectChanges();
          } else {
            console.warn('⚠️ No se recibieron datos del API');
          }

          // Desactivar loading después de actualizar todo
          this.loading = false;
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error(
            'Error recargando oportunidad con métricas al abrir drawer:',
            err,
          );
          // Si hay error, intentar cargar el chat con los datos existentes
          if (this.opportunity?.chatId) {
            console.log(
              '🔵 Cargando chat después de error, con chatId:',
              this.opportunity.chatId,
            );
            this.loadOpportunityChat();
          }
          // Desactivar loading incluso si hay error
          this.loading = false;
          this.cdr.detectChanges();
        },
      });
    } else {
      // Si no hay ID inicial, desactivar loading inmediatamente
      // pero intentar cargar el chat si existe
      if (this.opportunity?.chatId) {
        console.log(
          '🔵 Cargando chat sin ID inicial, con chatId:',
          this.opportunity.chatId,
        );
        this.loadOpportunityChat();
      }
      this.loading = false;
    }

    // Detectar si el usuario navega a proyectos para no redirigir a /opportunities al cerrar el drawer
    this.router.events
      .pipe(
        filter((e): e is NavigationStart => e instanceof NavigationStart),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((e) => {
        if (e.url && e.url.includes('/projects/')) {
          console.log('[Drawer] NavigationStart a proyectos detectado:', e.url);
          this.navigatingToProjects = true;
        }
      });

    // Si se pasaron budgetId y opportunityId en nzData, usarlos directamente
    // en lugar de depender solo de los query params
    const budgetIdFromData = (this.nzData as any).budgetId;
    const opportunityIdFromData = (this.nzData as any).opportunityId;

    // Si tenemos budgetId en nzData, establecer el modo budget INMEDIATAMENTE antes de cualquier otra inicialización
    if (budgetIdFromData) {
      this.viewMode = 'budget';
    }

    // Guardar el nombre original: para leads usar name si existe (API devuelve "Lead CEC 6"); para oportunidades solo si no parece email
    if (this.opportunity?.name && !this.opportunity.name.includes('@')) {
      const isLead =
        this.nzData.maestroName === 'Leads' ||
        this.opportunity?.type === 'Lead';
      if (isLead || !this.opportunity.name.includes(' ')) {
        this.originalOpportunityName = this.opportunity.name;
      }
    }

    // Inicializar isLead basado en maestroName o type
    this.isLead =
      this.nzData.maestroName === 'Leads' || this.opportunity?.type === 'Lead';

    // Inicializar viewMode basado en la ruta actual (solo si no se estableció por budgetId)
    if (!budgetIdFromData) {
      this.initializeViewModeFromRoute();
    }

    // Inicializar isLost basado en dateLost
    this.updateIsLost();

    // Inicializar isWon basado en dateWon
    this.updateIsWon();

    // Actualizar canCreateQuote
    this.updateCanCreateQuote();

    // Actualizar etapas del stepper
    this.updateStagesForTeam();
    this.updateCurrentStageName();

    // Cargar seguidores inmediatamente (independiente de actividades)
    if (this.opportunity?.id || this.opportunity?.originalId) {
      this.loadMailFollowers();
    }

    // Actualizar propiedades computadas
    this.updateComputedProperties();

    // Sincronizar URL con el estado actual (solo si no estamos ya en la ruta correcta)
    // Usar setTimeout para evitar conflictos con la apertura del drawer
    setTimeout(() => {
      this.isInitializing = false;
      this.updateUrlFromState();
    }, 100);

    combineLatest([this.route.params, this.route.queryParams])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([params, queryParams]) => {
        if (this.isInitializing) return;

        // Si viene de activities, no procesar cambios de ruta
        const openedFromActivities =
          (this.nzData as any).openedFromActivities === true;
        if (openedFromActivities) return;

        const combinedParams = { ...params, ...queryParams };
        const opportunityId = combinedParams['opportunity'];
        const budgetId = combinedParams['budgetId'] || combinedParams['budget'];

        // Si venimos de budget y la URL ya no lo tiene -> Limpiamos vista budget
        if (opportunityId && !budgetId && this.viewMode === 'budget') {
          if (this.guardUnsavedNewBudgetLeave(() => this.cleanupBudgetViewFromUrlChange())) {
            return;
          }

          this.cleanupBudgetViewFromUrlChange();
        }

        // Llamamos a la versión protegida
        this.handleRouteParams(combinedParams);
      });
    // 1. Ver qué hay en el "State" del historial cada vez que cambia la ruta
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        console.group('--- DEBUG NAVEGACIÓN ---');
        console.log('URL Actual:', event.urlAfterRedirects);
        console.log('ID en Historial (Navegador):', window.history.length);
        console.log('Estado del Historial:', window.history.state);
        console.groupEnd();
      });

    // 2. Ver si el botón atrás se dispara
    window.addEventListener('popstate', (event) => {
      console.warn('¡BOTÓN ATRÁS DETECTADO!');
      console.log('URL en el momento del click:', window.location.href);
    });

    // Load reference data
    this.loadStages();
    this.loadCompanies();
    this.loadCostCenters();
    this.loadTags();
    this.loadLostReasons();

    // NOTA: El chat se carga después del merge en el subscribe de ngOnInit
    // para asegurar que el chatId esté disponible después de cargar los datos del backend

    // Get the documents if available or load them
    if (this.opportunity?.documents?.length) {
      this.documents = this.opportunity.documents;
    } else if (this.opportunity?.id || this.opportunity?.originalId) {
      this.loadOpportunityDocuments(
        this.opportunity?.id || this.opportunity?.originalId,
      );
    }

    // Cargar presupuestos al abrir el drawer (solo si no es lead o si estamos en modo budget)
    if (
      (!this.isLead || this.viewMode === 'budget') &&
      (this.opportunity?.id || this.opportunity?.originalId)
    ) {
      // Si estamos en modo budget y hay un budgetId en nzData, cargar ese presupuesto específico
      const routeParams = this.route.snapshot.params;
      const queryParams = this.route.snapshot.queryParams;
      const budgetIdFromData = (this.nzData as any).budgetId;
      const budgetId =
        budgetIdFromData || routeParams['budgetId'] || queryParams['budget'];

      if (this.viewMode === 'budget' && budgetId) {
        const budgetIdNum = Number(budgetId);
        setTimeout(() => {
          this.loadSpecificBudget(budgetIdNum);
        }, 100);
      } else {
        // Si no hay budgetId específico, solo cargar la lista
        this.loadBudgets();
      }
    }

    // Cargar actividades al abrir el drawer
    // Si estamos en modo budget, esperar a que el presupuesto se cargue antes de cargar actividades
    if (this.viewMode === 'budget') {
      // Las actividades se cargarán después de que se cargue el presupuesto en loadSpecificBudget
      // o en setBudgetView
    } else if (this.opportunity?.id || this.opportunity?.originalId) {
      this.loadActivities();
    }

    // El loading se desactiva dentro del subscribe de getOpportunityById o inmediatamente si no hay ID
    this.drawerEscapeBlocker.block();
  }

  /**
   * ESTA ES LA CLAVE: Para abrir el budget sin perder el rastro,
   * debemos usar navegación, NO solo cambiar variables.
   */
  public openBudget(budgetId: number): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { budget: budgetId },
      queryParamsHandling: 'merge', // Mantiene el ?opportunity=158
    });
  }

  private cleanupBudgetViewFromUrlChange(): void {
    // Tu lógica de limpieza original (se mantiene igual)
    this.viewMode = this.isLead ? 'lead' : 'opportunity';
    this.budget = null;
    this.currentBudgetIndex = 0;

    if (this.isBudgetEditModeEnabled) {
      this.budgetEditModeAction = 'cancel';
      this.isBudgetEditModeEnabled = false;
    }

    if (this.budgetModeOpportunityBackup) {
      this.opportunity = JSON.parse(
        JSON.stringify(this.budgetModeOpportunityBackup),
      );
    }

    this.budgetModeOpportunityBackup = null;
    this.budgetBackup = null;

    if (this.opportunity?.id || this.opportunity?.originalId) {
      this.activitiesLoaded = false;
      this.loadActivities();
      this.loadOpportunityChat();
    }

    this.updateComputedProperties();
    this.cdr.detectChanges();
  }

  private handleRouteParams(params: any): void {
    const opportunityIdParam = params['opportunity'];
    const budgetIdParam = params['budgetId'] || params['budget'];

    // 1. ESCUDO: Si hay budget en la URL, lo cargamos y SALIMOS.
    // No dejamos que siga hacia la lógica de oportunidad.
    if (budgetIdParam) {
      const budgetIdNum = Number(budgetIdParam);
      if (
        !isNaN(budgetIdNum) &&
        (!this.budget || this.budget.id !== budgetIdNum)
      ) {
        this.loadSpecificBudget(budgetIdNum);
      }
      return;
    }

    // 2. LÓGICA DE OPORTUNIDAD:
    if (opportunityIdParam) {
      const opportunityIdNum = Number(opportunityIdParam);

      // 3. SEGUNDO ESCUDO (Clave): Solo pedimos cambio si el ID es DIFERENTE al actual
      // Si es el mismo (como cuando das atrás), NO llamamos a changeOpportunity
      if (
        !isNaN(opportunityIdNum) &&
        opportunityIdNum !== this.currentOpportunityId
      ) {
        console.log('Cambiando a nueva oportunidad:', opportunityIdNum);
        this.changeOpportunity(false, false, opportunityIdNum);
      } else {
        console.log(
          'Permaneciendo en la oportunidad actual, bloqueando redirección.',
        );
      }
    } else {
      // Si no hay opportunity en la URL: puede ser porque navegamos a /projects
      // No hacer nada si estamos navegando a proyectos (el drawer se cierra desde onNavigateToProject)
      if (this.navigatingToProjects || this.router.url.includes('/projects/')) {
        return;
      }
      console.warn('URL limpia detectada. Cerrando drawer.');
      if (this.viewMode === 'opportunity' || this.viewMode === 'lead') {
        // Solo si no estamos en budget cerramos el drawer principal
        // this.drawerRef.close();
      }
    }
  }
  // Importa HostListener de @angular/core
  @HostListener('window:popstate')
  onBrowserBack() {
    // Si el usuario da atrás y el drawer de budget está abierto
    if (this.viewMode === 'budget') {
      // La URL ya cambió gracias al navegador,
      // así que solo ejecutamos la limpieza visual.
      if (this.guardUnsavedNewBudgetLeave(() => this.cleanupBudgetViewFromUrlChange())) {
        return;
      }

      this.cleanupBudgetViewFromUrlChange();
    }
  }

  /* *
   * Maneja los cambios en los parámetros de la ruta y query params
   */
  /*  private handleRouteParams(params: any): void {
    // También verificar query params para presupuestos
    const queryParams = this.route.snapshot.queryParams;
    const budgetId = params['budgetId'] || queryParams['budget'];

    // Si cambió el budgetId (en params o query params), cargar ese presupuesto
    if (budgetId && this.viewMode !== 'budget') {
      const budgetIdNum = Number(budgetId);
      this.loadSpecificBudget(budgetIdNum);
    }
  } */

  ngAfterViewInit(): void {
    this.afterViewInit = true;

    // En algunos entornos los inputs pueden llegar después de OnInit.
    // Forzamos una verificación diferida para cargar actividades.
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['opportunity'] &&
      (this.opportunity?.id || this.opportunity?.originalId) &&
      !this.activitiesLoaded
    ) {
      console.log('loadActivities onChanges');
      this.loadActivities();
    }
  }

  // Método público para actualizar la oportunidad desde el componente padre
  // Este método se llama cuando el componente padre actualiza directamente this.opportunity
  updateOpportunityData(newOpportunity: any): void {
    if (!newOpportunity) return;

    // Actualizar la oportunidad
    this.opportunity = newOpportunity;

    // Actualizar isLead basado en el tipo
    this.isLead =
      this.nzData.maestroName === 'Leads' || newOpportunity?.type === 'Lead';

    // Actualizar viewMode si es necesario (no cambiar si estamos en modo budget)
    if (this.viewMode !== 'budget') {
      this.viewMode = this.isLead ? 'lead' : 'opportunity';
    }

    // Actualizar estados
    this.updateIsLost();
    this.updateIsWon();
    this.updateCanCreateQuote();

    // Actualizar etapas del stepper
    this.updateStagesForTeam();
    this.updateCurrentStageName();

    // Actualizar propiedades computadas (nombre, ID, usuarios, etc.)
    this.updateComputedProperties();

    // Recargar documentos si es necesario
    if (newOpportunity?.id || newOpportunity?.originalId) {
      const opportunityId = newOpportunity.id || newOpportunity.originalId;
      if (
        !this.documents?.length ||
        this.documents[0]?.entityId !== opportunityId
      ) {
        this.loadOpportunityDocuments(opportunityId);
      }
    }

    // Recargar presupuestos si no estamos en modo lead o si estamos en modo budget
    if (
      (!this.isLead || this.viewMode === 'budget') &&
      (newOpportunity?.id || newOpportunity?.originalId)
    ) {
      this.loadBudgets();
    }

    // Recargar actividades si no se han cargado
    if (
      !this.activitiesLoaded &&
      (newOpportunity?.id || newOpportunity?.originalId)
    ) {
      this.loadActivities();
    }

    // Forzar detección de cambios
    this.cdr.detectChanges();
  }

  loadStages(): void {
    this.globalValuesService.stages$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((stages: { id: number; name: string }[]) => {
        this.stages = stages || [];

        // Cuando los stages se carguen, actualizar las etapas para el equipo
        if (this.stages.length > 0 && this.opportunity) {
          this.updateStagesForTeam();
          this.updateCurrentStageName();
          this.updateCanCreateQuote();
          this.cdr.detectChanges();
        }
      });
  }

  loadCompanies(): void {
    this.globalValuesService.companies$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((companies: Company[]) => {
        this.companies = companies || [];
      });
  }

  loadCostCenters(): void {
    this.globalValuesService.costCenters$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((costCenters: { id: number; name: string }[]) => {
        this.costCenters = costCenters || [];
      });
  }

  loadTags(): void {
    this.globalValuesService.crmTags$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((tags: CrmTag[]) => {
        this.tags = tags || [];
      });
  }

  loadLostReasons(): void {
    // Opciones de razón de pérdida según la imagen
    this.lostReasons = [
      { id: 1, name: 'Precio' },
      { id: 2, name: 'Plazo' },
      { id: 3, name: 'Capacidad' },
      { id: 4, name: 'Negociación' },
      { id: 5, name: 'Otros' },
      { id: 6, name: 'No interesa' },
      { id: 7, name: 'Error' },
      { id: 8, name: 'No hay suficiente stock' },
      { id: 9, name: 'Escriba algo...' },
    ];
  }

  // Actualizar la propiedad isLost basada en dateLost
  updateIsLost(): void {
    this.isLost =
      this.opportunity?.dateLost !== null &&
      this.opportunity?.dateLost !== undefined;
  }

  // Actualizar la propiedad isWon basada en dateWon
  updateIsWon(): void {
    this.isWon =
      this.opportunity?.dateWon !== null &&
      this.opportunity?.dateWon !== undefined;
  }

  /** Navega al detalle del proyecto: cerrar primero con flag para que layout no redirija a /opportunities en afterClose */
  onNavigateToProject(projectId: number): void {
    console.log('[Drawer] onNavigateToProject recibido, projectId:', projectId);
    this.navigatingToProjects = true;
    // Cerrar PRIMERO con flag - el layout's afterClose debe ver 'navigatingToProjects' y no navegar
    // Si navegamos primero, el layout se destruye y ngOnDestroy cierra el drawer sin flag, pisando la ruta
    this.drawerRef.close('navigatingToProjects');
    console.log('[Drawer] Navegando a /projects/detail/' + projectId);
    this.router.navigate(['/projects', 'detail', projectId]);
  }

  private hasUnsavedNewBudget(): boolean {
    return this.viewMode === 'budget' && !!this.budget && !this.budget.id;
  }

  private guardUnsavedNewBudgetLeave(action: () => void): boolean {
    if (this.bypassUnsavedBudgetLeaveGuard || !this.hasUnsavedNewBudget()) {
      return false;
    }

    this.pendingUnsavedBudgetLeaveAction = action;
    this.openUnsavedBudgetLeaveModal();
    return true;
  }

  private openUnsavedBudgetLeaveModal(): void {
    if (this.unsavedBudgetLeaveModalRef) {
      return;
    }

    this.unsavedBudgetLeaveModalRef = this.modalService.create({
      nzTitle: undefined,
      nzContent: this.unsavedBudgetLeaveModalTmpl,
      nzFooter: null,
      nzClosable: false,
      nzWidth: 518,
      nzCentered: true,
    });
  }

  closeUnsavedBudgetLeaveModal(): void {
    this.pendingUnsavedBudgetLeaveAction = null;
    this.unsavedBudgetLeaveModalRef?.close();
    this.unsavedBudgetLeaveModalRef = null;
  }

  saveUnsavedBudgetAndContinue(): void {
    if (this.savingBudget || !this.hasUnsavedNewBudget()) {
      return;
    }

    this.cdr.detectChanges();
    this.saveEditModeBudget();
  }

  discardUnsavedBudgetAndContinue(): void {
    if (this.savingBudget) {
      return;
    }

    const pendingAction = this.pendingUnsavedBudgetLeaveAction;
    this.pendingUnsavedBudgetLeaveAction = null;
    this.unsavedBudgetLeaveModalRef?.close();
    this.unsavedBudgetLeaveModalRef = null;
    this.removeTemporaryBudgetState();
    this.runWithUnsavedBudgetGuardBypass(pendingAction);
  }

  private continueAfterUnsavedBudgetSave(): void {
    const pendingAction = this.pendingUnsavedBudgetLeaveAction;
    if (!pendingAction) {
      return;
    }

    this.pendingUnsavedBudgetLeaveAction = null;
    this.unsavedBudgetLeaveModalRef?.close();
    this.unsavedBudgetLeaveModalRef = null;
    this.runWithUnsavedBudgetGuardBypass(pendingAction);
  }

  private runWithUnsavedBudgetGuardBypass(action: (() => void) | null): void {
    if (!action) {
      return;
    }

    this.bypassUnsavedBudgetLeaveGuard = true;
    try {
      action();
    } finally {
      this.bypassUnsavedBudgetLeaveGuard = false;
    }
  }

  private removeTemporaryBudgetState(): void {
    const tempIndex = this.budgetList.findIndex((b) => !b.id);
    if (tempIndex !== -1) {
      this.budgetList.splice(tempIndex, 1);
    }

    this.budgetEditModeAction = 'cancel';
    this.isBudgetEditModeEnabled = false;
  }

  close(): void {
    if (this.guardUnsavedNewBudgetLeave(() => this.close())) {
      return;
    }

    // Limpiar estados de edición silenciosamente sin mostrar notificaciones
    // NO restaurar valores al backend, solo limpiar el estado local
    if (this.viewMode === 'budget' && this.isBudgetEditModeEnabled) {
      // Primero enviar señal de cancelar al componente hijo
      this.budgetEditModeAction = 'cancel';
      this.cdr.detectChanges();

      // Limpiar el estado de edición
      this.budgetEditModeAction = 'none';
      this.isBudgetEditModeEnabled = false;
      this.cdr.detectChanges();
    } else if (this.isEditModeEnabled && this.opportunityBackup) {
      // Cancelar edición de oportunidad/lead sin mostrar mensajes
      this.editModeAction = 'cancel';
      this.cdr.detectChanges();

      if (this.opportunityBackup) {
        this.opportunity = JSON.parse(JSON.stringify(this.opportunityBackup));
        this.opportunityBackup = null;
      }

      this.isEditModeEnabled = false;
      this.editModeAction = 'none';
    }

    // Verificar si estamos en la ruta de budgets
    const currentUrl = this.router.url;
    const isOnBudgetsRoute = currentUrl.includes('/opportunities/budgets');
    const openedFromActivities =
      (this.nzData as any).openedFromActivities === true;

    // Si viene de activities, no recargar oportunidades ni navegar
    if (!openedFromActivities) {
      // Recargar la lista de oportunidades/leads antes de cerrar
      // Solo si NO estamos en la ruta de budgets
      if (!isOnBudgetsRoute) {
        const opportunityType = this.isLead ? 'Lead' : 'Opportunity';
        this.opportunitiesUtilsService.getOpportunities({
          resetPage: true,
          type: opportunityType,
        });
      }
    }

    // Cerrar el drawer completamente de una vez
    this.drawerRef.close('reload');

    // Manejar la navegación después de cerrar el drawer
    // Si viene de activities, limpiar query params y no navegar
    if (openedFromActivities) {
      // Limpiar query params de opportunity si existen
      const currentQueryParams = this.route.snapshot.queryParams;
      if (currentQueryParams['opportunity'] || currentQueryParams['budget']) {
        this.router
          .navigate([], {
            relativeTo: this.route,
            queryParams: {},
            replaceUrl: true,
          })
          .catch(() => {
            // Ignorar errores de navegación
          });
      }
      return; // Salir sin navegar
    }

    if (isOnBudgetsRoute) {
      // Si estamos en /opportunities/budgets, limpiar los query params pero mantener la ruta
      this.router
        .navigate(['/opportunities/budgets'], {
          replaceUrl: true,
          queryParams: {}, // Limpiar todos los query params
        })
        .catch(() => {
          // Ignorar errores de navegación
        });
    } else {
      // Retrasar la navegación para que si el usuario navegó a proyectos (ej. "Ver detalles"),
      // la URL ya esté actualizada y no redirigamos a /opportunities (evita "primera vez /projects, luego /opportunities")
      const delayMs = 350;
      setTimeout(() => {
        const skip =
          this.navigatingToProjects || this.router.url.includes('/projects/');
        console.log('[Drawer close] setTimeout check:', {
          navigatingToProjects: this.navigatingToProjects,
          url: this.router.url,
          skip,
        });
        if (skip) {
          return;
        }
        const route = this.isLead ? '/opportunities/leads' : '/opportunities';
        console.log('[Drawer close] Redirigiendo a', route);
        this.router.navigate([route], { replaceUrl: true });
      }, delayMs);
    }
  }

  // Load the documents for this opportunity
  loadOpportunityDocuments(opportunityId: number): void {
    const getParams = {
      startRow: 0,
      endRow: 100,
      sortDirection: 'asc',
      sortProperty: 'id',
      entityId: opportunityId,
      entityType: 'Lead',
    };

    this.documentService.getDocument(getParams).subscribe({
      next: (response) => {
        if (response && response.data && response.data.rows) {
          this.documents = response.data.rows;
        }
      },
      error: (error) => {
        console.error('Error loading opportunity documents:', error);
      },
    });
  }

  // Load the chat for an entity (Opportunity or Budget)
  loadChat(chatId: number, target: any): void {
    console.log('🔵 loadChat llamado con chatId:', chatId, 'target:', target);
    if (!chatId || !target) {
      console.warn('⚠️ loadChat: chatId o target no válidos');
      return;
    }

    const chatParams: GetParams = {
      startRow: 0,
      endRow: 1,
      sortProperty: 'id',
      sortDirection: 'asc',
      filterProperty: 'id',
      filterValue: chatId.toString(),
    };

    console.log(
      '🔵 Llamando communicationService.getChat con params:',
      chatParams,
    );
    this.communicationService
      .getChat(chatParams)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          console.log('🔵 Respuesta de getChat:', response);
          const loadedChat = response?.data?.rows?.[0];
          console.log('🔵 loadedChat:', loadedChat);
          console.log('🔵 loadedChat.chatMessages:', loadedChat?.chatMessages);
          console.log(
            '🔵 Número de mensajes:',
            loadedChat?.chatMessages?.length || 0,
          );

          // VALIDACIÓN: Verificar que el chat recibido sea el correcto
          if (loadedChat && loadedChat.id !== chatId) {
            console.error('❌ Backend devolvió un chat incorrecto!');
            console.error('   Chat solicitado (chatId):', chatId);
            console.error('   Chat recibido (id):', loadedChat.id);
            console.error(
              '   Esto es un problema del backend que debe ser corregido.',
            );
            console.warn(
              '⚠️ Ignorando el chat incorrecto para evitar mostrar mensajes equivocados',
            );
            return;
          }

          if (loadedChat) {
            // Preservar los mensajes existentes si el chat ya tiene mensajes cargados
            // y el nuevo chat no trae mensajes o tiene menos
            if (target.chat?.chatMessages?.length > 0) {
              // Si el chat cargado tiene mensajes, usar esos (más actualizados)
              if (
                loadedChat.chatMessages &&
                loadedChat.chatMessages.length > 0
              ) {
                target.chat = {
                  ...loadedChat,
                  chatMessages: loadedChat.chatMessages,
                };
              } else {
                // Si no trae mensajes, mantener los existentes pero actualizar el resto del chat
                target.chat = {
                  ...loadedChat,
                  chatMessages: target.chat.chatMessages,
                };
              }
            } else {
              // Si no hay mensajes existentes, usar el chat cargado
              target.chat = loadedChat;
              if (!target.chat.chatMessages) {
                target.chat['chatMessages'] = [];
              }
            }

            // Crear una nueva referencia del objeto para que los componentes OnPush detecten el cambio
            if (target === this.opportunity) {
              this.opportunity = { ...this.opportunity };
              console.log(
                '🔵 Opportunity actualizada con chat, mensajes:',
                this.opportunity.chat?.chatMessages?.length || 0,
              );
            } else if (target === this.budget) {
              this.budget = { ...this.budget };
              console.log(
                '🔵 Budget actualizado con chat, mensajes:',
                this.budget.chat?.chatMessages?.length || 0,
              );
            }

            // Forzar detección de cambios for local component if needed, though OnPush might need markForCheck
            this.cdr.detectChanges();
            console.log(
              '🔵 Detección de cambios forzada después de cargar chat',
            );
          }
        },
        error: (error) => {
          console.error('❌ Error loading chat:', error);
        },
      });
  }

  // Load the chat for this opportunity (legacy wrapper)
  loadOpportunityChat(): void {
    console.log(
      '🔵 loadOpportunityChat llamado, chatId:',
      this.opportunity?.chatId,
    );
    if (!this.opportunity?.chatId) {
      console.warn('⚠️ loadOpportunityChat: No hay chatId en opportunity');
      return;
    }
    console.log('🔵 Llamando loadChat con chatId:', this.opportunity.chatId);
    this.loadChat(this.opportunity.chatId, this.opportunity);
  }

  // Get stage name by ID, considerando teamId
  getStageName(stageId: number | null): string {
    if (stageId === null || stageId === undefined) return '-';

    const teamId = this.opportunity?.teamId;
    const allStages = this.globalValuesService.stages$.value;

    // Buscar el stage por stageId y teamId (si está disponible)
    const stage =
      allStages.find(
        (s: any) => s.id === stageId && this.stageBelongsToTeam(s, teamId),
      ) || allStages.find((s: any) => s.id === stageId);

    return stage?.name || '-';
  }

  // Obtener las etapas filtradas por teamId de la oportunidad, ordenadas
  getStagesForTeam(): any[] {
    const teamId = this.normalizeId(this.opportunity?.teamId);
    return this.getStagesForTeamId(teamId);
  }

  private normalizeId(value: any): number | null {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'object') {
      return this.normalizeId(value.id ?? value.value);
    }

    const numericValue = Number(value);
    return Number.isFinite(numericValue) && numericValue !== 0
      ? numericValue
      : null;
  }

  // Metodo helper para obtener stages de un teamId especifico
  private getStagesForTeamId(teamId: number | null | undefined): any[] {
    const allStages = this.globalValuesService.stages$.value || [];
    const normalizedTeamId = this.normalizeId(teamId);

    const filteredStages: any[] = allStages.filter((stage: any) =>
      this.stageBelongsToTeam(stage, normalizedTeamId),
    );

    // Ordenar por sequence, order o id (leads suelen usar sequence)
    return filteredStages.sort((a, b) => {
      const orderA = a.sequence ?? a.order ?? a.id ?? 0;
      const orderB = b.sequence ?? b.order ?? b.id ?? 0;
      return Number(orderA) - Number(orderB);
    });
  }

  // Método helper para validar y corregir stageId para un teamId
  private getStageTeamIds(stage: any): Array<number | string> {
    const stageTeamIds = [
      stage?.teamId,
      stage?.salesTeamId,
      stage?.salesTeam?.id,
    ].filter((teamId: any) => teamId !== null && teamId !== undefined);

    if (Array.isArray(stage?.teamIds)) {
      stageTeamIds.push(
        ...stage.teamIds
          .map((team: any) => team?.id ?? team)
          .filter((teamId: any) => teamId !== null && teamId !== undefined),
      );
    }

    if (Array.isArray(stage?.teams)) {
      stageTeamIds.push(
        ...stage.teams
          .map((team: any) => team?.id ?? team)
          .filter((teamId: any) => teamId !== null && teamId !== undefined),
      );
    }

    return stageTeamIds;
  }

  private stageBelongsToTeam(stage: any, teamId: number | string | null | undefined): boolean {
    if (stage?.isGlobal === true) {
      return true;
    }

    const stageTeamIds = this.getStageTeamIds(stage);

    if (teamId === null || teamId === undefined || teamId === '') {
      return stageTeamIds.length === 0;
    }

    const normalizedTeamId = this.normalizeId(teamId);
    return stageTeamIds.some(
      (id) => this.normalizeId(id) === normalizedTeamId,
    );
  }

  private validateAndFixStageId(opportunity: any): void {
    if (!opportunity) return;

    const teamId = this.normalizeId(opportunity.teamId);
    const stageId = this.normalizeId(opportunity.stageId);
    opportunity.teamId = teamId;
    opportunity.stageId = stageId;

    console.log('🔍 validateAndFixStageId llamado:', { teamId, stageId });

    if (teamId && stageId) {
      const stagesForTeam = this.getStagesForTeamId(teamId);
      console.log(
        '🔍 Stages para el equipo:',
        stagesForTeam.map((s: any) => ({ id: s.id, name: s.name })),
      );

      const stageBelongsToTeam = stagesForTeam.some(
        (s: any) => this.normalizeId(s.id) === stageId,
      );

      if (!stageBelongsToTeam) {
        // El stageId no pertenece al equipo, asignar el primer stage
        if (stagesForTeam.length > 0) {
          const firstStage = stagesForTeam[0];
          const previousStageId = opportunity.stageId;
          opportunity.stageId = firstStage.id;
          opportunity.stage = firstStage;
          console.warn(
            `⚠️ StageId ${previousStageId} no pertenecía al TeamId ${teamId}, asignado primer stage: ${firstStage.id} (${firstStage.name})`,
          );
        } else {
          console.warn(
            `⚠️ No hay stages para el TeamId ${teamId}, manteniendo stageId original: ${stageId}`,
          );
        }
      } else {
        // Actualizar también el objeto stage si está disponible
        const restoredStage = stagesForTeam.find(
          (s: any) => this.normalizeId(s.id) === opportunity.stageId,
        );
        if (restoredStage) {
          opportunity.stage = restoredStage;
        }
        console.log('✅ StageId válido para el equipo');
      }
    } else if (teamId && !stageId) {
      // Si hay equipo pero no stageId, asignar el primer stage del equipo
      const stagesForTeam = this.getStagesForTeamId(teamId);
      if (stagesForTeam.length > 0) {
        const firstStage = stagesForTeam[0];
        opportunity.stageId = firstStage.id;
        opportunity.stage = firstStage;
        console.warn(
          `⚠️ No había stageId para TeamId ${teamId}, asignado primer stage: ${firstStage.id} (${firstStage.name})`,
        );
      } else {
        console.warn(`⚠️ No hay stages disponibles para TeamId ${teamId}`);
      }
    } else if (!teamId) {
      console.warn('⚠️ No hay teamId, no se puede validar stageId');
    }

    console.log('🔍 Después de validateAndFixStageId:', {
      teamId: opportunity.teamId,
      stageId: opportunity.stageId,
      stageName: opportunity.stage?.name,
    });
  }

  // Obtener la etapa actual de la oportunidad/lead
  getCurrentStage(): any {
    const stageId = this.opportunity?.stageId;
    if (stageId === null || stageId === undefined) return null;

    const teamId = this.opportunity?.teamId;
    const allStages = this.globalValuesService.stages$.value;

    // Buscar el stage por stageId y teamId (si está disponible)
    const stage =
      allStages.find(
        (s: any) => s.id === stageId && this.stageBelongsToTeam(s, teamId),
      ) || allStages.find((s: any) => s.id === stageId);

    return stage;
  }

  // Actualizar la propiedad canCreateQuote basándose en la etapa actual
  updateCanCreateQuote(): void {
    const currentStage = this.getCurrentStage();
    // Solo permitir crear presupuesto si la etapa lo permite Y la oportunidad está guardada (no en modo edición)
    this.canCreateQuote =
      currentStage?.canCreateQuote === true && !this.isEditModeEnabled;
  }

  // Obtener el icono para una etapa según su posición en el orden
  getStageIcon(index: number): string {
    return this.stageService.opportunityStages[index]
      ? this.detailsHelper.getStageIcon(index)
      : 'circle';
  }

  // Obtener el color para una etapa según su posición en el orden
  getStageColor(index: number): string {
    return this.detailsHelper.getStageColor(index);
  }

  // Obtener la clase CSS para el hover de una etapa según su posición en el orden
  getStageHoverClass(index: number): string {
    return this.detailsHelper.getStageHoverClass(index);
  }

  // Get contact name
  getContactName(): string {
    // Primero intentar obtener del objeto partner si existe
    if (this.opportunity?.contactPartner) {
      const contact = this.opportunity.contactPartner;
      return (
        [contact.name, contact.surName].filter(Boolean).join(' ').trim() || '-'
      );
    }
    // Si no existe, buscar por partnerId en la lista de partners
    if (this.opportunity?.partnerId) {
      const partner = this.globalValuesService.partnerlist?.find(
        (p) => p.id === this.opportunity.partnerId,
      );
      if (partner) {
        return (
          [partner.name, partner.surName].filter(Boolean).join(' ').trim() ||
          partner.name ||
          '-'
        );
      }
    }
    return '-';
  }

  // Get commercial name
  getCommercialName(): string {
    return this.detailsHelper.getCommercialName(this.opportunity);
  }

  // Toggle del modo de edición general
  toggleEditMode(): void {
    this.isEditModeEnabled = !this.isEditModeEnabled;
  }

  startEditMode(): void {
    if (this.isEditModeEnabled) return;
    console.log('📋 Creando backup del opportunity:', this.opportunity);

    // Crear backup con el nombre correcto preservado
    this.opportunityBackup = this.opportunity
      ? {
          ...JSON.parse(JSON.stringify(this.opportunity)),
          name:
            this.originalOpportunityName ||
            this.opportunity.oportunidad ||
            this.opportunity.originalOpportunityName ||
            this.opportunity.name,
        }
      : null;

    console.log(
      '📋 Backup creado con nombre corregido:',
      this.opportunityBackup,
    );
    this.isEditModeEnabled = true;
    this.updateCanCreateQuote();
    this.cdr.detectChanges();
  }

  async saveEditMode(): Promise<void> {
    // Notificar al componente hijo que se va a guardar
    this.editModeAction = 'save';
    this.cdr.detectChanges();

    // Guardar todos los cambios pendientes de forma secuencial y esperar a que terminen
    // NO limpiar estado aquí - eso se hará solo si el guardado es exitoso
    // Si hay error, se hará rollback y se mantendrá el modo de edición
    this.saveAllPendingChanges();
  }

  private saveAllPendingChanges(): void {
    if (!this.opportunityBackup || !this.opportunity) {
      // Si no hay backup, no hay cambios para guardar
      return;
    }

    const opportunityId = this.opportunity?.id || this.opportunity?.originalId;
    if (!opportunityId) {
      console.error('No se pudo identificar la oportunidad/lead');
      return;
    }

    // Construir el payload completo usando el método existente
    // Usamos el nombre actual editado de la oportunidad
    // Priorizar campos que tienen el nombre editado (oportunidad > originalOpportunityName > name)
    const opportunityName =
      this.opportunity?.oportunidad ||
      this.opportunity?.originalOpportunityName ||
      this.opportunity?.name ||
      this.getOpportunityName();

    console.log('📝 Valores de nombre en saveAllPendingChanges:', {
      oportunidad: this.opportunity?.oportunidad,
      originalOpportunityName: this.opportunity?.originalOpportunityName,
      name: this.opportunity?.name,
      'getOpportunityName()': this.getOpportunityName(),
      'Final opportunityName': opportunityName,
    });

    // Validar y corregir stageId antes de construir el payload
    console.log('🔍 Validando stageId antes de guardar:', {
      teamId: this.opportunity?.teamId,
      stageId: this.opportunity?.stageId,
      opportunity: JSON.parse(JSON.stringify(this.opportunity)),
    });
    this.validateAndFixStageId(this.opportunity);
    console.log('🔍 Después de validar stageId:', {
      teamId: this.opportunity?.teamId,
      stageId: this.opportunity?.stageId,
    });

    // Asegurarse de que this.opportunity tenga los valores correctos antes de construir el payload
    console.log(
      '🔍 this.opportunity antes de buildCompleteOpportunityPayload:',
      {
        teamId: this.opportunity?.teamId,
        stageId: this.opportunity?.stageId,
      },
    );

    const patchPayload = this.buildCompleteOpportunityPayload(
      'name',
      opportunityName,
    );

    console.log('🔍 Valores en el payload construido:', {
      payloadTeamId: patchPayload.teamId,
      payloadStageId: patchPayload.stageId,
      opportunityTeamId: this.opportunity?.teamId,
      opportunityStageId: this.opportunity?.stageId,
    });

    console.log(
      `💾 Guardando ${this.isLead ? 'lead' : 'oportunidad'} editada:`,
      {
        id: opportunityId,
        name: opportunityName,
        isLead: this.isLead,
        hasChanges: true,
        teamId: this.opportunity?.teamId,
        stageId: this.opportunity?.stageId,
        payloadTeamId: patchPayload.teamId,
        payloadStageId: patchPayload.stageId,
      },
    );
    console.log('📦 Payload completo:', JSON.stringify(patchPayload, null, 2));
    console.log('📦 Payload teamId y stageId:', {
      teamId: patchPayload.teamId,
      stageId: patchPayload.stageId,
      type: typeof patchPayload.teamId,
      stageIdType: typeof patchPayload.stageId,
    });

    // Usar el endpoint correcto según si es lead u oportunidad
    const updateObservable = this.isLead
      ? this.opportunityService.patchLead(patchPayload)
      : this.opportunityService.patchOpportunity(patchPayload);

    updateObservable.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response: any) => {
        console.log('✅ Cambios guardados exitosamente:', response);

        // Actualizar la oportunidad con la respuesta del backend
        if (response?.data) {
          const updatedOpportunity =
            this.opportunitiesUtilsService.mapOpportunityData(response.data);
          if (updatedOpportunity) {
            // Actualizar localmente
            this.opportunity = {
              ...this.opportunity,
              ...updatedOpportunity,
              documents: this.opportunity?.documents || [],
              chat: this.opportunity?.chat,
            };

            // Recargar el chat para obtener los mensajes actualizados del backend (incluidos los automáticos)
            if (this.opportunity?.chatId) {
              this.loadOpportunityChat();
            }

            // Actualizar propiedades computadas
            this.updateComputedProperties();

            // Verificar si se cambió el comercial o contacto para actualizar seguidores
            const oldUserId = this.opportunityBackup?.userId;
            const oldPartnerId = this.opportunityBackup?.partnerId;
            const oldComercialUserId =
              this.opportunityBackup?.comercialUser?.id;
            const oldContactPartnerId =
              this.opportunityBackup?.contactPartner?.id;

            const newUserId = updatedOpportunity?.userId;
            const newPartnerId = updatedOpportunity?.partnerId;
            const newComercialUserId = updatedOpportunity?.comercialUser?.id;
            const newContactPartnerId = updatedOpportunity?.contactPartner?.id;

            const commercialChanged =
              oldUserId !== newUserId ||
              oldComercialUserId !== newComercialUserId;
            const contactChanged =
              oldPartnerId !== newPartnerId ||
              oldContactPartnerId !== newContactPartnerId;

            if (commercialChanged || contactChanged) {
              // Si cambió el comercial o contacto, actualizar la lista de seguidores
              this.loadMailFollowers();
            }

            // El backend ahora calcula métricas automáticamente al crear/actualizar/vincular/desvincular proyectos.
            // Si la respuesta del patch incluye projects con metrics, las preservamos.
            // Si no vienen métricas, recargamos la oportunidad completa.
            if (!this.isLead) {
              const hasProjectsWithMetrics = updatedOpportunity?.projects?.some(
                (p: any) => p?.metrics && typeof p.metrics === 'object',
              );

              if (
                !hasProjectsWithMetrics &&
                this.opportunity?.projects?.length > 0
              ) {
                // Si había projects antes pero la respuesta no trae métricas, recargar
                this.reloadOpportunity();
              } else if (hasProjectsWithMetrics) {
                // Si vienen métricas en la respuesta, hacer merge preservando las métricas
                this.opportunity = {
                  ...this.opportunity,
                  projects: updatedOpportunity.projects,
                };
              }
            }

            // Emitir solo el objeto actualizado, no disparar recarga completa
            this.updateEvent.emit({
              ...this.opportunity,
              skipReload: true, // Flag para indicar que no se necesita recargar
            });
          }
        }

        const successMessage = this.isLead
          ? 'Lead actualizado exitosamente.'
          : 'Oportunidad actualizada exitosamente.';
        this.showNotification(successMessage);

        // Solo limpiar estado y desactivar modo edición si el guardado fue exitoso
        this.opportunityBackup = null;
        this.isEditModeEnabled = false;
        this.updateCanCreateQuote();
        this.sendOpportunitySavedMessage();

        // No navegar automáticamente - el drawer permanece abierto
        // Solo actualizar la URL si es necesario
        if (!this.isInitializing) {
          this.updateUrlFromState();
        }

        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('❌ Error guardando cambios:', error);

        // Detectar qué campo falta basándose en el error
        let missingFieldLabel = '';
        const errorMsg = error?.error?.message?.toLowerCase() || '';

        if (
          errorMsg.includes('contact') ||
          errorMsg.includes('contacto') ||
          errorMsg.includes('partner')
        ) {
          missingFieldLabel = `: falta el campo ${
            this.fieldLabels['partnerId'] || 'Contacto'
          }`;
        } else if (errorMsg.includes('team') || errorMsg.includes('equipo')) {
          missingFieldLabel = `: falta el campo ${
            this.fieldLabels['teamId'] || 'Equipo de ventas'
          }`;
        } else if (errorMsg.includes('cost') || errorMsg.includes('centro')) {
          missingFieldLabel = `: falta el campo ${
            this.fieldLabels['costCenterId'] || 'Centro de costos'
          }`;
        } else if (errorMsg.includes('type') || errorMsg.includes('tipo')) {
          missingFieldLabel = `: falta el campo ${
            this.fieldLabels['opportunityType'] || 'Tipo de oportunidad'
          }`;
        } else if (
          !this.opportunity?.partnerId &&
          this.opportunity?.clientCompany
        ) {
          // Si hay clientCompany pero no partnerId, es probable que falte el contacto
          missingFieldLabel = `: falta el campo ${
            this.fieldLabels['partnerId'] || 'Contacto'
          }`;
        } else if (
          errorMsg.includes('user') ||
          errorMsg.includes('comercial') ||
          errorMsg.includes('userId')
        ) {
          missingFieldLabel = `: falta el campo ${
            this.fieldLabels['userId'] || 'Comercial'
          }`;
        } else if (!this.opportunity?.userId && this.opportunity?.teamId) {
          // Si hay teamId pero no userId, verificar si hay comerciales disponibles para ese equipo
          const stagesForTeam = this.getStagesForTeamId(
            this.opportunity.teamId,
          );
          const salesTeams = this.globalValuesService.salesTeams$.value || [];
          const selectedTeam = salesTeams.find(
            (team: any) => team.id === this.opportunity.teamId,
          );

          if (selectedTeam) {
            const teamMemberUserIds: number[] = [];
            const members =
              (selectedTeam as any).memberList ||
              (selectedTeam as any).members ||
              [];

            members.forEach((member: any) => {
              if (typeof member === 'object' && member !== null) {
                const userId = member.userId || member.id;
                if (userId != null && userId !== undefined) {
                  teamMemberUserIds.push(userId);
                }
              } else if (typeof member === 'number') {
                teamMemberUserIds.push(member);
              }
            });

            if (teamMemberUserIds.length > 0) {
              // Si hay comerciales disponibles para el equipo pero no se seleccionó uno
              missingFieldLabel = `: falta el campo ${
                this.fieldLabels['userId'] || 'Comercial'
              }`;
            }
          }
        }

        // Personalizar el mensaje de error
        const errorMessage = this.isLead
          ? `Error al guardar cambios del lead${missingFieldLabel}`
          : `Error al guardar cambios de la oportunidad${missingFieldLabel}`;

        this.showErrorNotification(errorMessage);

        // Hacer rollback como en cancelEditMode - restaurar desde opportunityBackup
        if (this.opportunityBackup) {
          console.log('🔄 Haciendo rollback desde backup debido a error...');
          console.log('📋 Opportunity antes de rollback:', this.opportunity);
          console.log('📋 Backup:', this.opportunityBackup);

          // Detectar qué campos cambiaron antes de hacer rollback (para marcar errores)
          const changedFields: string[] = [];
          if (this.opportunity && this.opportunityBackup) {
            // Comparar campos críticos
            const criticalFields = [
              'clientCompany',
              'partnerId',
              'teamId',
              'costCenterId',
              'opportunityType',
            ];
            criticalFields.forEach((field) => {
              const currentValue = this.opportunity[field];
              const backupValue = this.opportunityBackup[field];
              if (currentValue !== backupValue) {
                changedFields.push(field);
                console.log(`📝 Campo ${field} cambió:`, {
                  current: currentValue,
                  backup: backupValue,
                });
              }
            });
          }

          // Guardar los campos que cambiaron antes de hacer rollback
          // Esto se usará para marcar errores en el componente hijo
          const fieldsWithErrors = [...changedFields];

          // Si falta partnerId pero hay clientCompany, agregar ambos a los errores
          if (!this.opportunity?.partnerId && this.opportunity?.clientCompany) {
            if (!fieldsWithErrors.includes('partnerId')) {
              fieldsWithErrors.push('partnerId');
            }
            if (!fieldsWithErrors.includes('clientCompany')) {
              fieldsWithErrors.push('clientCompany');
            }
          }

          // Guardar los campos con error en una propiedad temporal para que el hijo los detecte
          // Esto se pasará al hijo a través de editModeAction y opportunity
          (this.opportunityBackup as any).__fieldsWithErrors = fieldsWithErrors;

          // Restaurar desde el backup (igual que cancelEditMode)
          // Crear una nueva referencia para que Angular detecte el cambio
          const restoredOpportunity = JSON.parse(
            JSON.stringify(this.opportunityBackup),
          );
          // Preservar los campos con error en el objeto restaurado
          (restoredOpportunity as any).__fieldsWithErrors = fieldsWithErrors;

          // Validar y corregir stageId restaurado
          this.validateAndFixStageId(restoredOpportunity);

          this.opportunity = restoredOpportunity;
          console.log('📋 Opportunity después de rollback:', this.opportunity);
          console.log(
            '📋 Campos que cambiaron (serán marcados con error):',
            fieldsWithErrors,
          );

          // Restaurar también los valores de backupFieldValues para limpiar el estado
          this.backupFieldValues = {};

          // Actualizar propiedades computadas
          this.updateComputedProperties();

          // Notificar al componente hijo que se hizo rollback (similar a cancel)
          // Esto activará la detección de errores en el hijo
          // Primero establecer 'cancel' para que el hijo detecte el rollback
          this.editModeAction = 'cancel';
          this.cdr.detectChanges();

          console.log(
            '🔄 editModeAction establecido a cancel, esperando detección del hijo...',
          );

          // Forzar otra actualización para asegurar que el hijo vea el cambio
          setTimeout(() => {
            // Crear otra referencia para forzar detección
            this.opportunity = { ...this.opportunity };
            this.cdr.detectChanges();
            console.log('✅ Primera actualización de rollback completada');

            // Dar tiempo para que el hijo procese el cambio
            setTimeout(() => {
              // Volver a modo edición para que el usuario pueda corregir
              this.editModeAction = 'none';
              this.cdr.detectChanges();
              console.log('✅ Rollback completado, modo edición restaurado');
            }, 100);
          }, 200);
        } else {
          console.warn('⚠️ No hay opportunityBackup para hacer rollback');
        }
      },
    });
  }

  private hasValueChanged(current: any, backup: any): boolean {
    return this.detailsHelper.hasValueChanged(current, backup);
  }

  confirmCancelEditMode(): void {
    this.cancelEditModeConfirmationModal.config = {
      title: '¿Cancelar cambios?',
      message:
        '¿Estás seguro de que deseas cancelar los cambios realizados? Se perderán todos los cambios no guardados.',
      confirmText: 'Sí, cancelar',
      cancelText: 'No, continuar editando',
      confirmButtonType: 'primary',
      confirmButtonDanger: true,
    };
    this.cancelEditModeConfirmationModal.open();
  }

  onCancelEditModeConfirmed(data?: {
    textValue?: string;
    dateValue?: Date;
  }): void {
    this.cancelEditMode();
  }

  cancelEditMode(): void {
    console.log('🔄 Cancelando edición...');
    console.log('📋 Opportunity antes de cancelar:', this.opportunity);
    console.log('📋 Backup:', this.opportunityBackup);

    // Notificar al componente hijo que se va a cancelar
    // Esto evita que el componente hijo emita eventos fieldUpdate que causen peticiones adicionales
    this.editModeAction = 'cancel';
    this.cdr.detectChanges();

    if (this.opportunityBackup) {
      this.opportunity = JSON.parse(JSON.stringify(this.opportunityBackup));
      console.log('📋 Opportunity después de restaurar:', this.opportunity);

      // Validar y corregir stageId restaurado
      this.validateAndFixStageId(this.opportunity);

      // Restaurar también los valores de backupFieldValues para limpiar el estado
      this.backupFieldValues = {};

      // Actualizar propiedades computadas para reflejar el stageId restaurado en el stepper
      this.updateComputedProperties();

      // Enviar los valores restaurados al backend
      this.saveCurrentOpportunityToBackend();

      this.opportunityBackup = null;
    }

    // Restablecer editModeAction después de un breve delay para permitir que el componente hijo
    // procese la cancelación antes de volver a aceptar eventos fieldUpdate
    setTimeout(() => {
      this.isEditModeEnabled = false;
      this.editModeAction = 'none';
      this.updateCanCreateQuote();
      this.cdr.detectChanges();
    }, 100);
  }

  private sendOpportunitySavedMessage(): void {
    try {
      if (!this.opportunity?.chat?.id) return;
      const loggedUser = this.authService.loggedUser$.value;
      if (!loggedUser?.id) return;

      const now = new Date();
      const dateStr = now.toLocaleString('es-ES', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });

      const entityType = this.isLead ? 'Lead' : 'Oportunidad';
      const message =
        `${this.getOpportunityName()}\n` +
        `${entityType} actualizada\n` +
        `${dateStr}`;
      const payload = {
        message,
        type: 'text',
        chatId: this.opportunity.chat.id,
        userId: loggedUser.id,
        authUserEmail: loggedUser.email,
        files: [],
      } as any;

      this.communicationService.createChatMessage(payload).subscribe({
        next: (rsp) => {
          // Actualizar localmente el chat si existe la lista
          if (this.opportunity?.chat && rsp?.data) {
            if (!Array.isArray(this.opportunity.chat.chatMessages)) {
              this.opportunity.chat.chatMessages = [];
            }
            // Usar el mensaje que devuelve el backend (incluye createdByName y otros campos)
            this.opportunity.chat.chatMessages.unshift(rsp.data);
            // Forzar referencia nueva para que el chat OnPush detecte el cambio
            // Primero actualizar el chat
            const updatedChat = {
              ...this.opportunity.chat,
              chatMessages: [...this.opportunity.chat.chatMessages],
            };
            // Luego actualizar la referencia completa de opportunity para que activities-panel detecte el cambio
            this.opportunity = {
              ...this.opportunity,
              chat: updatedChat,
            };
            this.cdr.detectChanges();
          }
        },
        error: (err) =>
          console.error('Error enviando mensaje de actualización', err),
      });
    } catch (err) {
      console.error('Error sendOpportunitySavedMessage', err);
    }
  }

  private sendBudgetSavedMessage(): void {
    try {
      if (!this.opportunity?.chat?.id) return;
      const loggedUser = this.authService.loggedUser$.value;
      if (!loggedUser?.id) return;

      const now = new Date();
      const dateStr = now.toLocaleString('es-ES', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });

      const budgetName = this.budget?.name || 'Presupuesto';
      const message =
        `${budgetName}\n` + `Presupuesto actualizado\n` + `${dateStr}`;
      const payload = {
        message,
        type: 'text',
        chatId: this.opportunity.chat.id,
        userId: loggedUser.id,
        authUserEmail: loggedUser.email,
        files: [],
      } as any;

      this.communicationService.createChatMessage(payload).subscribe({
        next: (rsp) => {
          // Actualizar localmente el chat si existe la lista
          if (this.opportunity?.chat && rsp?.data) {
            if (!Array.isArray(this.opportunity.chat.chatMessages)) {
              this.opportunity.chat.chatMessages = [];
            }
            // Usar el mensaje que devuelve el backend (incluye createdByName y otros campos)
            this.opportunity.chat.chatMessages.unshift(rsp.data);
            // Forzar referencia nueva para que el chat OnPush detecte el cambio
            // Primero actualizar el chat
            const updatedChat = {
              ...this.opportunity.chat,
              chatMessages: [...this.opportunity.chat.chatMessages],
            };
            // Luego actualizar la referencia completa de opportunity para que activities-panel detecte el cambio
            this.opportunity = {
              ...this.opportunity,
              chat: updatedChat,
            };
            this.cdr.detectChanges();
          }
        },
        error: (err) =>
          console.error(
            'Error enviando mensaje de actualización de presupuesto',
            err,
          ),
      });
    } catch (err) {
      console.error('Error sendBudgetSavedMessage', err);
    }
  }

  private sendBudgetEmailSentMessage(): void {
    try {
      // Usar el chat de la entidad que muestra el panel: en modo budget es budget, sino opportunity.
      // Si en modo budget no tiene chat, usar el de la opportunity para que quede registrado.
      let entity: any =
        this.viewMode === 'budget' && this.budget
          ? this.budget
          : this.opportunity;
      if (!entity) return;
      let rawChatId = entity?.chat?.id ?? entity?.chatId;
      if (rawChatId == null && this.viewMode === 'budget' && this.opportunity) {
        entity = this.opportunity;
        rawChatId = entity?.chat?.id ?? entity?.chatId;
      }
      const chatId =
        rawChatId != null && typeof rawChatId === 'object'
          ? (rawChatId as any).id
          : rawChatId;
      if (chatId == null) return;
      const loggedUser = this.authService.loggedUser$.value;
      if (!loggedUser?.id) return;

      const now = new Date();
      const dateStr = now.toLocaleString('es-ES', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });

      const budgetName = this.budget?.name || 'Presupuesto';
      const message =
        `${budgetName}\n` + `Presupuesto enviado por correo\n` + `${dateStr}`;
      const payload = {
        message,
        type: 'text',
        chatId: Number(chatId),
        userId: loggedUser.id,
        authUserEmail: loggedUser.email,
        files: [],
      } as any;

      this.communicationService.createChatMessage(payload).subscribe({
        next: (rsp) => {
          // Usar el mensaje que devuelve el backend (incluye createdByName y otros campos)
          const newMessage = rsp?.data;
          if (!newMessage) return;

          if (entity.chat) {
            if (!Array.isArray(entity.chat.chatMessages)) {
              entity.chat.chatMessages = [];
            }
            entity.chat.chatMessages.unshift(newMessage);
            const updatedChat = {
              ...entity.chat,
              chatMessages: [...entity.chat.chatMessages],
            };
            if (
              this.viewMode === 'budget' &&
              this.budget &&
              entity === this.budget
            ) {
              this.budget = { ...this.budget, chat: updatedChat };
            } else {
              this.opportunity = {
                ...this.opportunity,
                chat: updatedChat,
              };
            }
          } else {
            if (
              this.viewMode === 'budget' &&
              this.budget &&
              entity === this.budget
            ) {
              this.budget = {
                ...this.budget,
                chat: {
                  id: Number(chatId),
                  chatMessages: [newMessage],
                } as any,
              };
            } else {
              this.opportunity = {
                ...this.opportunity,
                chat: {
                  id: Number(chatId),
                  chatMessages: [newMessage],
                } as any,
              };
            }
          }
          this.cdr.detectChanges();
        },
        error: (err) =>
          console.error(
            'Error enviando mensaje de presupuesto enviado por correo',
            err,
          ),
      });
    } catch (err) {
      console.error('Error sendBudgetEmailSentMessage', err);
    }
  }

  // Inicializar formulario para marcar como perdido
  initLostForm(): void {
    this.lostForm = this.fb.group({
      reason: [null],
      lostNote: [''],
    });
  }

  // Guardar la opportunity actual al backend sin comparaciones (para cancelaciones)
  private saveCurrentOpportunityToBackend(): void {
    const opportunityId = this.opportunity?.id || this.opportunity?.originalId;
    if (!opportunityId) {
      return;
    }

    // Para cancelar, enviamos la opportunity restaurada (ya corregida en cancelEditMode)
    // Construir el payload completo usando el método existente
    const opportunityName = this.getOpportunityName();
    const patchPayload = this.buildCompleteOpportunityPayload(
      'name',
      opportunityName,
    );

    console.log(
      `🔄 Enviando valores cancelados al backend (${
        this.isLead ? 'lead' : 'oportunidad'
      }):`,
      {
        id: opportunityId,
        name: opportunityName,
        restored: true,
        isLead: this.isLead,
      },
    );

    // Usar el endpoint correcto según si es lead u oportunidad
    const updateObservable = this.isLead
      ? this.opportunityService.patchLead(patchPayload)
      : this.opportunityService.patchOpportunity(patchPayload);

    updateObservable.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response: any) => {
        console.log('✅ Valores cancelados guardados en backend:', response);

        // Actualizar la oportunidad con la respuesta del backend
        if (response?.data) {
          const updatedOpportunity =
            this.opportunitiesUtilsService.mapOpportunityData(response.data);
          if (updatedOpportunity) {
            this.opportunity = {
              ...this.opportunity,
              ...updatedOpportunity,
              documents: this.opportunity?.documents || [],
              chat: this.opportunity?.chat,
            };
            this.updateEvent.emit(this.opportunity);
          }
        }

        const cancelMessage = this.isLead
          ? 'Cambios del lead cancelados correctamente.'
          : 'Cambios de la oportunidad cancelados correctamente.';
        this.showNotification(cancelMessage);

        //const route = this.isLead ? '/oportunities/leads' : '/oportunities';
        //this.router.navigate([route]);
      },
      error: (error) => {
        console.error('❌ Error guardando valores cancelados:', error);
        const errorMessage = this.isLead
          ? 'Error al cancelar cambios del lead'
          : 'Error al cancelar cambios de la oportunidad';
        this.showErrorNotification(errorMessage);
      },
    });
  }

  // Construir el payload completo para guardar todos los cambios
  private buildCompleteOpportunityPayloadForSave(changedFields: {
    [field: string]: any;
  }): any {
    // Función helper para extraer ID
    const getIdValue = (value: any): number | null => {
      if (value === null || value === undefined || value === '') return null;
      if (typeof value === 'object' && value?.id !== undefined) return value.id;
      if (typeof value === 'number') return value;
      if (typeof value === 'string' && !isNaN(Number(value)))
        return Number(value);
      return null;
    };

    // Validar y corregir stageId antes de construir el payload (teamId como número para que se envíe correctamente)
    let stageIdValue = getIdValue(this.opportunity?.stageId);
    const teamIdRaw = this.opportunity?.teamId;
    const teamIdValue =
      teamIdRaw !== null && teamIdRaw !== undefined && teamIdRaw !== ''
        ? Number(teamIdRaw)
        : null;
    const teamIdValueNum =
      teamIdValue !== null && !isNaN(teamIdValue) ? teamIdValue : null;

    if (stageIdValue && teamIdValueNum) {
      const stagesForTeam = this.getStagesForTeam();
      const stageBelongsToTeam = stagesForTeam.some(
        (s: any) => s.id === stageIdValue,
      );
      if (!stageBelongsToTeam) {
        // Si el stage no pertenece al equipo, asignar el primer stage del equipo
        if (stagesForTeam.length > 0) {
          const firstStage = stagesForTeam[0];
          console.warn(
            `⚠️ StageId ${stageIdValue} no pertenece al TeamId ${teamIdValueNum}, asignando primer stage: ${firstStage.id}`,
          );
          // Actualizar también en la oportunidad para mantener consistencia
          this.opportunity.stageId = firstStage.id;
          this.opportunity.stage = firstStage;
          stageIdValue = firstStage.id;
        } else {
          // Si no hay stages para el equipo, usar null (el backend debería manejarlo)
          console.warn(
            `⚠️ No hay stages para el TeamId ${teamIdValueNum}, enviando null`,
          );
          stageIdValue = null;
        }
      }
    }

    // Incluir el ID y todos los campos actuales (teamId como número para que el backend reciba el equipo seleccionado)
    const payload: any = {
      id: this.opportunity?.id || this.opportunity?.originalId,
      name: this.getOpportunityName() || this.opportunity?.name || '',
      description: this.opportunity?.description,
      amount: this.opportunity?.amount,
      stageId: stageIdValue,
      ...(getIdValue(this.opportunity?.userId) !== null
        ? { userId: getIdValue(this.opportunity?.userId) }
        : {}),
      teamId: teamIdValueNum,
      dateDeadLine: this.opportunity?.dateDeadLine
        ? typeof this.opportunity.dateDeadLine === 'string'
          ? this.opportunity.dateDeadLine
          : new Date(this.opportunity.dateDeadLine).toISOString()
        : null,
      dateWon: this.opportunity?.dateWon
        ? typeof this.opportunity.dateWon === 'string'
          ? this.opportunity.dateWon
          : new Date(this.opportunity.dateWon).toISOString()
        : null,
      dateLost: this.opportunity?.dateLost
        ? typeof this.opportunity.dateLost === 'string'
          ? this.opportunity.dateLost
          : new Date(this.opportunity.dateLost).toISOString()
        : null,
      ...(this.isLead
        ? {}
        : { opportunityType: this.opportunity?.opportunityType }),
      priority: this.opportunity?.priority ?? null,
      ...(getIdValue(this.opportunity?.companyId) != null
        ? { companyId: this.opportunity!.companyId }
        : {}),
      costCenterId: getIdValue(this.opportunity?.costCenterId),
      lostReasonId: getIdValue(this.opportunity?.lostReasonId),
      ...(getIdValue(this.opportunity?.partnerId) != null
        ? { partnerId: this.opportunity!.partnerId }
        : {}),
      intermediaryCompanyId: getIdValue(
        this.opportunity?.intermediaryCompanyId,
      ),
      tags: this.opportunity?.tags,
      dateCreated: this.opportunity?.dateCreated
        ? typeof this.opportunity.dateCreated === 'string'
          ? this.opportunity.dateCreated
          : new Date(this.opportunity.dateCreated).toISOString()
        : null,
      dateLastActivity: this.opportunity?.dateLastActivity
        ? typeof this.opportunity.dateLastActivity === 'string'
          ? this.opportunity.dateLastActivity
          : new Date(this.opportunity.dateLastActivity).toISOString()
        : null,
      dateNextActivity: this.opportunity?.dateNextActivity
        ? typeof this.opportunity.dateNextActivity === 'string'
          ? this.opportunity.dateNextActivity
          : new Date(this.opportunity.dateNextActivity).toISOString()
        : null,
      countryId: getIdValue(this.opportunity?.countryId),
      stateId: getIdValue(this.opportunity?.stateId),
      chatId: getIdValue(this.opportunity?.chatId),
    };

    // Agregar los campos que cambiaron
    Object.keys(changedFields).forEach((field) => {
      if (field === 'tags') {
        payload.tagIds = changedFields[field];
      } else {
        payload[field] = changedFields[field];
      }
    });

    return payload;
  }

  // Método helper para construir el payload completo con todas las propiedades de la oportunidad
  private buildCompleteOpportunityPayload(path: string, value: any): any {
    const opportunityName = this.getOpportunityName();

    // Si es de tipo Date se transforma con toISOString
    const processedValue =
      typeof value === 'object' && value?.toISOString?.()
        ? value.toISOString()
        : value;

    // Helper para obtener valor de ID normalizado (null si es 0 o no existe)
    const getIdValue = (value: any): number | null => this.normalizeId(value);
    // companyId/partnerId: solo incluir cuando hay valor real; enviar 0 hace fallar al backend (lead no existe para company 0)
    const companyIdForPayload =
      path === 'companyId'
        ? processedValue != null && processedValue !== 0
          ? getIdValue(processedValue)
          : undefined
        : getIdValue(this.opportunity?.companyId) != null
          ? getIdValue(this.opportunity?.companyId)
          : undefined;
    const partnerIdForPayload =
      path === 'partnerId'
        ? processedValue != null && processedValue !== 0
          ? getIdValue(processedValue)
          : undefined
        : getIdValue(this.opportunity?.partnerId) != null
          ? getIdValue(this.opportunity?.partnerId)
          : undefined;

    // Construir el payload completo con todas las propiedades
    const payload: any = {
      ...(companyIdForPayload != null
        ? { companyId: companyIdForPayload }
        : {}),
      type: this.opportunity?.type || 'Lead',
      // Alinear los campos de nombre cuando se edita explícitamente
      ...(path === 'name' && {
        oportunidad:
          typeof processedValue === 'string' ? processedValue : opportunityName,
        originalOpportunityName:
          typeof processedValue === 'string'
            ? processedValue
            : this.opportunity?.originalOpportunityName || opportunityName,
      }),
      // Tipo de oportunidad solo aplica a oportunidades, no a leads (el backend no lo exige en PATCH lead)
      ...(this.isLead
        ? {}
        : {
            opportunityType:
              path === 'opportunityType'
                ? processedValue
                : this.opportunity?.opportunityType || null,
          }),
      priority:
        path === 'priority'
          ? processedValue
          : this.opportunity?.priority || null,
      expectedRevenue:
        path === 'expectedRevenue'
          ? typeof processedValue === 'number'
            ? processedValue
            : typeof processedValue === 'string'
              ? parseFloat(processedValue)
              : 0
          : (this.opportunity?.expectedRevenue?.parsedValue ??
            (this.opportunity?.expectedRevenue?.source
              ? parseFloat(this.opportunity.expectedRevenue.source)
              : null) ??
            this.opportunity?.expectedRevenue ??
            0),
      probability:
        path === 'probability'
          ? typeof processedValue === 'number'
            ? processedValue
            : typeof processedValue === 'string'
              ? parseFloat(processedValue)
              : 0
          : this.opportunity?.probability || 0,
      expectedClosingDate:
        path === 'expectedClosingDate'
          ? processedValue
            ? typeof processedValue === 'string'
              ? processedValue
              : new Date(processedValue).toISOString()
            : null
          : this.opportunity?.expectedClosingDate
            ? typeof this.opportunity.expectedClosingDate === 'string'
              ? this.opportunity.expectedClosingDate
              : new Date(this.opportunity.expectedClosingDate).toISOString()
            : null,
      source:
        path === 'source' ? processedValue : this.opportunity?.source || null,
      referencedById:
        path === 'referencedById'
          ? processedValue
          : this.opportunity?.referencedById || null,
      description:
        path === 'description'
          ? processedValue
          : this.opportunity?.description || null,
      email:
        path === 'email' ? processedValue : this.opportunity?.email || null,
      phone:
        path === 'phone' ? processedValue : this.opportunity?.phone || null,
      mobile: this.opportunity?.mobile || null,
      street:
        path === 'street' ? processedValue : this.opportunity?.street || null,
      street2: this.opportunity?.street2 || null,
      city: this.opportunity?.city || null,
      province: this.opportunity?.province || null,
      zip: this.opportunity?.zip || null,
      website:
        path === 'website' ? processedValue : this.opportunity?.website || null,
      jobTitle:
        path === 'jobTitle'
          ? processedValue
          : this.opportunity?.jobTitle || null,
      isActive:
        this.opportunity?.isActive !== undefined
          ? this.opportunity.isActive
          : true,
      dateConversion: this.opportunity?.dateConversion
        ? typeof this.opportunity.dateConversion === 'string'
          ? this.opportunity.dateConversion
          : new Date(this.opportunity.dateConversion).toISOString()
        : null,
      dateWon: this.opportunity?.dateWon
        ? typeof this.opportunity.dateWon === 'string'
          ? this.opportunity.dateWon
          : new Date(this.opportunity.dateWon).toISOString()
        : null,
      dateLost: this.opportunity?.dateLost
        ? typeof this.opportunity.dateLost === 'string'
          ? this.opportunity.dateLost
          : new Date(this.opportunity.dateLost).toISOString()
        : null,
      dateLastActivity: this.opportunity?.dateLastActivity
        ? typeof this.opportunity.dateLastActivity === 'string'
          ? this.opportunity.dateLastActivity
          : new Date(this.opportunity.dateLastActivity).toISOString()
        : null,
      dateNextActivity: this.opportunity?.dateNextActivity
        ? typeof this.opportunity.dateNextActivity === 'string'
          ? this.opportunity.dateNextActivity
          : new Date(this.opportunity.dateNextActivity).toISOString()
        : null,
      ...(path === 'userId'
        ? (() => {
            const userIdValue = getIdValue(processedValue);
            // Solo incluir userId si tiene un valor válido (no null)
            // El backend no acepta null para userId
            return userIdValue !== null ? { userId: userIdValue } : {};
          })()
        : (() => {
            const userIdValue = getIdValue(this.opportunity?.userId);
            // Solo incluir userId si tiene un valor válido (no null)
            // El backend no acepta null para userId
            return userIdValue !== null ? { userId: userIdValue } : {};
          })()),
      teamId: (() => {
        const teamIdValue =
          path === 'teamId'
            ? getIdValue(processedValue)
            : getIdValue(this.opportunity?.teamId);
        console.log('🔍 buildCompleteOpportunityPayload - teamId:', {
          path,
          processedValue,
          opportunityTeamId: this.opportunity?.teamId,
          teamIdValue,
        });
        return teamIdValue;
      })(),
      stageId: (() => {
        // Obtener el teamId que se usará en el payload (puede ser el nuevo valor si se está cambiando)
        const teamIdForPayload =
          path === 'teamId'
            ? getIdValue(processedValue)
            : getIdValue(this.opportunity?.teamId);

        let stageIdValue =
          path === 'stageId'
            ? getIdValue(processedValue)
            : getIdValue(this.opportunity?.stageId);

        console.log(
          '🔍 buildCompleteOpportunityPayload - stageId validación:',
          {
            path,
            teamIdForPayload,
            stageIdValue,
            opportunityTeamId: this.opportunity?.teamId,
            opportunityStageId: this.opportunity?.stageId,
          },
        );

        // SIEMPRE validar que el stageId pertenezca al teamId que se usará en el payload
        // Esto es crítico porque el stageId puede haber quedado de un equipo anterior
        if (teamIdForPayload) {
          const stagesForTeam = this.getStagesForTeamId(teamIdForPayload);
          console.log(
            '🔍 Stages disponibles para teamId:',
            teamIdForPayload,
            stagesForTeam.map((s: any) => ({ id: s.id, name: s.name })),
          );

          if (stageIdValue) {
            const stageBelongsToTeam = stagesForTeam.some(
              (s: any) => this.normalizeId(s.id) === stageIdValue,
            );
            console.log('🔍 Stage pertenece al equipo?', {
              stageIdValue,
              teamIdForPayload,
              stageBelongsToTeam,
            });
            if (!stageBelongsToTeam) {
              // Si el stage no pertenece al equipo, asignar el primer stage del equipo
              if (stagesForTeam.length > 0) {
                const firstStage = stagesForTeam[0];
                console.warn(
                  `⚠️ StageId ${stageIdValue} no pertenece al TeamId ${teamIdForPayload}, asignando primer stage: ${firstStage.id}`,
                );
                // Actualizar también en la oportunidad para mantener consistencia
                this.opportunity.stageId = firstStage.id;
                this.opportunity.stage = firstStage;
                stageIdValue = firstStage.id;
              } else {
                // Si no hay stages para el equipo, retornar null (el backend debería manejarlo)
                console.warn(
                  `⚠️ No hay stages para el TeamId ${teamIdForPayload}, enviando null`,
                );
                stageIdValue = null;
              }
            }
          } else if (stagesForTeam.length > 0) {
            // Si no hay stageId pero hay stages disponibles, asignar el primero
            const firstStage = stagesForTeam[0];
            console.warn(
              `⚠️ No hay stageId para el TeamId ${teamIdForPayload}, asignando primer stage: ${firstStage.id}`,
            );
            this.opportunity.stageId = firstStage.id;
            this.opportunity.stage = firstStage;
            stageIdValue = firstStage.id;
          }
        }

        console.log(
          '🔍 buildCompleteOpportunityPayload - stageId final:',
          stageIdValue,
        );
        return stageIdValue;
      })(),
      lostReasonId: getIdValue(this.opportunity?.lostReasonId),
      countryId: getIdValue(this.opportunity?.countryId),
      stateId: getIdValue(this.opportunity?.stateId),
      chatId: getIdValue(this.opportunity?.chatId),
      costCenterId:
        path === 'costCenterId'
          ? getIdValue(processedValue)
          : getIdValue(this.opportunity?.costCenterId),
      projectId:
        path === 'projectId'
          ? getIdValue(processedValue)
          : getIdValue(
              this.opportunity?.projectId ??
                this.opportunity?.projects?.[0]?.id,
            ),
      intermediaryCompanyId:
        path === 'intermediaryCompanyId'
          ? getIdValue(processedValue)
          : getIdValue(this.opportunity?.intermediaryCompanyId),
      currencyId:
        path === 'currencyId'
          ? getIdValue(processedValue)
          : getIdValue(this.opportunity?.currencyId),
    };

    // Si el campo modificado no está en el payload base, agregarlo
    if (!payload.hasOwnProperty(path)) {
      // Si es tags, usar tagIds en el payload
      if (path === 'tags') {
        payload.tagIds = processedValue;
      } else if (path === 'description') {
        // Para description, asegurar que sea cadena vacía si está vacío, no null
        payload[path] =
          processedValue === null ||
          processedValue === undefined ||
          processedValue.trim() === ''
            ? ''
            : processedValue;
      } else {
        payload[path] = processedValue;
      }
    } else if (path === 'description') {
      // Si description ya está en el payload (aunque no debería), asegurar que sea cadena vacía si está vacío
      payload[path] =
        processedValue === null ||
        processedValue === undefined ||
        processedValue.trim() === ''
          ? ''
          : processedValue;
    }

    return this.payloadBuilder.buildOpportunityUpdatePayload(
      this.opportunity,
      {
        ...payload,
        ...(path === 'name' ? { name: processedValue || '' } : {}),
        ...(path === 'partnerId'
          ? { partnerId: partnerIdForPayload ?? null }
          : {}),
      },
    );
  }

  // Método genérico para actualizar un campo individual (similar a list-key-value)
  patchField(path: string, value: any): Observable<any> {
    const opportunityId = this.opportunity?.id || this.opportunity?.originalId;
    if (!opportunityId) {
      console.error('No se pudo identificar la oportunidad');
      return of(null);
    }

    // Manejo especial para tags: el endpoint de tags recibe operaciones individuales
    if (path === 'tags') {
      const tagIds = Array.isArray(value)
        ? value
            .map((tag: any) => (typeof tag === 'object' ? tag.id : tag))
            .filter((id: any) => id !== null && id !== undefined)
        : [];

      // Usar el backup guardado (debe venir de onFieldUpdate)
      const previousTagsRaw =
        this.backupFieldValues[path] !== undefined
          ? this.backupFieldValues[path]
          : [];

      const currentTagIds = Array.isArray(previousTagsRaw)
        ? previousTagsRaw
            .map((tag: any) => (typeof tag === 'object' ? tag.id : tag))
            .filter((id: any) => id !== null && id !== undefined)
        : [];

      const tagsToAdd = tagIds.filter(
        (id: number) => !currentTagIds.includes(id),
      );
      const tagsToRemove = currentTagIds.filter(
        (id: number) => !tagIds.includes(id),
      );

      // Si no hay cambios reales, salir
      if (tagsToAdd.length === 0 && tagsToRemove.length === 0) {
        return of(null);
      }

      const tagRequests: Observable<PostResponse>[] = [];

      tagsToAdd.forEach((id: number) => {
        tagRequests.push(this.opportunityService.addLeadTag(opportunityId, id));
      });

      tagsToRemove.forEach((id: number) => {
        tagRequests.push(
          this.opportunityService.removeLeadTag(opportunityId, id),
        );
      });

      if (tagRequests.length > 0) {
        return forkJoin(tagRequests).pipe(
          takeUntilDestroyed(this.destroyRef),
          map(() => {
            if (this.opportunity) {
              this.opportunity.tags = tagIds;
            }
            this.updateEvent.emit(this.opportunity);
            this.cdr.detectChanges();
            return null;
          }),
          catchError((error) => {
            console.error('Error actualizando tags:', error);
            // Revertir al valor previo
            if (this.opportunity && previousTagsRaw !== undefined) {
              this.opportunity.tags = previousTagsRaw;
              this.cdr.detectChanges();
            }
            throw error;
          }),
        );
      }

      return of(null);
    }

    // Campos obligatorios: para leads no exigir opportunityType ni costCenterId
    const requiredFields = this.isLead
      ? ['partnerId', 'teamId']
      : ['opportunityType', 'costCenterId', 'partnerId', 'teamId'];
    if (requiredFields.includes(path) && (!value || value === '')) {
      console.warn(
        `${path} es obligatorio, no se puede enviar null. Revertiendo cambio.`,
      );
      // Revertir el cambio
      if (this.backupFieldValues[path] !== undefined && this.opportunity) {
        this.opportunity[path] = this.backupFieldValues[path];
        this.cdr.detectChanges();
      }
      return of(null);
    }

    // Construir el payload completo con todas las propiedades
    const patchPayload = this.buildCompleteOpportunityPayload(path, value);
    this.payloadToSave = patchPayload;

    // Determinar si es un lead o una oportunidad para usar el endpoint correcto
    const updateObservable = this.isLead
      ? this.opportunityService.patchLead(patchPayload)
      : this.opportunityService.patchOpportunity(patchPayload);

    // Retornar el observable para que el llamador pueda manejarlo
    return updateObservable.pipe(
      takeUntilDestroyed(this.destroyRef),
      map((response: any) => {
        // Actualizar la oportunidad con la respuesta del backend
        if (response?.data) {
          const updatedOpportunity =
            this.opportunitiesUtilsService.mapOpportunityData(response.data);
          if (updatedOpportunity) {
            // Preservar datos locales que no vienen del backend
            this.opportunity = {
              ...this.opportunity,
              ...updatedOpportunity,
              documents: this.opportunity?.documents || [],
              chat: this.opportunity?.chat,
            };
            // Actualizar isLost después de actualizar la oportunidad
            this.updateIsLost();
            // Actualizar isWon después de actualizar la oportunidad
            this.updateIsWon();
            // Actualizar canCreateQuote después de actualizar la oportunidad
            this.updateCanCreateQuote();
            // Emitir evento para actualizar el componente padre
            this.updateEvent.emit(this.opportunity);
            this.cdr.detectChanges();
          }
        } else if (response?.id) {
          const updatedOpportunity =
            this.opportunitiesUtilsService.mapOpportunityData(response);
          if (updatedOpportunity) {
            this.opportunity = {
              ...this.opportunity,
              ...updatedOpportunity,
              documents: this.opportunity?.documents || [],
              chat: this.opportunity?.chat,
            };
            // Actualizar isLost después de actualizar la oportunidad
            this.updateIsLost();
            // Actualizar isWon después de actualizar la oportunidad
            this.updateIsWon();
            // Actualizar canCreateQuote después de actualizar la oportunidad
            this.updateCanCreateQuote();
            // Emitir evento para actualizar el componente padre
            this.updateEvent.emit(this.opportunity);
            this.cdr.detectChanges();
          }
        } else {
          // Fallback: actualizar solo el campo específico
          if (this.opportunity) {
            // Si es de tipo Date se transforma con toISOString
            const processedValue =
              typeof value === 'object' && value?.toISOString?.()
                ? value.toISOString()
                : value;
            this.opportunity[path] = processedValue;
            // Actualizar isLost si se modificó dateLost
            if (path === 'dateLost') {
              this.updateIsLost();
            }
            // Actualizar isWon si se modificó dateWon
            if (path === 'dateWon') {
              this.updateIsWon();
            }
            // Actualizar canCreateQuote si se modificó stageId
            if (path === 'stageId') {
              this.updateCanCreateQuote();
            }
            // Emitir evento para actualizar el componente padre
            this.updateEvent.emit(this.opportunity);
            this.cdr.detectChanges();
          }
        }
        return response;
      }),
      catchError((error) => {
        console.error(`❌ Error updating field ${path}:`, error);

        // Revertir el cambio visualmente
        const revertedValue =
          this.backupFieldValues[path] !== undefined
            ? this.backupFieldValues[path]
            : this.opportunity
              ? this.opportunity[path]
              : undefined;

        // Obtener el nombre del campo en español
        const fieldLabel = this.fieldLabels[path] || path;

        // Detectar campos faltantes basándose en el error o en validaciones
        let missingFieldLabel = '';
        if (path === 'clientCompany' && !this.opportunity?.partnerId) {
          missingFieldLabel = `: falta el campo ${
            this.fieldLabels['partnerId'] || 'Contacto'
          }`;
        } else if (error?.error?.message) {
          // Intentar extraer información del error del backend
          const errorMsg = error.error.message.toLowerCase();
          if (
            errorMsg.includes('contact') ||
            errorMsg.includes('contacto') ||
            errorMsg.includes('partner')
          ) {
            missingFieldLabel = `: falta el campo ${
              this.fieldLabels['partnerId'] || 'Contacto'
            }`;
          } else if (errorMsg.includes('team') || errorMsg.includes('equipo')) {
            missingFieldLabel = `: falta el campo ${
              this.fieldLabels['teamId'] || 'Equipo de ventas'
            }`;
          } else if (errorMsg.includes('cost') || errorMsg.includes('centro')) {
            missingFieldLabel = `: falta el campo ${
              this.fieldLabels['costCenterId'] || 'Centro de costos'
            }`;
          }
        }

        // Personalizar el mensaje de error
        const errorMessage = this.isLead
          ? `Error al guardar el campo "${fieldLabel}" del lead${missingFieldLabel}`
          : `Error al guardar el campo "${fieldLabel}" de la oportunidad${missingFieldLabel}`;

        // Mostrar notificación de error personalizada
        this.showErrorNotification(errorMessage);

        // Revertir el cambio creando una nueva referencia del objeto para que Angular detecte el cambio
        if (revertedValue !== undefined && this.opportunity) {
          // Guardar el valor que se intentó guardar (antes de revertir) para que el hijo lo detecte
          const attemptedValue = this.opportunity[path];

          // Crear una nueva referencia del objeto para forzar la detección de cambios
          this.opportunity = {
            ...this.opportunity,
            [path]: revertedValue,
          };

          // Actualizar también las propiedades computadas relacionadas
          if (path === 'clientCompany') {
            // Si se revierte clientCompany, restaurar también partnerId si había backup
            if (this.backupFieldValues['partnerId'] !== undefined) {
              this.opportunity.partnerId = this.backupFieldValues['partnerId'];
              // Buscar el contactPartner si hay partnerId
              if (this.opportunity.partnerId) {
                const partner = this.globalValuesService.partnerlist.find(
                  (p: any) => p.id === this.opportunity.partnerId,
                );
                this.opportunity.contactPartner = partner || null;
              } else {
                this.opportunity.contactPartner = null;
              }
            }
          }

          if (path === 'stageId') {
            this.updateCanCreateQuote();
            this.updateCurrentStageName();
            this.updateComputedProperties();
          }

          if (path === 'name') {
            this.originalOpportunityName = revertedValue;
            this.opportunity = {
              ...this.opportunity,
              name: revertedValue,
              oportunidad: revertedValue,
              originalOpportunityName: revertedValue,
            };
            this.updateComputedProperties();
          }

          console.log('🔄 Revertiendo campo', path, {
            attemptedValue,
            revertedValue,
            backupValue: this.backupFieldValues[path],
          });

          // Forzar detección de cambios - Angular debería detectar el cambio de referencia
          this.cdr.detectChanges();

          // Usar setTimeout para asegurar que el cambio se propague al hijo
          setTimeout(() => {
            // Forzar otra actualización para asegurar que el hijo vea el cambio
            this.opportunity = { ...this.opportunity };
            this.cdr.detectChanges();
            console.log('✅ Cambio propagado al hijo para', path);
          }, 100);
        }

        // NO re-emitir el error - ya lo manejamos completamente aquí
        // Retornar un observable vacío para que el flujo continúe sin error
        return of(null);
      }),
    );
  }

  protected readonly isExpirationAlert = isExpirationAlert;

  // Get opportunity name (handles the mapping where name is overwritten with contact name)
  getOpportunityName(): string {
    return this.detailsHelper.getOpportunityName(
      this.opportunity,
      this.originalOpportunityName,
    );
  }

  // Stage methods
  updateStagesForTeam(): void {
    if (this.isLead) {
      const allStages = this.getStagesForTeam();
      this.stagesForTeam = allStages.filter(
        (stage) => stage.useForLeads === true,
      );
    } else {
      this.stagesForTeam = this.getStagesForTeam();
    }
  }

  updateCurrentStageName(): void {
    if (this.viewMode === 'budget') {
      // Actualizar las etapas a mostrar (normales + cancelado si está cancelado)
      const movableStages = BUDGET_STAGES.filter((s) => s.value !== 'Cancelled');
      if (this.budget?.dateCancelled) {
        const cancelledStage = BUDGET_STAGES.find((s) => s.value === 'Cancelled')!;
        this.budgetStagesToDisplay = [...movableStages, cancelledStage];
        this.currentBudgetStageValue = 'Cancelled';
        this.currentBudgetStageName = cancelledStage.name;
      } else if (this.budget?.isActive === false) {
        this.budgetStagesToDisplay = [DRAFT_BUDGET_STAGE, ...movableStages];
        this.currentBudgetStageValue = DRAFT_BUDGET_STAGE.value;
        this.currentBudgetStageName = DRAFT_BUDGET_STAGE.name;
      } else {
        // Para presupuesto, usar la etapa del presupuesto
        const stageValue = this.budget?.stage || 'Budget';
        this.currentBudgetStageValue = stageValue;
        this.budgetStagesToDisplay = movableStages;
        const currentStage = movableStages.find((s) => s.value === stageValue);
        this.currentBudgetStageName = currentStage?.name || 'Presupuesto';
      }
    } else {
      // Para oportunidad/lead, usar la etapa de la oportunidad
      this.currentStageName =
        this.getStageName(this.opportunity?.stageId) || 'Nuevo';
    }
  }

  isCurrentStage(stageName: string): boolean {
    return this.currentStageName === stageName;
  }

  // Budget stage methods
  isCurrentBudgetStage(stageValue: string): boolean {
    return this.currentBudgetStageValue === stageValue;
  }

  private readonly budgetStageHoverClasses: { [key: string]: string } = {
    Budget: 'blue-hover',
    BudgetSent: 'purple-hover',
    SaleOrder: 'green-hover',
    Cancelled: 'red-hover',
  };

  getBudgetStageHoverClass(stageValue: string): string {
    return this.budgetStageHoverClasses[stageValue] || 'gray-hover';
  }

  // Cambiar la etapa del presupuesto usando el endpoint correspondiente a cada destino
  changeBudgetStage(stage: BudgetStage | DraftBudgetStage): void {
    if (!this.nzData.editPermissions) {
      return;
    }
    // `Draft` has `route: null` — it is never a transition target (view-only state).
    if (!stage.route) {
      return;
    }
    if (!this.budget?.id || this.currentBudgetStageValue === stage.value) {
      return;
    }

    const leadId = this.opportunity?.id || this.opportunity?.originalId;
    if (!leadId) {
      return;
    }

    // Cancellation carve-out (F-2): the block guard MUST NOT run when the target is
    // cancellation — a confirmed quote may still be cancelled from the stepper too.
    if (stage.value !== 'Cancelled') {
      const block = findBudgetStageBlock(this.budget);
      if (block) {
        this.toastConfig = {
          icon: 'dangerous',
          iconColor: '#CB2A2F',
          content: block.reason,
          showUndo: false,
        };
        this.notRef = this.notificationService.create(
          'blank',
          '',
          this.notificationTmpl,
          { nzPlacement: 'bottomRight' },
        );
        return;
      }
    }

    const request$ =
      stage.route === 'confirm'
        ? this.opportunityService.confirmSaleOrder(this.budget.id, leadId)
        : stage.route === 'cancel'
          ? this.opportunityService.cancelSaleOrder(this.budget.id, leadId)
          : this.opportunityService.changeSaleOrderStage(this.budget.id, leadId, stage.value);

    request$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response: any) => {
        if (response?.data) {
          this.budget = { ...this.budget, ...response.data };
          const index = this.budgetList.findIndex(
            (b: any) => b.id === this.budget.id,
          );
          if (index !== -1) {
            this.budgetList[index] = { ...this.budget };
          }
          this.updateComputedProperties();
          this.cdr.detectChanges();

          // Tell the list this quote moved, so the kanban card follows it. The drawer has
          // no handle on the budgets list; updateEvent is the seam the parent already
          // listens on for the other maestros.
          this.updateEvent.emit(this.budget);

          this.toastConfig = {
            icon: 'check_circle',
            iconColor: '#6CDA75',
            content: `El presupuesto se movió a "${stage.name}".`,
            showUndo: false,
          };
          this.notRef = this.notificationService.create(
            'blank',
            '',
            this.notificationTmpl,
            { nzPlacement: 'bottomRight' },
          );
        }
      },
      error: (error: any) => {
        console.error('Error changing budget stage:', error);
        this.toastConfig = {
          icon: 'dangerous',
          iconColor: '#CB2A2F',
          content: error.error?.message || 'No fue posible mover el presupuesto.',
          showUndo: false,
        };
        this.notRef = this.notificationService.create(
          'blank',
          '',
          this.notificationTmpl,
          { nzPlacement: 'bottomRight' },
        );
      },
    });
  }

  // Cambiar la etapa de la oportunidad usando el endpoint específico
  changeStage(stage: any): void {
    // Solo permitir cambio de etapa si está en modo edición
    if (!this.nzData.editPermissions) {
      return;
    }

    const stageId = stage.id;

    // Verificar si el valor cambió
    const backupValue = this.opportunity?.stageId;
    if (backupValue === stageId || backupValue == stageId) {
      return; // No hay cambio
    }

    // Guardar el valor anterior para poder revertir si es necesario
    const path = 'stageId';
    if (!this.backupFieldValues[path]) {
      this.backupFieldValues[path] = backupValue;
    }

    // Actualizar solo el objeto local - NO enviar al backend todavía
    // El cambio se guardará cuando se presione el botón "Guardar"
    if (this.opportunity) {
      this.opportunity.stageId = stageId;

      // Si la etapa trae probabilidad, actualizarla; si no, intentar obtenerla de la lista de etapas
      let stageProbability =
        stage?.probability !== undefined && stage?.probability !== null
          ? stage.probability
          : null;
      if (stageProbability === null) {
        const stageFromList = this.stagesForTeam?.find(
          (s: any) => s.id === stageId,
        );
        if (
          stageFromList &&
          stageFromList.probability !== undefined &&
          stageFromList.probability !== null
        ) {
          stageProbability = stageFromList.probability;
        }
      }
      if (stageProbability !== null) {
        this.opportunity.probability = stageProbability;
      }

      // Forzar cambio de referencia para que los hijos detecten cambios
      this.opportunity = { ...this.opportunity };

      // Actualizar propiedades computadas que dependen de stageId
      this.updateCanCreateQuote();
      this.updateCurrentStageName();
      this.updateComputedProperties();

      // Actualizar la vista sin enviar al backend
      this.cdr.detectChanges();
    }

    this.onFieldUpdate({ path, value: stageId, previousValue: backupValue });
  }

  // Change opportunity (navigation similar to service orders)
  changeOpportunity(isNext: boolean, isPrev: boolean, id: number): void {
    this.changeOpportunityEvent.emit({ isNext, isPrev, id });
  }

  private getBudgetDisplayName(): string {
    const opportunityName = (this.budget?.lead?.oportunidad || this.budget?.lead?.originalOpportunityName || this.budget?.originalOpportunityName || this.opportunity?.oportunidad || this.opportunity?.originalOpportunityName || this.budget?.lead?.name || this.opportunity?.name || '').trim();
    const budgetCode = (this.budget?.budgetCode || '').trim();

    if (opportunityName && opportunityName !== '-' && budgetCode && budgetCode !== '-') {
      return `${opportunityName} # ${budgetCode}`;
    }

    return (this.budget?.name || opportunityName || budgetCode || '-').trim();
  }

  getBudgetNavigationReference(budget: any): string {
    return resolveSaleOrderNavigationReference(budget);
  }

  // Update computed properties
  updateComputedProperties(): void {
    // Update stages
    this.updateStagesForTeam();
    this.updateCurrentStageName();

    if (this.viewMode === 'budget') {
      // Update budget properties
      if (this.budget) {
        this.budgetName = this.getBudgetDisplayName();
        this.saleOrderName = resolveSaleOrderName(this.budget);
        this.budgetNavigationReference =
          this.getBudgetNavigationReference(this.budget);
        // El stage se actualiza en updateCurrentStageName() que ya se llamó arriba
        this.hasMultipleBudgets = (this.budgetList?.length || 0) > 1;
      }

      // También actualizar opportunityName cuando estamos en modo budget
      // para que el header muestre el nombre correcto de la oportunidad
      if (this.opportunity) {
        if (
          this.opportunity?.oportunidad &&
          this.opportunity.oportunidad !== '-'
        ) {
          this.opportunityName = this.opportunity.oportunidad;
        } else if (
          this.opportunity?.originalOpportunityName &&
          this.opportunity.originalOpportunityName !== '-'
        ) {
          this.opportunityName = this.opportunity.originalOpportunityName;
        } else {
          const contactName = this.getContactName();
          if (
            this.opportunity?.name &&
            this.opportunity.name !== contactName &&
            this.opportunity.name !== '-'
          ) {
            this.opportunityName = this.opportunity.name;
          } else {
            this.opportunityName = '-';
          }
        }

        // Current opportunity ID
        this.currentOpportunityId =
          this.opportunity?.id || this.opportunity?.originalId || 0;

        // Opportunity users
        this.opportunityUsers = this.getOpportunityUsers();
      }

      return;
    }

    if (!this.opportunity) return;

    // Opportunity name
    if (this.opportunity?.oportunidad && this.opportunity.oportunidad !== '-') {
      this.opportunityName = this.opportunity.oportunidad;
    } else if (
      this.opportunity?.originalOpportunityName &&
      this.opportunity.originalOpportunityName !== '-'
    ) {
      this.opportunityName = this.opportunity.originalOpportunityName;
    } else {
      const contactName = this.getContactName();
      if (
        this.opportunity?.name &&
        this.opportunity.name !== contactName &&
        this.opportunity.name !== '-'
      ) {
        this.opportunityName = this.opportunity.name;
      } else {
        this.opportunityName = '-';
      }
    }

    // Current opportunity ID
    this.currentOpportunityId =
      this.opportunity?.id || this.opportunity?.originalId || 0;

    // NO cargar seguidores aquí - ya se carga en ngOnInit() independientemente
    // this.loadMailFollowers(); // Removido para evitar que se ejecute después de actividades

    // Has multiple opportunities
    this.hasMultipleOpportunities =
      (this.nzData.opportunityList?.length || 0) > 1;
  }

  // Load budgets list (solo se llama al abrir el drawer)
  loadBudgets(): void {
    const opportunityId = this.opportunity?.id || this.opportunity?.originalId;
    if (!opportunityId) return;
    const companyId = this.getOpportunityCompanyId();
    if (companyId === null) {
      this._budgetsNeedReload = true;
      return;
    }

    console.log('🔵 [Budget] loadBudgets para opportunityId:', opportunityId);

    this.opportunityService
      .getSaleOrders(
        {
          startRow: 0,
          endRow: 100,
          sortDirection: 'desc',
          sortProperty: 'budgetDate',
          leadId: opportunityId,
        },
        undefined,
        companyId,
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          console.log('🔵 [Budget] Respuesta de getSaleOrders:', response);
          console.log(
            '🔵 [Budget] Número de budgets:',
            response?.data?.rows?.length || 0,
          );
          if (response?.data?.rows && response.data.rows.length > 0) {
            console.log('🔵 [Budget] Primer budget:', response.data.rows[0]);
            console.log(
              '🔵 [Budget] Primer budget chatId:',
              response.data.rows[0]?.chatId,
            );
            console.log(
              '🔵 [Budget] Primer budget chat:',
              response.data.rows[0]?.chat,
            );
          }
          this.budgetList = response?.data?.rows || [];
          this.sortBudgets();
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('❌ [Budget] Error loading budgets:', error);
          this.budgetList = [];
          this.showBudgetRequestError(
            error,
            'No se pudieron cargar los presupuestos. Inténtalo de nuevo.',
          );
          this.cdr.detectChanges();
        },
      });
  }

  private getOpportunityCompanyId(): number | null {
    const companyId = Number(this.opportunity?.companyId);
    return Number.isInteger(companyId) && companyId > 0 ? companyId : null;
  }

  private showBudgetRequestError(
    error: any,
    fallbackMessage: string,
    authorizationMessage =
      'No tienes autorización para acceder a los presupuestos de la compañía de esta oportunidad.',
  ): void {
    const isAuthorizationError = error?.status === 401 || error?.status === 403;
    this.showErrorNotification(
      isAuthorizationError ? authorizationMessage : fallbackMessage,
    );
  }

  // Ordenar la lista de presupuestos: primero los aprobados (con dateConfirm),
  // manteniendo el orden por fecha que viene del backend
  private sortBudgets(): void {
    if (!this.budgetList || this.budgetList.length === 0) return;

    this.budgetList = [...this.budgetList].sort((a: any, b: any) => {
      // Verificar si están aprobados (tienen dateConfirm)
      const aIsApproved = !!a.dateConfirm;
      const bIsApproved = !!b.dateConfirm;

      // Si uno está aprobado y el otro no, el aprobado va primero
      if (aIsApproved && !bIsApproved) return -1;
      if (!aIsApproved && bIsApproved) return 1;

      // Si ambos están aprobados o ambos no están aprobados, mantener el orden original (ya viene ordenado por fecha del backend)
      return 0;
    });
  }

  // Handle budget deletion
  onBudgetDeleted(budgetId: number): void {
    // Eliminar el presupuesto de la lista
    const index = this.budgetList.findIndex((b: any) => b.id === budgetId);
    if (index !== -1) {
      this.budgetList.splice(index, 1);

      // Si el presupuesto eliminado era el que estaba siendo visualizado, volver a la vista de oportunidad
      if (this.budget?.id === budgetId) {
        this.closeBudgetView();
      }

      // Actualizar propiedades computadas
      this.updateComputedProperties();
      this.cdr.detectChanges();
    }
  }

  // Open budget view (acepta ID o objeto completo)
  openBudgetView(budgetOrId: any): void {
    if (!budgetOrId) return;

    // Si es un ID, buscar o cargar el presupuesto completo
    if (typeof budgetOrId === 'number') {
      const budgetId = budgetOrId;

      // Primero buscar en la lista actual
      let budget = this.budgetList.find((b: any) => b.id === budgetId);

      if (!budget) {
        // Si no está en la lista, recargar la lista
        this.loadBudgets();

        // Esperar a que se carguen los presupuestos
        setTimeout(() => {
          budget = this.budgetList.find((b: any) => b.id === budgetId);
          if (budget) {
            this.setBudgetView(budget);
          } else {
            // Si aún no está, cargar directamente desde el backend
            this.loadBudgetById(budgetId);
          }
        }, 100);
        return;
      }

      this.setBudgetView(budget);
    } else {
      // Es un objeto completo
      this.setBudgetView(budgetOrId);
    }
  }

  // Método privado para establecer la vista de presupuesto
  private setBudgetView(budget: any): void {
    if (!budget) return;

    this.hasBudgetQuotationLines = false;
    this.isBudgetCurrencyConversionValid = true;
    console.log('🔵 [Budget] setBudgetView llamado con budget:', budget);
    console.log('🔵 [Budget] budget.id:', budget.id);
    console.log('🔵 [Budget] budget.chatId:', budget.chatId);
    console.log('🔵 [Budget] budget.chat:', budget.chat);

    // Asegurar que budgetList esté cargado
    if (this.budgetList.length === 0) {
      console.log('🔵 [Budget] budgetList vacía, cargando...');
      this.loadBudgets();
      // Esperar a que se carguen los presupuestos antes de continuar
      setTimeout(() => {
        this.setBudgetView(budget);
      }, 200);
      return;
    }

    // Evitar llamadas duplicadas mientras se está estableciendo el budget
    // Verificar si ya estamos en el mismo budget para evitar reemplazarlo
    if (this.budget?.id === budget.id && this.viewMode === 'budget') {
      console.log(
        '🔵 [Budget] Ya estamos en este budget, evitando llamada duplicada',
      );
      return;
    }

    // backup para poder cancelar sin persistir cambios locales
    this.budgetModeOpportunityBackup = this.opportunity
      ? JSON.parse(JSON.stringify(this.opportunity))
      : null;
    this.budgetBackup = budget ? JSON.parse(JSON.stringify(budget)) : null;

    this.viewMode = 'budget';
    this.budget = budget;

    // Cargar actividades, chat y seguidores específicos del presupuesto
    this.loadActivities();
    if (this.budget?.chatId) {
      console.log(
        '🔵 [Budget] Cargando chat del budget con chatId:',
        this.budget.chatId,
      );
      this.loadChat(this.budget.chatId, this.budget);
    } else {
      console.warn(
        '⚠️ [Budget] Budget no tiene chatId, no se puede cargar el chat',
      );
    }

    // Encontrar el índice del presupuesto actual en la lista ya cargada
    this.currentBudgetIndex = this.budgetList.findIndex(
      (b: any) => b.id === budget.id,
    );
    if (this.currentBudgetIndex === -1) {
      // Si no está en la lista, agregarlo al principio
      this.budgetList.unshift(budget);
      this.currentBudgetIndex = 0;
    }

    // Actualizar todas las propiedades computadas (incluyendo opportunityName, budgetName, etc.)
    this.updateComputedProperties();

    // Recargar actividades cuando se cambia a modo budget para mostrar las actividades del SaleOrder
    if (this.budget?.id) {
      this.activitiesLoaded = false; // Resetear flag para forzar recarga
      this.loadActivities();
    }

    // Forzar detección de cambios para actualizar el header y otros elementos
    this.cdr.detectChanges();

    // Navegar agregando budget al historial (sin replace) para que Atrás vuelva a opportunity
    const budgetId = budget?.id;
    const opportunityId = this.opportunity?.id || this.opportunity?.originalId;
    if (budgetId && opportunityId && !this.isInitializing) {
      this.router.navigate([], {
        queryParams: { budget: budgetId },
        queryParamsHandling: 'merge',
        replaceUrl: false,
      });
    }
  }

  /**
   * Agrega el parámetro budget al historial del navegador
   * Esto asegura que el historial tenga:
   * - Estado anterior: /opportunities?opportunity=:id
   * - Estado actual: /opportunities?opportunity=:id&budget=:budgetId
   */
  private ensureBudgetBackGoesToOpportunity(): void {
    if (this.isInitializing) return;
    const opportunityId = this.opportunity?.id || this.opportunity?.originalId;
    const budgetId = this.budget?.id;
    if (!opportunityId || !budgetId) return;

    const currentUrl = this.router.url;
    const isOnOpportunitiesBase =
      currentUrl === '/opportunities' ||
      currentUrl.startsWith('/opportunities?');
    const isOnBudgetsRoute = currentUrl.includes('/opportunities/budgets');

    // Solo aplica a la apertura en /opportunities (no tocar el flujo especial de /opportunities/budgets)
    if (!isOnOpportunitiesBase || isOnBudgetsRoute) return;

    // Verificar si ya tenemos el parámetro budget en la URL
    const urlParams = new URLSearchParams(currentUrl.split('?')[1] || '');
    if (urlParams.get('budget') === budgetId.toString()) {
      return; // Ya está en la URL, no hacer nada
    }

    // Construir la nueva URL con el parámetro budget
    const basePath = this.isLead ? '/opportunities/leads' : '/opportunities';
    const newUrl = `${basePath}?opportunity=${opportunityId}&budget=${budgetId}`;

    // Usar history.pushState para agregar un nuevo estado al historial
    // Esto permite que el botón Atrás funcione correctamente
    window.history.pushState(null, '', newUrl);
  }

  // Cargar un presupuesto por ID desde el backend
  private loadBudgetById(budgetId: number): void {
    const opportunityId = this.opportunity?.id || this.opportunity?.originalId;
    if (!opportunityId) return;
    const companyId = this.getOpportunityCompanyId();
    if (companyId === null) {
      this.budgetList = [];
      this.showErrorNotification(
        'La oportunidad no tiene una compañía válida. No se pueden cargar sus presupuestos.',
      );
      this.cdr.detectChanges();
      return;
    }

    console.log(
      '🔵 [Budget] loadBudgetById para budgetId:',
      budgetId,
      'opportunityId:',
      opportunityId,
    );

    this.opportunityService
      .getSaleOrders(
        {
          startRow: 0,
          endRow: 100,
          sortDirection: 'desc',
          sortProperty: 'budgetDate',
          leadId: opportunityId,
        },
        undefined,
        companyId,
      )
      .pipe(take(1))
      .subscribe({
        next: (response: any) => {
          console.log(
            '🔵 [Budget] Respuesta de getSaleOrders en loadBudgetById:',
            response,
          );
          this.budgetList = response?.data?.rows || [];
          const budget = this.budgetList.find((b: any) => b.id === budgetId);
          console.log('🔵 [Budget] Budget encontrado:', budget);
          console.log('🔵 [Budget] Budget chatId:', budget?.chatId);
          console.log('🔵 [Budget] Budget chat:', budget?.chat);
          if (budget) {
            this.setBudgetView(budget);
          }
        },
        error: (error) => {
          console.error('❌ [Budget] Error loading budget:', error);
          this.budgetList = [];
          this.showBudgetRequestError(
            error,
            'No se pudo cargar el presupuesto. Inténtalo de nuevo.',
          );
          this.cdr.detectChanges();
        },
      });
  }

  // Load budget (solo cambia el presupuesto actual, no hace peticiones)
  loadBudget(budget: any): void {
    if (!budget) return;
    // Usar setBudgetView para mantener consistencia
    this.setBudgetView(budget);
  }

  // Close budget view (solo cambia la vista, no cierra el drawer)
  // skipUrlUpdate: útil cuando el navegador ya está haciendo popstate (Atrás) y NO queremos disparar otra navegación
  closeBudgetView(skipUrlUpdate: boolean = false): void {
    if (this.guardUnsavedNewBudgetLeave(() => this.closeBudgetView(skipUrlUpdate))) {
      return;
    }

    // Limpiar estados de edición si hay alguno activo
    if (this.isBudgetEditModeEnabled) {
      // Si el presupuesto no tiene ID (es temporal), asegurarse de que no esté en la lista
      // (aunque ya no debería estar porque no lo agregamos en openBudgetDrawer)
      if (this.budget && !this.budget.id) {
        const tempIndex = this.budgetList.findIndex((b) => b.id === null);
        if (tempIndex !== -1) {
          this.budgetList.splice(tempIndex, 1);
        }
      }

      this.budgetEditModeAction = 'cancel';
      this.cdr.detectChanges();

      this.budgetEditModeAction = 'none';
      this.isBudgetEditModeEnabled = false;
    }

    // Limpiar backups (no restaurar, recargar desde backend para obtener valores actualizados)
    this.budgetModeOpportunityBackup = null;
    this.budgetBackup = null;

    // Cambiar el viewMode antes de actualizar propiedades
    this.viewMode = this.isLead ? 'lead' : 'opportunity';
    this.budget = null;
    // NO limpiar budgetList completamente, mantenerlo para que los tabs funcionen
    // this.budgetList = [];
    this.currentBudgetIndex = 0;

    // Recargar la oportunidad desde el backend para obtener valores actualizados
    // (totalRevenue y totalExpenses que pueden haber cambiado al modificar saleOrderLines)
    const opportunityId = this.opportunity?.id || this.opportunity?.originalId;
    if (opportunityId) {
      this.opportunityService
        .getOpportunityById(opportunityId, undefined, undefined, true)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (response: any) => {
            let updatedOpportunity = null;

            if (
              response?.data?.rows &&
              Array.isArray(response.data.rows) &&
              response.data.rows.length > 0
            ) {
              updatedOpportunity = response.data.rows[0];
            } else if (
              response?.data &&
              Array.isArray(response.data) &&
              response.data.length > 0
            ) {
              updatedOpportunity = response.data[0];
            } else if (
              response?.data &&
              typeof response.data === 'object' &&
              !Array.isArray(response.data)
            ) {
              updatedOpportunity = response.data;
            }

            if (updatedOpportunity) {
              const existing = this.opportunity;
              this.opportunity = this.mergeOpportunityFromApi(
                updatedOpportunity,
                existing,
              );
              this.updateComputedProperties();
              this.updateIsLost();
              this.updateIsWon();
              this.updateCanCreateQuote();
              this.loadOpportunityChat();
            }

            // Recargar actividades cuando se sale del modo budget para mostrar las actividades de la oportunidad/lead
            this.activitiesLoaded = false; // Resetear flag para forzar recarga
            this.loadActivities();

            // Forzar detección de cambios
            this.cdr.detectChanges();
          },
          error: (error) => {
            console.error(
              'Error al recargar oportunidad al salir del modo budget:',
              error,
            );
            // Si falla, al menos actualizar propiedades y recargar actividades
            this.updateComputedProperties();
            this.updateIsLost();
            this.updateIsWon();
            this.updateCanCreateQuote();
            this.activitiesLoaded = false;
            this.loadActivities();
            this.cdr.detectChanges();
          },
        });
    } else {
      // Si no hay opportunityId, solo actualizar propiedades computadas y recargar actividades
      this.updateComputedProperties();
      this.updateIsLost();
      this.updateIsWon();
      this.updateCanCreateQuote();
      this.activitiesLoaded = false;
      this.loadActivities();
      this.loadOpportunityChat();
      this.cdr.detectChanges();
    }

    // Actualizar la URL (salvo que estemos manejando Atrás del navegador)
    if (!skipUrlUpdate) {
      this.updateUrlFromState();
    }
  }

  // Open budget preview - abre el PDF en una nueva pestaña
  openBudgetPreview(unitary: boolean = false): void {
    if (!this.isBudgetCurrencyConversionValid) {
      this.showErrorNotification('No se puede generar la vista previa hasta completar la conversión monetaria.');
      return;
    }

    if (!this.budget?.id || !this.hasBudgetQuotationLines) {
      console.error('No hay presupuesto seleccionado');
      return;
    }

    const saleOrderIdNum = Number(this.budget.id);
    const companyId = this.getOpportunityCompanyId();
    if (companyId === null) {
      this.previewingBudget = false;
      this.showErrorNotification(
        'La oportunidad no tiene una compañía válida. No se puede generar el PDF del presupuesto.',
      );
      this.cdr.detectChanges();
      return;
    }

    this.previewingBudget = true;
    this.cdr.detectChanges();

    // Usar el método que obtiene el PDF como blob desde /preview
    this.opportunityService
      .generateSaleOrderPdfPreview(saleOrderIdNum, unitary, companyId)
      .pipe(
        take(1),
        finalize(() => {
          this.previewingBudget = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (blob: Blob) => {
          // Generar el nombre desde la referencia vigente de la cotización.
          const budgetDisplayName =
            resolveSaleOrderNavigationReference(this.budget);
          // Sanitizar el nombre: reemplazar / por guiones y : por puntos
          const sanitizedName = budgetDisplayName
            .replace(/\//g, '-')  // Reemplazar / por -
            .replace(/:/g, '.')   // Reemplazar : por . (para la hora quede legible como 11.58)
            .replace(/[^\w\s.-]/gi, '')  // Eliminar otros caracteres especiales (mantener punto y guion)
            .replace(/\s+/g, '_')  // Reemplazar espacios por _
            .replace(/\.{2,}/g, '.')  // Limpiar puntos dobles o más
            .replace(/_\._/g, '_')  // Limpiar patrones _._
            .replace(/\._/g, '_')   // Limpiar patrones ._
            .replace(/_\./g, '_');  // Limpiar patrones _.
          const viewType = unitary ? 'Unitario' : 'Completo';
          const fileName = `${sanitizedName}_${viewType}.pdf`;

          // Crear un nuevo blob con el tipo correcto
          const pdfBlob = new Blob([blob], { type: 'application/pdf' });

          // Crear objeto URL para el Blob
          const url = window.URL.createObjectURL(pdfBlob);

          // Crear una nueva ventana con interfaz personalizada
          // Agregamos #toolbar=0 al URL del PDF para ocultar la barra de herramientas del visor
          const newWindow = window.open('', '_blank');

          if (newWindow) {
            newWindow.document.write('<!DOCTYPE html><html><head><title>' + fileName + '</title><meta charset="utf-8"><style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;display:flex;flex-direction:column;height:100vh;overflow:hidden;background:#525659}.header{background:#323639;border-bottom:1px solid #1c1c1c;padding:10px 20px;display:flex;align-items:center;justify-content:space-between;gap:16px;box-shadow:0 2px 8px rgba(0,0,0,0.15)}.title{font-size:14px;color:#e8eaed;font-weight:500;flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.download-btn{background:#1890ff;color:white;border:none;padding:8px 20px;border-radius:4px;font-size:14px;cursor:pointer;display:flex;align-items:center;gap:8px;transition:all 0.2s;white-space:nowrap;font-weight:500;box-shadow:0 2px 4px rgba(0,0,0,0.2)}.download-btn:hover{background:#40a9ff;transform:translateY(-1px);box-shadow:0 4px 8px rgba(0,0,0,0.3)}.download-btn:active{background:#096dd9;transform:translateY(0);box-shadow:0 2px 4px rgba(0,0,0,0.2)}.pdf-container{flex:1;width:100%;height:calc(100vh - 47px);position:relative}embed{width:100%;height:100%;border:none}</style></head><body><div class="header"><div class="title">' + fileName + '</div><button class="download-btn" onclick="downloadPdf()"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>Descargar</button></div><div class="pdf-container"><embed src="' + url + '#toolbar=0&navpanes=0&scrollbar=0" type="application/pdf"></div><script>function downloadPdf(){const link=document.createElement("a");link.href="' + url + '";link.download="' + fileName + '";document.body.appendChild(link);link.click();document.body.removeChild(link)}document.querySelector("embed").addEventListener("contextmenu",function(e){e.preventDefault();return false});</script></body></html>');
            newWindow.document.close();
          } else {
            console.error('No se pudo abrir la ventana. Verifica que los pop-ups no estén bloqueados.');
          }

          // Mantener el blob disponible mientras el usuario revisa el preview.
          // QA suele descargar luego de mirar el documento; 30s revocaba el blob demasiado pronto.
          const revokePdfUrl = () => window.URL.revokeObjectURL(url);
          if (newWindow) {
            newWindow.addEventListener('beforeunload', revokePdfUrl, { once: true });
          }
          setTimeout(revokePdfUrl, 10 * 60 * 1000);
        },
        error: (err) => {
          console.error('Error al abrir el PDF del presupuesto:', err);
          this.showBudgetRequestError(
            err,
            'No se pudo generar el PDF del presupuesto. Inténtalo de nuevo.',
            'No tienes autorización para generar el PDF del presupuesto de esta oportunidad.',
          );
        },
      });
  }

  // Generate budget PDF version - stores an immutable PDF; it does not open it
  generateBudgetVersion(): void {
    if (this.generatingVersion) {
      return;
    }
    if (!this.budget?.id || !canGenerateBudgetVersion(this.budget, this.hasBudgetQuotationLines)) {
      return;
    }
    if (!this.isBudgetCurrencyConversionValid) {
      this.showErrorNotification('No se puede generar el presupuesto hasta completar la conversión monetaria.');
      return;
    }

    const companyId = this.getOpportunityCompanyId();
    if (companyId === null) {
      this.showErrorNotification(
        'La oportunidad no tiene una compañía válida. No se puede generar el PDF del presupuesto.',
      );
      return;
    }

    const pastExpiration = getPastBudgetExpiration(this.budget.expirationDate);
    if (pastExpiration) {
      // Existing budgets have no drawer edit mode here; the expiration is edited inline, so
      // "Editar vencimiento" only closes the confirmation without generating.
      const confirmation = buildPastExpirationConfirmation(pastExpiration);
      this.modalService.confirm({
        nzTitle: confirmation.title,
        nzContent: confirmation.content,
        nzOkText: confirmation.okText,
        nzCancelText: confirmation.cancelText,
        nzOnOk: () => this.openIssueCurrencyPicker(companyId),
      });
      return;
    }

    this.openIssueCurrencyPicker(companyId);
  }

  private openIssueCurrencyPicker(companyId: number): void {
    const data: BudgetIssueCurrencyPickerData = {
      currencies: this.globalValuesService.currencies$.value,
      budgetCurrencyId: this.budget?.currencyId,
    };
    openBudgetIssueCurrencyPicker(this.modalService, data, (issueCurrencyId) =>
      this.requestBudgetVersion(companyId, issueCurrencyId),
    );
  }

  private requestBudgetVersion(companyId: number, issueCurrencyId?: number): void {
    if (this.generatingVersion || !this.budget?.id) {
      return;
    }

    const selectedIso =
      this.globalValuesService.currencies$.value.find((currency) => currency.id === issueCurrencyId)?.isoCode ?? null;

    this.generatingVersion = true;
    this.cdr.detectChanges();

    this.opportunityService
      .generateSaleOrderPdfVersion(Number(this.budget.id), companyId, issueCurrencyId)
      .pipe(
        take(1),
        finalize(() => {
          this.generatingVersion = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (response) => {
          this.showNotification(formatBudgetVersionGeneratedMessage(response));
          if (wasIssueCurrencyIgnored(response, issueCurrencyId !== undefined)) {
            this.showErrorNotification(buildIssueCurrencyIgnoredMessage());
          }
          this.pdfVersionsRefreshKey++;
        },
        error: (err) => {
          const { message, refreshList } = mapBudgetVersionGenerationError(err, selectedIso);
          this.showErrorNotification(message);
          if (refreshList) {
            this.pdfVersionsRefreshKey++;
          }
        },
      });
  }

  // Change budget (navegación entre presupuestos)
  changeBudget(isNext: boolean, isPrev: boolean, budgetId?: number): void {
    if (this.guardUnsavedNewBudgetLeave(() => this.changeBudget(isNext, isPrev, budgetId))) {
      return;
    }

    // Si budgetList está vacío, cargarlo primero
    if (this.budgetList.length === 0) {
      this.loadBudgets();
      // Esperar a que se carguen los presupuestos
      setTimeout(() => {
        this.changeBudget(isNext, isPrev, budgetId);
      }, 200);
      return;
    }

    if (budgetId) {
      const budget = this.budgetList.find((b: any) => b.id === budgetId);
      if (budget) {
        this.setBudgetView(budget);
      }
      return;
    }

    if (isNext && this.currentBudgetIndex < this.budgetList.length - 1) {
      this.currentBudgetIndex++;
      this.setBudgetView(this.budgetList[this.currentBudgetIndex]);
    } else if (isPrev && this.currentBudgetIndex > 0) {
      this.currentBudgetIndex--;
      this.setBudgetView(this.budgetList[this.currentBudgetIndex]);
    }
  }

  /**
   * Inicializa el viewMode basado en los parámetros de la ruta
   */
  private initializeViewModeFromRoute(): void {
    const routeParams = this.route.snapshot.params;
    const queryParams = this.route.snapshot.queryParams;
    const urlSegments = this.route.snapshot.url.map((segment) => segment.path);
    const currentUrl = this.router.url;

    // Verificar si se pasó budgetId en nzData primero
    const budgetIdFromData = (this.nzData as any).budgetId;

    // Si hay un budgetId en nzData, ruta o query params, mostrar vista de presupuesto
    if (budgetIdFromData || routeParams['budgetId'] || queryParams['budget']) {
      this.viewMode = 'budget';
      // El presupuesto se cargará después de que se carguen los presupuestos
      // Se cargará en loadSpecificBudget después de que se carguen los presupuestos
      return;
    }

    // Si es un lead en la ruta específica (/opportunities/lead/:id)
    if (routeParams['id'] && urlSegments.includes('lead')) {
      this.isLead = true;
      this.viewMode = 'lead';
    }
    // Si estamos en la ruta de leads (/opportunities/leads) o si ya se detectó que es lead
    else if (
      this.isLead ||
      currentUrl.includes('/leads') ||
      this.nzData.maestroName === 'Leads'
    ) {
      // Asegurar que isLead esté establecido correctamente
      if (!this.isLead) {
        this.isLead = true;
      }
      this.viewMode = 'lead';
    }
    // Por defecto, es una oportunidad
    else {
      this.viewMode = 'opportunity';
    }
  }

  /**
   * Actualiza la URL basándose en el estado actual del drawer
   */
  private updateUrlFromState(): void {
    // No actualizar la URL durante la inicialización para evitar cerrar el drawer
    if (this.isInitializing) {
      return;
    }

    // Si viene de activities, no actualizar la URL
    const openedFromActivities =
      (this.nzData as any).openedFromActivities === true;
    if (openedFromActivities) {
      return;
    }

    const opportunityId = this.opportunity?.id || this.opportunity?.originalId;
    if (!opportunityId) return;

    // Verificar la ruta actual para evitar navegaciones innecesarias
    const currentUrl = this.router.url;

    // NO actualizar la URL si estamos en una ruta PDF (estado-pago, orden-compra, saleorder/preview)
    // Esto evita que el drawer redirija cuando se navega a una vista PDF
    if (
      currentUrl.includes('/estado-pago/') ||
      currentUrl.includes('/orden-compra/') ||
      currentUrl.includes('/saleorder/')
    ) {
      return; // Salir temprano para evitar redirecciones
    }

    // NO actualizar la URL si estamos en proyectos (ej. usuario hizo clic en "Ver detalles" del proyecto)
    // Evita que el drawer redirija a /opportunities cuando se navega a /projects/detail/:id
    if (currentUrl.includes('/projects/')) {
      return;
    }

    // NO actualizar la URL si estamos en la ruta de budgets
    // Cuando se abre desde /opportunities/budgets, mantener esa ruta y solo agregar query params
    if (currentUrl.includes('/opportunities/budgets')) {
      // Si estamos en modo budget, solo agregar query params a la ruta actual
      if (this.viewMode === 'budget' && this.budget?.id) {
        const queryParams = this.route.snapshot.queryParams;
        const queryParamOpportunity = queryParams['opportunity'];
        const queryParamBudget =
          queryParams['budget'] || queryParams['budgetId'];

        const needsUpdate =
          queryParamOpportunity !== opportunityId.toString() ||
          queryParamBudget !== this.budget.id.toString();

        if (needsUpdate) {
          // Solo agregar query params a la ruta actual de budgets
          this.router
            .navigate([], {
              relativeTo: this.route,
              queryParams: {
                opportunity: opportunityId,
                budget: this.budget.id,
              },
              queryParamsHandling: 'merge',
              replaceUrl: true,
            })
            .catch((error) => {
              console.warn(
                'Error al actualizar query params de presupuesto:',
                error,
              );
            });
        }
      } else {
        // CLAVE: Si ya no estamos en modo budget pero la URL todavía lo tiene, lo limpiamos
        const queryParams = this.route.snapshot.queryParams;
        const queryParamBudget =
          queryParams['budget'] || queryParams['budgetId'];
        if (queryParamBudget) {
          this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { budget: null, budgetId: null },
            queryParamsHandling: 'merge',
            replaceUrl: true,
          });
        }
      }
      return;
    }

    const queryParams = this.route.snapshot.queryParams;
    const queryParamOpportunity = queryParams['opportunity'];
    const queryParamBudget = queryParams['budget'] || queryParams['budgetId'];

    // Para presupuestos, SIEMPRE usar query params para mantener el drawer abierto
    // Nunca cambiar la ruta completa cuando el drawer está abierto
    if (this.viewMode === 'budget' && this.budget?.id) {
      // Verificar si los query params ya están correctos
      const needsUpdate =
        queryParamOpportunity !== opportunityId.toString() ||
        queryParamBudget !== this.budget.id.toString();

      if (needsUpdate) {
        // Usar query params para mantener el drawer abierto
        // Similar a como se hace cuando se abre el drawer inicialmente
        this.router
          .navigate([], {
            relativeTo: this.route,
            queryParams: {
              opportunity: opportunityId,
              budget: this.budget.id,
            },
            queryParamsHandling: 'merge',
            replaceUrl: false,
          })
          .catch((error) => {
            console.warn(
              'Error al actualizar query params de presupuesto:',
              error,
            );
          });
      }
      return;
    }

    // Para leads y oportunidades, usar query params si estamos en la ruta base
    // o navegar a la ruta específica si no estamos en la ruta base
    let expectedRoutePath: string;
    if (this.viewMode === 'lead' || this.isLead) {
      expectedRoutePath = `/opportunities/lead/${opportunityId}`;
    } else {
      expectedRoutePath = `/opportunities/opportunity/${opportunityId}`;
    }

    const isOnExpectedRoute =
      (currentUrl === expectedRoutePath ||
        currentUrl.startsWith(expectedRoutePath + '/') ||
        currentUrl.startsWith(expectedRoutePath + '?')) &&
      !queryParamBudget;

    // Verificar si estamos en la ruta base con query params
    const isOnLeadsRouteWithQuery =
      this.isLead &&
      (currentUrl === '/opportunities/leads' ||
        currentUrl.startsWith('/opportunities/leads?')) &&
      (queryParamOpportunity === opportunityId.toString() ||
        queryParamOpportunity === opportunityId);

    const isOnOpportunitiesRouteWithQuery =
      !this.isLead &&
      (currentUrl === '/opportunities' ||
        currentUrl.startsWith('/opportunities?')) &&
      (queryParamOpportunity === opportunityId.toString() ||
        queryParamOpportunity === opportunityId);

    const isOnBaseRouteWithQuery =
      (isOnLeadsRouteWithQuery || isOnOpportunitiesRouteWithQuery) &&
      !queryParamBudget;

    if (isOnExpectedRoute || isOnBaseRouteWithQuery) {
      // Ya estamos en la ruta correcta, no hacer nada
      return;
    }

    // Si estamos en la ruta base, usar query params para mantener el drawer abierto
    if (
      currentUrl === '/opportunities' ||
      currentUrl === '/opportunities/leads' ||
      currentUrl.startsWith('/opportunities?') ||
      currentUrl.startsWith('/opportunities/leads?')
    ) {
      this.router.navigate([], {
        relativeTo: this.route,
        // Explicitly set budget and budgetId to null to remove them from the URL when queryParamsHandling is 'merge'
        queryParams: {
          opportunity: opportunityId,
          budget: null,
          budgetId: null,
        },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
      return;
    }

    // Solo navegar a la ruta específica si no estamos en ninguna ruta relacionada
    // Esto solo debería pasar cuando se accede directamente desde una URL
    this.router
      .navigate([expectedRoutePath], {
        replaceUrl: true,
      })
      .catch((error) => {
        // Si hay error de navegación, usar query params como fallback
        console.warn(
          'Error al navegar a ruta específica, usando query params:',
          error,
        );
        this.router.navigate([], {
          relativeTo: this.route,
          // Explicitly set budget and budgetId to null to remove them from the URL when queryParamsHandling is 'merge'
          queryParams: {
            opportunity: opportunityId,
            budget: null,
            budgetId: null,
          },
          queryParamsHandling: 'merge',
          replaceUrl: true,
        });
      });
  }

  /**
   * Carga un presupuesto específico por ID
   */
  private loadSpecificBudget(budgetId: number): void {
    console.log('loadSpecificBudget llamado con budgetId:', budgetId);
    console.log('budgetList actual:', this.budgetList?.length || 0);
    console.log(
      'opportunity:',
      this.opportunity?.id || this.opportunity?.originalId,
    );
    const companyId = this.getOpportunityCompanyId();
    if (companyId === null) {
      this._pendingBudgetId = budgetId;
      return;
    }

    if (!this.budgetList || this.budgetList.length === 0) {
      // Si no hay presupuestos cargados, cargarlos primero
      const opportunityId =
        this.opportunity?.id || this.opportunity?.originalId;
      if (!opportunityId) {
        console.error('No hay opportunityId para cargar budgets');
        return;
      }
      console.log('Cargando budgets para opportunityId:', opportunityId);
      const params: any = {
        startRow: 0,
        endRow: 100,
        sortDirection: 'desc',
        sortProperty: 'budgetDate',
      };

      // Agregar leadId u opportunityId según corresponda
      // Use leadId for both leads and opportunities as per OpportunityService.getSaleOrders implementation
      params.leadId = opportunityId;

      this.opportunityService
        .getSaleOrders(params, undefined, companyId)
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe({
          next: (response: any) => {
            console.log('Budgets cargados:', response?.data?.rows?.length || 0);
            this.budgetList = response?.data?.rows || [];
            const budget = this.budgetList.find((b: any) => b.id === budgetId);
            console.log('Budget encontrado:', budget ? 'Sí' : 'No');
            if (budget) {
              console.log('Abriendo vista de budget:', budget.id);
              this.openBudgetView(budget);
            } else {
              console.error(
                'No se encontró el budget con ID:',
                budgetId,
                'en la lista:',
                this.budgetList.map((b: any) => b.id),
              );
            }
          },
          error: (error) => {
            console.error('Error loading budgets:', error);
            this.budgetList = [];
            this.showBudgetRequestError(
              error,
              'No se pudo cargar el presupuesto. Inténtalo de nuevo.',
            );
            this.cdr.detectChanges();
          },
        });
    } else {
      const budget = this.budgetList.find((b: any) => b.id === budgetId);
      console.log(
        'Budget encontrado en lista existente:',
        budget ? 'Sí' : 'No',
      );
      if (budget) {
        console.log('Abriendo vista de budget:', budget.id);
        this.openBudgetView(budget);
      } else {
        console.error(
          'No se encontró el budget con ID:',
          budgetId,
          'en la lista:',
          this.budgetList.map((b: any) => b.id),
        );
      }
    }
  }

  // Handle field update from child components
  onFieldUpdate(data: {
    path: string;
    value: any;
    previousValue?: any;
    error?: boolean;
    errorMessage?: string;
  }): void {
    // Si estamos cancelando la edición, ignorar todos los eventos fieldUpdate
    // para evitar peticiones adicionales durante el proceso de cancelación
    if (this.editModeAction === 'cancel') {
      console.log('🚫 Ignorando fieldUpdate durante cancelación:', data.path);
      return;
    }

    // En modo edición, no guardar automáticamente - solo acumular cambios locales
    if (this.isEditModeEnabled) {
      // Si se está editando el nombre, actualizar también originalOpportunityName
      if (data.path === 'name' && data.value) {
        this.originalOpportunityName = data.value;
        console.log('📝 Nombre actualizado en padre:', data.value);
      }

      // Guardar backup para tags si viene en el evento (para poder revertir)
      if (data.path === 'tags' && data.previousValue !== undefined) {
        this.backupFieldValues[data.path] = data.previousValue;
      }

      // CRÍTICO: Actualizar this.opportunity inmediatamente en modo edición
      // para que buildCompleteOpportunityPayload use los valores correctos
      if (this.opportunity && data.path) {
        // Actualizar el campo directamente en this.opportunity
        if (data.path === 'teamId') {
          const previousTeamId = this.opportunity.teamId;
          const numVal = this.normalizeId(data.value);
          this.opportunity.teamId = numVal;
          console.log('📝 teamId actualizado en this.opportunity:', {
            from: previousTeamId,
            to: this.opportunity.teamId,
          });

          // CRÍTICO: Recargar las etapas cuando cambia el equipo
          // porque cada equipo tiene sus propias etapas
          this.updateStagesForTeam();

          // CRÍTICO: Si el stageId actual no pertenece al nuevo equipo,
          // asignar el primer stage del nuevo equipo para evitar errores del backend
          const stagesForNewTeam = this.getStagesForTeamId(
            this.opportunity.teamId,
          );
          const currentStageId = this.normalizeId(this.opportunity.stageId);

          if (currentStageId && stagesForNewTeam.length > 0) {
            const stageBelongsToNewTeam = stagesForNewTeam.some(
              (s: any) => this.normalizeId(s.id) === currentStageId,
            );
            if (!stageBelongsToNewTeam) {
              // El stageId no pertenece al nuevo equipo, asignar el primer stage
              const firstStage = stagesForNewTeam[0];
              this.opportunity.stageId = firstStage.id;
              this.opportunity.stage = firstStage;
              console.warn(
                `⚠️ StageId ${currentStageId} no pertenece al nuevo TeamId ${data.value}, asignando primer stage: ${firstStage.id}`,
              );
            }
          } else if (stagesForNewTeam.length > 0) {
            // Si no hay stageId pero hay stages disponibles, asignar el primero
            const firstStage = stagesForNewTeam[0];
            this.opportunity.stageId = firstStage.id;
            this.opportunity.stage = firstStage;
            console.warn(
              `⚠️ No había stageId, asignando primer stage del TeamId ${data.value}: ${firstStage.id}`,
            );
          }

          this.updateComputedProperties();
          console.log('🔄 Etapas recargadas para teamId:', data.value);
        } else if (data.path === 'userId') {
          this.opportunity.userId = data.value;
          // También actualizar comercialUser si viene en el evento
          if (data.value) {
            const user = this.globalValuesService.users$.value?.find(
              (u: any) => u.id === data.value,
            );
            this.opportunity.comercialUser = user?.partner || null;
          } else {
            this.opportunity.comercialUser = null;
          }
          console.log('📝 userId actualizado en this.opportunity:', data.value);
        } else if (data.path === 'stageId') {
          const stageIdValue = this.normalizeId(data.value);
          this.opportunity.stageId = stageIdValue;
          // También actualizar stage si viene en el evento
          if (stageIdValue) {
            const stages = this.getStagesForTeamId(this.opportunity.teamId);
            const stage = stages.find(
              (s: any) => this.normalizeId(s.id) === stageIdValue,
            );
            if (stage) {
              this.opportunity.stage = stage;
            }
          } else {
            this.opportunity.stage = null;
          }
          console.log(
            '📝 stageId actualizado en this.opportunity:',
            data.value,
          );
        } else {
          // Para otros campos, actualizar directamente
          this.opportunity[data.path] = data.value;
        }
      }

      // No llamar patchField - los cambios se guardarán solo al presionar "Guardar"
      return;
    }

    // Si no está en modo edición, guardar automáticamente (comportamiento anterior)
    // Guardar backup del valor anterior antes de intentar guardar
    if (data.previousValue !== undefined) {
      this.backupFieldValues[data.path] = data.previousValue;
    } else if (this.opportunity && this.opportunity[data.path] !== undefined) {
      // Si no viene previousValue, usar el valor actual como backup
      this.backupFieldValues[data.path] = this.opportunity[data.path];
    }

    // Guardar backup para tags si viene en el evento
    if (data.path === 'tags' && data.previousValue !== undefined) {
      this.backupFieldValues[data.path] = data.previousValue;
    }

    // Delegar al método patchField existente y suscribirse al resultado
    this.patchField(data.path, data.value).subscribe({
      next: (response) => {
        // Si hay respuesta, el campo se guardó correctamente
        if (response) {
          if (data.path === 'name' && this.opportunity) {
            const savedName = data.value || '';
            this.originalOpportunityName = savedName;
            this.opportunity = {
              ...this.opportunity,
              name: savedName,
              oportunidad: savedName,
              originalOpportunityName: savedName,
            };
            this.updateComputedProperties();
            this.updateEvent.emit(this.opportunity);
            this.cdr.detectChanges();
          }

          console.log('✅ Campo guardado correctamente:', data.path);
        }
      },
      error: (error) => {
        // El error ya se maneja completamente en patchField con catchError
        // Este callback no debería ejecutarse porque catchError retorna of(null)
        // Pero lo dejamos por si acaso hay algún error inesperado
        console.log('ℹ️ Error ya manejado en patchField para:', data.path);
      },
    });
  }

  // Handle field update from budget component
  onBudgetFieldUpdate(data: {
    path: string;
    value: any;
    previousValue?: any;
  }): void {
    if (data.path !== 'terms' && !this.isBudgetEditModeEnabled) {
      this.saveBudgetHeaderField(data.path, data.value, data.previousValue);
      return;
    }

    if (data.path === 'terms') {
      this.saveBudgetTerms(data.value, data.previousValue);
      return;
    }

    // En modo edición de presupuesto, no guardar automáticamente - solo acumular cambios locales
    if (this.isBudgetEditModeEnabled) {
      // Los cambios se guardarán solo al presionar "Guardar"
      return;
    }

    // Si no está en modo edición, guardar automáticamente (comportamiento anterior)
    // El autoguardado de cabecera ya se resuelve al inicio del método.
  }

  private saveBudgetTerms(value: string, previousValue?: string): void {
    if (!this.budget?.id) {
      return;
    }

    this.enqueueBudgetTermsSave({
      budgetId: this.budget.id,
      payload: this.buildBudgetTermsPayload(value),
      value,
      previousValue,
    });
  }

  /** El payload del PUT de cabecera con los términos nuevos, a partir de la cotización en pantalla. */
  private buildBudgetTermsPayload(value: string): any {
    const budgetId = this.budget.id;
    const payload = {
      id: budgetId,
      name: this.budget.name || '',
      driveUrl: this.budget.driveUrl || '',
      isActive:
        this.budget.isActive !== undefined ? this.budget.isActive : true,
      leadId:
        this.budget.leadId ||
        this.opportunity?.id ||
        this.opportunity?.originalId,
      teamId: this.budget.teamId || this.opportunity?.teamId,
      currencyId: this.budget.currencyId || 1,
      partnerId: this.budget.partnerId || null,
      expirationDate: this.budget.expirationDate
        ? typeof this.budget.expirationDate === 'string'
          ? this.budget.expirationDate
          : new Date(this.budget.expirationDate).toISOString()
        : null,
      budgetDate: this.budget.budgetDate
        ? typeof this.budget.budgetDate === 'string'
          ? this.budget.budgetDate
          : new Date(this.budget.budgetDate).toISOString()
        : null,
      budgetTemplateId: this.budget.budgetTemplateId || null,
      priceListId: this.budget.priceListId || null,
      paymentConditionsId: this.budget.paymentConditionsId || null,
      terms: value || null,
    };

    return payload;
  }

  private enqueueBudgetTermsSave(request: BudgetTermsSaveRequest): void {
    this.latestTermsByBudget.set(request.budgetId, request.value);

    if (this.termsSaveInFlight) {
      this.pendingTermsSaves.set(request.budgetId, request);
      return;
    }

    this.termsSaveInFlight = true;
    this.opportunityService
      .updateSaleOrder(request.budgetId, request.payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          try {
            this.applyTermsSaveResponse(response, request);
          } finally {
            this.onTermsSaveSettled();
          }
        },
        error: (error: any) => {
          try {
            this.applyTermsSaveError(error, request);
          } finally {
            this.onTermsSaveSettled();
          }
        },
      });
  }

  /** CEC-692. Una respuesta solo manda si es la del último guardado pedido para esa cotización. */
  private isLatestTermsSave(request: BudgetTermsSaveRequest): boolean {
    return this.latestTermsByBudget.get(request.budgetId) === request.value
      && !this.pendingTermsSaves.has(request.budgetId);
  }

  private onTermsSaveSettled(): void {
    this.termsSaveInFlight = false;
    const next = this.pendingTermsSaves.values().next();
    if (!next.done) {
      this.pendingTermsSaves.delete(next.value.budgetId);
      this.enqueueBudgetTermsSave(next.value);
    }
  }

  /** Escribe los términos en la cotización a la que pertenecen: la de pantalla solo si es esa. */
  private setBudgetTerms(budgetId: number, terms: string | undefined): void {
    if (this.budget?.id === budgetId) {
      this.budget.terms = terms;
    }

    const index = this.budgetList.findIndex((b: any) => b.id === budgetId);
    if (index !== -1) {
      this.budgetList[index].terms = terms;
    }
  }

  private applyTermsSaveResponse(response: any, request: BudgetTermsSaveRequest): void {
    if (!this.isLatestTermsSave(request)) {
      // Hay un guardado más reciente de esta cotización: esta respuesta ya no dice nada del texto actual.
      return;
    }

    if (!isSuccessfulSaleOrderUpdate(response)) {
      this.revertBudgetTerms(request);
      this.showErrorNotification(
        response?.message || 'Error al guardar los términos y condiciones.',
      );
      this.cdr.detectChanges();
      return;
    }

    this.setBudgetTerms(request.budgetId, response?.data?.terms ?? request.value);
    this.cdr.detectChanges();
  }

  private applyTermsSaveError(error: any, request: BudgetTermsSaveRequest): void {
    console.error('Error guardando términos de presupuesto:', error);
    if (!this.isLatestTermsSave(request)) {
      // El guardado siguiente lleva el texto completo: no se revierte encima de él ni se avisa aún.
      return;
    }

    this.revertBudgetTerms(request);
    this.showErrorNotification(
      error?.error?.message ||
        'Error al guardar los términos y condiciones.',
    );
    this.cdr.detectChanges();
  }

  /** Si el último guardado falla, los términos vuelven a lo que había antes de editar, como hasta ahora. */
  private revertBudgetTerms(request: BudgetTermsSaveRequest): void {
    if (request.previousValue === undefined) {
      return;
    }

    if (this.budget?.id === request.budgetId) {
      this.budget = {
        ...this.budget,
        terms: request.previousValue,
      };
    }

    const index = this.budgetList.findIndex((b: any) => b.id === request.budgetId);
    if (index !== -1) {
      this.budgetList[index].terms = request.previousValue;
    }
  }

  private saveBudgetHeaderField(
    fieldPath: string,
    value: any,
    previousValue?: any,
  ): void {
    if (!this.budget?.id) {
      return;
    }

    const budgetId = this.budget.id;
    const payload = this.buildBudgetHeaderPayload();

    this.opportunityService
      .updateSaleOrder(budgetId, payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          if (!isSuccessfulSaleOrderUpdate(response)) {
            if (previousValue !== undefined) {
              this.applyBudgetFieldValue(fieldPath, previousValue);
            }
            this.showErrorNotification(
              response?.message || 'Error al actualizar el presupuesto.',
            );
            this.updateComputedProperties();
            this.cdr.detectChanges();
            return;
          }

          const savedBudget = response?.data || {};
          this.budget = {
            ...this.budget,
            ...savedBudget,
          };

          const savedValue =
            savedBudget[fieldPath] ??
            this.getSavedBudgetFieldAlias(savedBudget, fieldPath) ??
            (fieldPath === 'name' ? response?.name : undefined) ??
            value;
          this.applyBudgetFieldValue(fieldPath, savedValue);

          const index = this.budgetList.findIndex(
            (b: any) => b.id === budgetId,
          );
          if (index !== -1) {
            this.budgetList[index] = {
              ...this.budgetList[index],
              ...this.budget,
            };
          }

          this.updateComputedProperties();
          this.cdr.detectChanges();
        },
        error: (error: any) => {
          console.error('Error guardando campo de presupuesto:', error);
          if (previousValue !== undefined) {
            this.applyBudgetFieldValue(fieldPath, previousValue);
          }

          this.showErrorNotification(
            error?.error?.message || 'Error al actualizar el presupuesto.',
          );
          this.updateComputedProperties();
          this.cdr.detectChanges();
        },
      });
  }

  private buildBudgetHeaderPayload(): any {
    return buildSaleOrderHeaderPayload(this.budget, this.opportunity);
  }

  private getSavedBudgetFieldAlias(savedBudget: any, fieldPath: string): any {
    const aliases: Record<string, string> = {
      budgetTemplate: 'budgetTemplateId',
      paymentConditions: 'paymentConditionsId',
    };

    const alias = aliases[fieldPath];
    return alias ? savedBudget[alias] : undefined;
  }

  private applyBudgetFieldValue(fieldPath: string, value: any): void {
    if (!this.budget) {
      return;
    }

    this.budget = {
      ...this.budget,
      [fieldPath]: value,
    };

    if (fieldPath === 'budgetTemplate') {
      this.budget.budgetTemplateId = value;
    }

    if (fieldPath === 'paymentConditions') {
      this.budget.paymentConditionsId = value;
    }
  }

  // Start edit mode for budget
  startEditModeBudget(): void {
    if (this.isBudgetEditModeEnabled || this.budget?.id) return;

    this.isBudgetEditModeEnabled = true;
    this.cdr.detectChanges();
  }

  // Save edit mode for budget
  async saveEditModeBudget(): Promise<void> {
    if (!this.budget) {
      console.warn('No hay presupuesto para guardar');
      return;
    }

    if (!this.isBudgetCurrencyConversionValid) {
      this.showErrorNotification('No se puede guardar hasta completar la conversión monetaria.');
      return;
    }

    if (this.budget.id) {
      console.warn(
        'saveEditModeBudget was called for an existing budget; existing budgets use inline autosave.',
      );
      return;
    }

    // Notificar al componente hijo que se va a guardar
    this.budgetEditModeAction = 'save';
    this.cdr.detectChanges();

    // Si el presupuesto no tiene ID, significa que es nuevo y hay que crearlo
    if (!this.budget.id) {
      this.savingBudget = true;

      // Guardar líneas locales para guardarlas después de la creación exitosa del cabecero
      const quotationLinesToSave = [...(this.budget.quotationLines || [])];
      const budgetLinesToSave = [...(this.budget.budgetLines || [])];

      const payload = {
        name: this.budget.name || this.getDefaultBudgetName(),
        isActive:
          this.budget.isActive !== undefined ? this.budget.isActive : false,
        leadId:
          this.budget.leadId ||
          this.opportunity?.id ||
          this.opportunity?.originalId,
        teamId: this.budget.teamId || this.opportunity?.teamId,
        // CEC-565: los tres caminos de alta resuelven la moneda igual, y el ultimo recurso es
        // la moneda de referencia del catalogo en vez de un 1 fijo.
        currencyId: resolveNewQuotationCurrencyId(
          [this.budget.currencyId, this.opportunity?.company?.currencyId],
          this.globalValuesService.currencies$.value,
        ),
        partnerId: this.budget.partnerId || this.opportunity?.partnerId || null,
        expirationDate: this.budget.expirationDate
          ? typeof this.budget.expirationDate === 'string'
            ? this.budget.expirationDate
            : new Date(this.budget.expirationDate).toISOString()
          : new Date(
              new Date().setMonth(new Date().getMonth() + 1),
            ).toISOString(),
        budgetDate: this.budget.budgetDate
          ? typeof this.budget.budgetDate === 'string'
            ? this.budget.budgetDate
            : new Date(this.budget.budgetDate).toISOString()
          : new Date().toISOString(),
        driveUrl: this.budget.driveUrl || '',
        terms: this.budget.terms || null,
        budgetTemplateId: this.budget.budgetTemplateId || null,
        priceListId: this.budget.priceListId || null,
        paymentConditionsId: this.budget.paymentConditionsId || null,
        // quotationLines: [] // No enviamos lineas aqui
      };

      // Crear presupuesto (cabecero) usando el servicio
      this.opportunityService
        .createSaleOrder(payload)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          finalize(() => {
            this.savingBudget = false;
          }),
        )
        .subscribe({
          next: (response: any) => {
            if (response?.data) {
              const createdBudget = response.data;

              // Actualizar budgetList
              const existingIndex = this.budgetList.findIndex(
                (b) => b.id === createdBudget.id,
              );
              if (existingIndex === -1) {
                this.budgetList.unshift(createdBudget);
                this.currentBudgetIndex = 0;
              } else {
                this.budgetList[existingIndex] = createdBudget;
                this.currentBudgetIndex = existingIndex;
              }

              // Actualizar el presupuesto actual
              this.budget = createdBudget;

              // Crear todas las lineas. Las budget lines de venta se crean antes que
              // los costes para poder reemplazar asociaciones locales por IDs reales.
              const initialLineRequests: Observable<any>[] = [];
              const createdBudgetLineIdByLocalId = new Map<number, number>();

              // 1. Quotation Lines
              quotationLinesToSave.forEach((line) => {
                const supplierId =
                  this.normalizeId(line.supplier) ??
                  this.normalizeId(line.supplierId);

                const qLinePayload = {
                  saleOrderId: createdBudget.id,
                  name: line.name || '',
                  productId: line.product || null,
                  supplierId,
                  quantity: line.quantity || 0,
                  unitOfMeasureId: line.unit || null,
                  priceUnit: line.priceUnit || 0,
                  amount: line.amount || 0,
                  discount: line.discount || 0,
                  currencyId: line.currency || createdBudget.currencyId || 1,
                  taxIds: line.taxes || [],
                  sourceId: line.sourceId || null,
                  destinationId: line.destinationId || null,
                  isCost: false,
                  isUnitPrice: true,
                  isActive: true,
                  sequence: line.sequence || 0,
                };
                initialLineRequests.push(
                  this.opportunityService.createSaleOrderLine(qLinePayload),
                );
              });

              const buildBudgetLinePayload = (line: any) => {
                const supplierId =
                  this.normalizeId(line.supplier) ??
                  this.normalizeId(line.supplierId);
                const localAssociatedSaleId = this.normalizeId(
                  line.associatedSale,
                );
                const associatedSaleLineId =
                  line.isCost && localAssociatedSaleId
                    ? createdBudgetLineIdByLocalId.get(localAssociatedSaleId) ??
                      null
                    : null;

                return {
                  saleOrderId: createdBudget.id,
                  name: line.name || '',
                  quantity: line.quantity || 0,
                  discount: line.discount || 0,
                  isCost: line.isCost || false,
                  price: line.price || 0,
                  amount: line.amount || 0,
                  isUnitPrice:
                    line.isUnitPrice !== undefined ? line.isUnitPrice : true,
                  sequence: line.sequence || 0,
                  isActive: line.isActive !== undefined ? line.isActive : true,
                  productId: line.product || null,
                  supplierId,
                  currencyId: line.currency || createdBudget.currencyId || 1,
                  unitOfMeasureId: line.unit || null,
                  associatedSaleLineId,
                  taxIds: line.taxes || [],
                  sourceId: line.sourceId || null,
                  destinationId: line.destinationId || null,
                };
              };

              // 2. Budget Lines de venta: se crean primero para obtener IDs reales.
              budgetLinesToSave
                .filter((line) => !line.isCost)
                .forEach((line) => {
                  const localLineId =
                    this.normalizeId(line.id) ?? this.normalizeId(line.localId);
                  initialLineRequests.push(
                    this.opportunityService
                      .createBudgetLine(buildBudgetLinePayload(line))
                      .pipe(
                        map((response: any) => {
                          const savedLine = response?.data || response;
                          const savedLineId = this.normalizeId(
                            Array.isArray(savedLine)
                              ? savedLine[0]?.id
                              : savedLine?.id,
                          );

                          if (localLineId && savedLineId) {
                            createdBudgetLineIdByLocalId.set(
                              localLineId,
                              savedLineId,
                            );
                          }

                          return response;
                        }),
                      ),
                  );
                });

              const createCostLineRequests = (): Observable<any>[] =>
                budgetLinesToSave
                  .filter((line) => line.isCost)
                  .map((line) =>
                    this.opportunityService.createBudgetLine(
                      buildBudgetLinePayload(line),
                    ),
                  );

              const finishLineCreation = () => {
                console.log('Todas las lineas creadas con exito');
                this.finishBudgetSave();
              };

              const createCostLines = () => {
                const costLineRequests = createCostLineRequests();
                if (costLineRequests.length > 0) {
                  forkJoin(costLineRequests)
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe({
                      next: finishLineCreation,
                      error: (err) => {
                        console.error('Error al crear lineas:', err);
                        this.finishBudgetSave();
                      },
                    });
                } else {
                  finishLineCreation();
                }
              };

              // Ejecutar creacion de lineas si existen
              if (initialLineRequests.length > 0) {
                forkJoin(initialLineRequests)
                  .pipe(takeUntilDestroyed(this.destroyRef))
                  .subscribe({
                    next: createCostLines,
                    error: (err) => {
                      console.error('Error al crear lineas:', err);
                      this.finishBudgetSave();
                    },
                  });
              } else {
                createCostLines();
              }

              // Mostrar notificación
              this.showSuccessNotification('Presupuesto creado correctamente.');

              // Actualizar la URL y el estado
              if (!this.isInitializing) {
                this.updateUrlFromState();
              }
              this.cdr.detectChanges();
            }
          },
          error: (error) => {
            console.error('Error creating budget:', error);
            this.budgetEditModeAction = 'none';
            this.showErrorNotification(
              error?.error?.message || 'Error al crear el presupuesto.',
            );
          },
      });
      return;
    }
  }

  // Cancel edit mode for budget (without confirmation)
  cancelEditModeBudget(): void {
    // Si el presupuesto no tiene ID (es temporal), eliminarlo de la lista y volver a oportunidad
    if (this.budget && !this.budget.id) {
      const tempIndex = this.budgetList.findIndex((b) => b.id === null);
      if (tempIndex !== -1) {
        this.budgetList.splice(tempIndex, 1);
      }

      // Volver a la vista de oportunidad
      this.viewMode = this.isLead ? 'lead' : 'opportunity';
      this.budget = null;
      this.currentBudgetIndex = 0;
      this.isBudgetEditModeEnabled = false;
      this.budgetEditModeAction = 'cancel';

      // Actualizar propiedades computadas
      this.updateComputedProperties();

      // Limpiar parámetros de budget de la URL
      if (!this.isInitializing) {
        const opportunityId =
          this.opportunity?.id || this.opportunity?.originalId;
        if (opportunityId) {
          this.router
            .navigate([], {
              relativeTo: this.route,
              queryParams: {
                opportunity: opportunityId,
              },
              queryParamsHandling: 'merge',
              replaceUrl: true,
            })
            .catch((error) => {
              console.warn('Error updating URL:', error);
            });
        }
      }

      this.cdr.detectChanges();
      return;
    }

    this.budgetEditModeAction = 'none';
    this.isBudgetEditModeEnabled = false;

    this.cdr.detectChanges();
  }

  /**
   * Finaliza el estado de guardado del presupuesto y actualiza las propiedades
   */
  private finishBudgetSave(): void {
    // Desactivar modo edición
    this.isBudgetEditModeEnabled = false;
    this.budgetEditModeAction = 'none';
    this.continueAfterUnsavedBudgetSave();

    // Actualizar propiedades computadas
    this.updateComputedProperties();

    // Forzar detección de cambios
    this.cdr.detectChanges();
  }

  /**
   * Muestra una notificación de éxito estándar
   */
  private showSuccessNotification(content: string): void {
    this.toastConfig = {
      icon: 'check_circle',
      iconColor: '#6CDA75',
      content: content,
      showUndo: false,
    };
    this.notRef = this.notificationService.create(
      'blank',
      '',
      this.notificationTmpl,
      { nzPlacement: 'bottomRight' },
    );
  }

  /**
   * Muestra una notificación de error estándar
   */
  private showErrorNotification(content: string): void {
    this.toastConfig = {
      icon: 'dangerous',
      iconColor: '#CB2A2F',
      content: content,
      showUndo: false,
    };
    this.notRef = this.notificationService.create(
      'blank',
      '',
      this.notificationTmpl,
      { nzPlacement: 'bottomRight' },
    );
  }

  // Budget actions
  confirmCancelBudget(): void {
    this.cancelBudgetConfirmationModal.config = {
      title: '¿Cancelar presupuesto?',
      message:
        '¿Estás seguro de que deseas cancelar este presupuesto? Esta acción no se puede deshacer.',
      confirmText: 'Sí, cancelar',
      cancelText: 'No',
      confirmButtonType: 'primary',
      confirmButtonDanger: true,
    };
    this.cancelBudgetConfirmationModal.open();
  }

  onCancelBudgetConfirmed(data?: {
    textValue?: string;
    dateValue?: Date;
  }): void {
    this.cancelBudget();
  }

  private cancelBudget(): void {
    if (!this.budget?.id) return;
    const leadId = this.opportunity?.id || this.opportunity?.originalId;
    if (!leadId) return;

    this.cancelingBudget = true;
    this.opportunityService
      .cancelSaleOrder(this.budget.id, leadId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.cancelingBudget = false;
        }),
      )
      .subscribe({
        next: (response: any) => {
          if (response?.data) {
            // Actualizar el presupuesto con los datos de la respuesta
            this.budget = { ...this.budget, ...response.data };
            // Actualizar en la lista también
            const index = this.budgetList.findIndex(
              (b: any) => b.id === this.budget.id,
            );
            if (index !== -1) {
              this.budgetList[index] = { ...this.budget };
            }
            // Actualizar propiedades computadas
            this.updateComputedProperties();
            this.cdr.detectChanges();

            // Mostrar notificación de éxito
            this.toastConfig = {
              icon: 'check_circle',
              iconColor: '#6CDA75',
              content: 'Presupuesto cancelado correctamente.',
              showUndo: false,
            };
            this.notRef = this.notificationService.create(
              'blank',
              '',
              this.notificationTmpl,
              { nzPlacement: 'bottomRight' },
            );
          }
        },
        error: (error: any) => {
          console.error('Error cancelando presupuesto:', error);
          this.toastConfig = {
            icon: 'dangerous',
            iconColor: '#CB2A2F',
            content: error.error?.message || 'Error al cancelar el presupuesto.',
            showUndo: false,
          };
          this.notRef = this.notificationService.create(
            'blank',
            '',
            this.notificationTmpl,
            { nzPlacement: 'bottomRight' },
          );
        },
      });
  }

  recoverBudget(): void {
    if (!this.budget?.id) return;
    const leadId = this.opportunity?.id || this.opportunity?.originalId;
    if (!leadId) return;

    this.recoveringBudget = true;
    this.opportunityService
      .recoverSaleOrder(this.budget.id, leadId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.recoveringBudget = false;
        }),
      )
      .subscribe({
        next: (response: any) => {
          if (response?.data) {
            // Actualizar el presupuesto con los datos de la respuesta
            this.budget = { ...this.budget, ...response.data };
            // Actualizar en la lista también
            const index = this.budgetList.findIndex(
              (b: any) => b.id === this.budget.id,
            );
            if (index !== -1) {
              this.budgetList[index] = { ...this.budget };
            }
            // Actualizar propiedades computadas
            this.updateComputedProperties();
            this.cdr.detectChanges();

            // Mostrar notificación de éxito
            this.toastConfig = {
              icon: 'check_circle',
              iconColor: '#6CDA75',
              content: 'Presupuesto recuperado correctamente.',
              showUndo: false,
            };
            this.notRef = this.notificationService.create(
              'blank',
              '',
              this.notificationTmpl,
              { nzPlacement: 'bottomRight' },
            );
          }
        },
        error: (error: any) => {
          console.error('Error recuperando presupuesto:', error);
          this.toastConfig = {
            icon: 'dangerous',
            iconColor: '#CB2A2F',
            content:
              error.error?.message || 'Error al recuperar el presupuesto.',
            showUndo: false,
          };
          this.notRef = this.notificationService.create(
            'blank',
            '',
            this.notificationTmpl,
            { nzPlacement: 'bottomRight' },
          );
        },
      });
  }

  requestApproval(): void {
    if (!this.budget?.id) return;
    const leadId = this.opportunity?.id || this.opportunity?.originalId;
    if (!leadId) return;

    this.requestingApproval = true;
    this.opportunityService
      .askForApproveSaleOrder(this.budget.id, leadId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.requestingApproval = false;
        }),
      )
      .subscribe({
        next: (response: any) => {
          if (response?.data) {
            // Actualizar el presupuesto con los datos de la respuesta
            this.budget = { ...this.budget, ...response.data };
            // Actualizar en la lista también
            const index = this.budgetList.findIndex(
              (b: any) => b.id === this.budget.id,
            );
            if (index !== -1) {
              this.budgetList[index] = { ...this.budget };
            }
            // Actualizar propiedades computadas
            this.updateComputedProperties();
            this.cdr.detectChanges();

            // Recargar actividades para mostrar la nueva actividad generada por el backend
            this.loadActivities();

            // Mostrar notificación de éxito
            this.toastConfig = {
              icon: 'check_circle',
              iconColor: '#6CDA75',
              content: 'Aprobación solicitada correctamente.',
              showUndo: false,
            };
            this.notRef = this.notificationService.create(
              'blank',
              '',
              this.notificationTmpl,
              { nzPlacement: 'bottomRight' },
            );
          }
        },
        error: (error: any) => {
          console.error('Error solicitando aprobación:', error);
          this.toastConfig = {
            icon: 'dangerous',
            iconColor: '#CB2A2F',
            content:
              error.error?.message ||
              'Error al solicitar la aprobación del presupuesto.',
            showUndo: false,
          };
          this.notRef = this.notificationService.create(
            'blank',
            '',
            this.notificationTmpl,
            { nzPlacement: 'bottomRight' },
          );
        },
      });
  }

  sendBudget() {
    combineLatest({
      isMobile: this.screenService.isMobile$,
      isMedium: this.screenService.isMedium$,
    })
      .pipe(take(1))
      .subscribe(({ isMobile, isMedium }) => {
        const sendBudgetDrawer = this.drawerService.create({
          nzWidth: 793,
          nzHeight: '98%',
          nzPlacement: isMobile ? 'bottom' : 'right',
          nzClosable: false,
          nzMaskClosable: false,
          nzMask: true,
          nzContent: SendBudgetDrawerComponent,
          nzData: {
            budgetId: this.budget?.id || this.budget?.budgetId || '',
            budget: this.budget,
          },
        });

        sendBudgetDrawer.afterOpen.subscribe(() => {
          const drawerComponent =
            sendBudgetDrawer.getContentComponent() as SendBudgetDrawerComponent;
        });

        sendBudgetDrawer.afterClose.subscribe((result: any) => {
          if (result === 'sent') {
            this.sendBudgetEmailSentMessage();
          }
        });
      });
  }

  confirmBudget(): void {
    if (!this.budget?.id) return;

    // CEC-720: an opportunity may have several quotations approved as Pedido de venta. The backend
    // still refuses approving the same quotation twice or a cancelled one.
    const leadId = this.opportunity?.id || this.opportunity?.originalId;
    if (!leadId) return;

    this.confirmingBudget = true;
    this.opportunityService
      .confirmSaleOrder(this.budget.id, leadId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.confirmingBudget = false;
        }),
      )
      .subscribe({
        next: (response: any) => {
          if (response?.data) {
            // Actualizar el presupuesto con los datos de la respuesta
            this.budget = { ...this.budget, ...response.data };
            // Actualizar en la lista también
            const index = this.budgetList.findIndex(
              (b: any) => b.id === this.budget.id,
            );
            if (index !== -1) {
              this.budgetList[index] = { ...this.budget };
            }
            this.sortBudgets(); // Re-ordenar después de adoptar/confirmar
            // Actualizar propiedades computadas
            this.updateComputedProperties();
            this.cdr.detectChanges();

            // Mostrar notificación de éxito
            this.toastConfig = {
              icon: 'check_circle',
              iconColor: '#6CDA75',
              content: 'Presupuesto aprobado como pedido de venta.',
              showUndo: false,
            };
            this.notRef = this.notificationService.create(
              'blank',
              '',
              this.notificationTmpl,
              { nzPlacement: 'bottomRight' },
            );

            // On an awarded opportunity the backend has just sent it to SourTrack: show the new marker.
            if (this.isWon) this.refreshSourTrackMarker(leadId);
          }
        },
        error: (error: any) => {
          console.error('Error aprobando presupuesto:', error);
          this.toastConfig = {
            icon: 'dangerous',
            iconColor: '#CB2A2F',
            content: error.error?.message || 'Error al aprobar el presupuesto.',
            showUndo: false,
          };
          this.notRef = this.notificationService.create(
            'blank',
            '',
            this.notificationTmpl,
            { nzPlacement: 'bottomRight' },
          );
        },
      });
  }

  adoptBudget(): void {
    this.executeAdoptBudget();
  }

  private executeAdoptBudget(updateLinkedSaleOrderLines?: boolean): void {
    if (!this.budget?.id) return;
    const leadId = this.opportunity?.id || this.opportunity?.originalId;
    if (!leadId) return;

    this.adoptingBudget = true;
    this.opportunityService
      .adoptSaleOrder(this.budget.id, leadId, updateLinkedSaleOrderLines)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.adoptingBudget = false;
        }),
      )
      .subscribe({
        next: (response: any) => {
          this.applyAdoptBudgetResponse(response);
        },
        error: (error: any) => {
          if (this.isAdoptBudgetConfirmationRequired(error)) {
            this.openAdoptBudgetConfirmation(error);
            return;
          }

          console.error('Error adoptando presupuesto:', error);
          this.toastConfig = {
            icon: 'dangerous',
            iconColor: '#CB2A2F',
            content: error.error?.message || 'Error al adoptar el presupuesto.',
            showUndo: false,
          };
          this.notRef = this.notificationService.create(
            'blank',
            '',
            this.notificationTmpl,
            { nzPlacement: 'bottomRight' },
          );
        },
      });
  }

  private applyAdoptBudgetResponse(response: any): void {
    const saleOrder = response?.data?.saleOrder || response?.data;
    if (!saleOrder) return;

    this.budget = { ...this.budget, ...saleOrder };
    const index = this.budgetList.findIndex(
      (b: any) => b.id === this.budget.id,
    );
    if (index !== -1) {
      this.budgetList[index] = { ...this.budget };
    }
    this.sortBudgets();
    this.updateComputedProperties();
    this.cdr.detectChanges();

    this.toastConfig = {
      icon: 'check_circle',
      iconColor: '#6CDA75',
      content: 'Presupuesto adoptado correctamente.',
      showUndo: false,
    };
    this.notRef = this.notificationService.create(
      'blank',
      '',
      this.notificationTmpl,
      { nzPlacement: 'bottomRight' },
    );
  }

  private isAdoptBudgetConfirmationRequired(error: any): boolean {
    return error?.status === 409 && error?.error?.data?.requiresConfirmation === true;
  }

  private openAdoptBudgetConfirmation(error: any): void {
    const modifiedCount = error?.error?.data?.modifiedLinkedLinesCount || 0;
    this.modalService.confirm({
      nzTitle: 'Hay lineas del pedido con cambios',
      nzContent:
        `Se detectaron ${modifiedCount} linea(s) vinculada(s) que cambiaron respecto del presupuesto. ` +
        'Queres actualizar esas lineas existentes o mantenerlas como estan y adoptar solo las lineas directas?',
      nzOkText: 'Actualizar lineas',
      nzCancelText: 'Mantener actuales',
      nzOnOk: () => this.executeAdoptBudget(true),
      nzOnCancel: () => this.executeAdoptBudget(false),
    });
  }

  openSendEmailDrawer(): void {
    combineLatest({
      isMobile: this.screenService.isMobile$,
      isMedium: this.screenService.isMedium$,
    })
      .pipe(take(1))
      .subscribe(({ isMobile, isMedium }) => {
        const sendBudgetDrawer = this.drawerService.create({
          nzWidth: 793,
          nzHeight: '98%',
          nzPlacement: isMobile ? 'bottom' : 'right',
          nzClosable: false,
          nzMaskClosable: false,
          nzMask: true,
          nzContent: SendBudgetDrawerComponent,
          nzData: {
            budgetId: null,
            budget: null,
          } as any,
        });

        sendBudgetDrawer.afterOpen.subscribe(() => {
          // const drawerComponent =
          //   sendBudgetDrawer.getContentComponent() as SendBudgetDrawerComponent;
        });

        sendBudgetDrawer.afterClose.subscribe((result: any) => {
          if (result === 'sent') {
            // Recargar actividades si se envió un correo
            this.loadActivities();
          }
        });
      });
  }

  // Get system participants for the followers avatar group.
  // Only the commercial is shown here by default. The contact already has its
  // own field in the opportunity detail and should not be visually mixed with
  // real followers, otherwise it looks like an automatic MailFollower.
  getOpportunityUsers(): any[] {
    const users: any[] = [];

    // Agregar el comercial si existe (objeto o por ID)
    let commercialUser = null;
    if (this.opportunity?.comercialUser) {
      commercialUser = {
        id: this.opportunity.comercialUser.id,
        name: this.opportunity.comercialUser.name,
        surName: this.opportunity.comercialUser.surName,
        avatar: this.opportunity.comercialUser.avatar,
        isSystemUser: true, // Marcar como no eliminable
        role: 'Comercial', // Rol para mostrar en badge
      };
    } else if (this.opportunity?.userId) {
      const userList = this.globalValuesService.users$.value || [];
      const user = userList.find((u: any) => u.id === this.opportunity.userId);
      if (user?.partner) {
        commercialUser = {
          id: user.partner.id,
          name: user.partner.name,
          surName: user.partner.surName,
          avatar: user.partner.avatar,
          isSystemUser: true, // Marcar como no eliminable
          role: 'Comercial', // Rol para mostrar en badge
        };
      }
    }

    if (commercialUser) {
      users.push(commercialUser);
    }

    // Aquí se pueden agregar más usuarios si la oportunidad tiene un array de participantes
    if (
      this.opportunity?.participants &&
      Array.isArray(this.opportunity.participants)
    ) {
      this.opportunity.participants.forEach((participant: any) => {
        if (!users.find((u) => u.id === participant.id)) {
          users.push(participant);
        }
      });
    }

    return users;
  }

  // Abrir modal para marcar como perdido
  markAsLost(): void {
    // Resetear el formulario
    this.lostForm.reset();

    this.markAsLostModalRef = this.modalService.create({
      nzTitle: undefined,
      nzContent: this.markAsLostModalTmpl,
      nzFooter: null,
      nzClosable: false,
      nzWidth: 518,
      nzCentered: true,
    });
  }

  // Confirmar y ejecutar la acción de marcar como perdido
  confirmMarkAsLost(): void {
    if (this.lostForm.invalid) {
      return;
    }

    const opportunityId = this.opportunity?.id || this.opportunity?.originalId;
    if (!opportunityId) {
      console.warn('No se encontró el ID de la oportunidad/lead');
      return;
    }

    const formValue = this.lostForm.value;
    this.markingAsLost = true;

    // Cerrar el modal
    if (this.markAsLostModalRef) {
      this.markAsLostModalRef.close();
      this.markAsLostModalRef = null;
    }

    this.opportunityService
      .markAsLost(opportunityId, formValue.reason, formValue.lostNote)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.markingAsLost = false;
        }),
      )
      .subscribe({
        next: (response: PostResponse) => {
          // Mostrar notificación de éxito
          this.toastConfig.icon = 'check_circle';
          this.toastConfig.iconColor = '#6CDA75';
          this.toastConfig.content = this.isLead
            ? 'El lead ha sido marcado como perdido.'
            : 'La oportunidad ha sido marcada como perdida.';
          this.toastConfig.showUndo = false;
          this.notRef = this.notificationService.create(
            '',
            '',
            this.notificationTmpl,
            {
              nzPlacement: 'bottomRight',
              nzDuration: 3000,
            },
          );

          // Actualizar la oportunidad con la respuesta del backend si está disponible
          if (response?.data) {
            const updatedOpportunity =
              this.opportunitiesUtilsService.mapOpportunityData(response.data);
            if (updatedOpportunity) {
              this.opportunity = {
                ...this.opportunity,
                ...updatedOpportunity,
                documents: this.opportunity?.documents || [],
                chat: this.opportunity?.chat,
              };
              // Actualizar isLost después de actualizar la oportunidad
              this.updateIsLost();
              // Actualizar isWon después de actualizar la oportunidad
              this.updateIsWon();
              // Actualizar canCreateQuote después de actualizar la oportunidad
              this.updateCanCreateQuote();
              // Emitir evento para actualizar el componente padre
              this.updateEvent.emit(this.opportunity);
              this.cdr.detectChanges();
            }
          }
        },
        error: (error) => {
          console.error('Error al marcar como perdido:', error);
          this.toastConfig.icon = 'error';
          this.toastConfig.iconColor = '#FF4D4F';
          this.toastConfig.content = this.isLead
            ? 'Error al marcar el lead como perdido.'
            : 'Error al marcar la oportunidad como perdida.';
          this.toastConfig.showUndo = false;
          this.notRef = this.notificationService.create(
            '',
            '',
            this.notificationTmpl,
            {
              nzPlacement: 'bottomRight',
              nzDuration: 3000,
            },
          );
        },
      });
  }

  // Cancelar el modal de marcar como perdido
  cancelMarkAsLost(): void {
    if (this.markAsLostModalRef) {
      this.markAsLostModalRef.close();
      this.markAsLostModalRef = null;
    }
  }

  // Abrir modal para añadir seguidor
  openAddFollowerModal(): void {
    // Cargar contactos y usuarios
    this.loadContactsAndUsers();

    // Resetear selección y búsqueda
    this.selectedFollowersIds.clear();
    this.followerSearchText = '';
    this.followerSelectedType = 'user';

    this.addFollowerModalRef = this.modalService.create({
      nzTitle: undefined,
      nzContent: this.addFollowerModalTmpl,
      nzFooter: null,
      nzClosable: false,
      nzWidth: 600,
      nzCentered: true,
    });
  }

  // Cerrar modal de añadir seguidor
  closeAddFollowerModal(): void {
    if (this.addFollowerModalRef) {
      this.addFollowerModalRef.close();
      this.addFollowerModalRef = null;
    }
    // Limpiar selección
    this.selectedFollowersIds.clear();
    this.followerSearchText = '';
  }

  // Abrir modal para asociar proyecto existente
  openLinkProjectModal(): void {
    this.linkProjectSelectedId = null;
    this.linkingProjectId = null;
    this.loadingLinkProjects = true;
    this.availableProjectsForLink = [];

    const opportunityId = this.opportunity?.id ?? this.opportunity?.originalId;
    const linkedProjectIds = new Set<number>();
    (this.opportunity?.projects || []).forEach((p: any) => {
      const id = p?.id ?? p?.projectId ?? p?.project?.id;
      if (id != null) linkedProjectIds.add(Number(id));
    });

    this.projectService
      .getProjectByCompanies({
        startRow: 0,
        endRow: 10000,
        sortDirection: 'desc',
        sortProperty: 'id',
      })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => (this.loadingLinkProjects = false)),
      )
      .subscribe({
        next: (rsp) => {
          const allProjects = rsp.data?.rows || [];
          this.availableProjectsForLink = allProjects.filter(
            (p: any) =>
              isProjectAvailableForOpportunity(p) && !linkedProjectIds.has(p.id),
          );
        },
        error: (e) => {
          console.error('Error cargando proyectos:', e);
          this.showErrorNotification('No se pudieron cargar los proyectos.');
        },
      });

    this.linkProjectModalRef = this.modalService.create({
      nzTitle: undefined,
      nzContent: this.linkProjectModalTmpl,
      nzFooter: null,
      nzClosable: false,
      nzWidth: 600,
      nzCentered: true,
    });
  }

  closeLinkProjectModal(): void {
    if (this.linkProjectModalRef) {
      this.linkProjectModalRef.close();
      this.linkProjectModalRef = null;
    }
    this.linkProjectSelectedId = null;
    this.linkingProjectId = null;
  }

  confirmLinkProject(): void {
    const projectId = this.linkProjectSelectedId;
    const opportunityId = this.opportunity?.id ?? this.opportunity?.originalId;
    if (!projectId || !opportunityId) return;

    this.linkingProjectId = projectId;
    this.opportunityService
      .linkProject(opportunityId, projectId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => (this.linkingProjectId = null)),
      )
      .subscribe({
        next: () => {
          this.closeLinkProjectModal();
          this.reloadOpportunity();
          this.showNotification('El proyecto se ha asociado correctamente.');
        },
        error: (err) => {
          console.error('Error asociando proyecto:', err);
          this.showErrorNotification(
            err?.error?.message ||
              err?.message ||
              'No se pudo asociar el proyecto.',
          );
        },
      });
  }

  // Cargar contactos y usuarios
  loadContactsAndUsers(): void {
    // Cargar contactos
    this.globalValuesService.isPartnerReady$
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((value: boolean) => {
        if (value) {
          this.allContacts = this.globalValuesService.origenContactList || [];
          this.filterContacts();
        }
      });

    // Cargar usuarios
    this.globalValuesService.users$
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((users: any[]) => {
        this.allUsers = users || [];
        this.filterUsers();
      });
  }

  // Cambiar tipo de seguidor (contacto/usuario)
  onFollowerTypeChange(value: 'contact' | 'user'): void {
    this.followerSelectedType = value;
    this.followerSearchText = ''; // Resetear búsqueda al cambiar de tipo
    if (value === 'contact') {
      this.filterContacts();
    } else {
      this.filterUsers();
    }
  }

  // Filtrar contactos
  filterContacts(): void {
    // Crear un Set con los IDs de los seguidores actuales para excluirlos
    const existingFollowerIds = new Set(
      this.opportunityUsers.map((follower) => follower.id),
    );

    // Crear un Set con los IDs de los partners de los usuarios para excluirlos de los contactos
    const partnerIds = new Set(
      this.allUsers
        .map((user) => user.partner?.id)
        .filter((id) => id !== undefined && id !== null),
    );

    // Filtrar contactos que no sean seguidores ni partners de usuarios
    let availableContacts = this.allContacts.filter(
      (contact) =>
        !existingFollowerIds.has(contact.id) && !partnerIds.has(contact.id),
    );

    // Aplicar filtro de búsqueda si existe
    if (!this.followerSearchText || this.followerSearchText.trim() === '') {
      this.filteredContacts = availableContacts;
    } else {
      const searchLower = this.followerSearchText.toLowerCase().trim();
      this.filteredContacts = availableContacts.filter((contact) => {
        const name = (contact.name || '').toLowerCase();
        const surName = (contact.surName || '').toLowerCase();
        const email = (contact.email || '').toLowerCase();
        return (
          name.includes(searchLower) ||
          surName.includes(searchLower) ||
          email.includes(searchLower) ||
          `${name} ${surName}`.trim().includes(searchLower)
        );
      });
    }
  }

  // Filtrar usuarios
  filterUsers(): void {
    // Crear un Set con los IDs de los seguidores actuales para excluirlos
    const existingFollowerIds = new Set(
      this.opportunityUsers.map((follower) => follower.id),
    );

    // Filtrar usuarios que no sean seguidores
    // Comparar con partner.id o id del usuario
    let availableUsers = this.allUsers.filter((user) => {
      const userId = user.partner?.id || user.id;
      return !existingFollowerIds.has(userId);
    });

    // Aplicar filtro de búsqueda si existe
    if (!this.followerSearchText || this.followerSearchText.trim() === '') {
      this.filteredUsers = availableUsers;
    } else {
      const searchLower = this.followerSearchText.toLowerCase().trim();
      this.filteredUsers = availableUsers.filter((user) => {
        const name = (user.partner?.name || user.name || '').toLowerCase();
        const surName = (
          user.partner?.surName ||
          user.surName ||
          ''
        ).toLowerCase();
        const email = (user.partner?.email || user.email || '').toLowerCase();
        return (
          name.includes(searchLower) ||
          surName.includes(searchLower) ||
          email.includes(searchLower) ||
          `${name} ${surName}`.trim().includes(searchLower)
        );
      });
    }
  }

  // Manejar cambio en el buscador
  onFollowerSearchChange(searchText: string): void {
    this.followerSearchText = searchText;
    if (this.followerSelectedType === 'contact') {
      this.filterContacts();
    } else {
      this.filterUsers();
    }
  }

  // Alternar selección de seguidor
  toggleFollowerSelection(id: number, checked: boolean): void {
    if (checked) {
      this.selectedFollowersIds.add(id);
    } else {
      this.selectedFollowersIds.delete(id);
    }
  }

  // Obtener iniciales desde un contacto
  getUserInitialsFromContact(contact: any): string {
    if (!contact) return 'C';

    const firstName = contact.name || '';
    const lastName = contact.surName || '';

    // Si solo hay firstName, usar las primeras 2 letras del nombre
    if (firstName && !lastName) {
      return firstName.substring(0, 2).toUpperCase();
    }

    // Combinar iniciales
    const initials = (firstName.charAt(0) + lastName.charAt(0)).toUpperCase();
    if (initials && initials.length >= 2) return initials;

    // Si solo hay una letra, usar la primera del nombre
    if (firstName) return firstName.charAt(0).toUpperCase();

    return 'C';
  }

  // Confirmar añadir seguidores
  confirmAddFollowers(): void {
    if (this.selectedFollowersIds.size === 0) {
      return;
    }

    const opportunityId = this.opportunity?.id || this.opportunity?.originalId;
    if (!opportunityId) {
      console.warn('No se encontró el ID de la oportunidad');
      return;
    }

    const entityType = PARENT_ENTITY_TYPE.OPPORTUNITY; // 49 para Lead/Opportunity

    // Separar IDs en userIds y partnerIds
    const userIds: number[] = [];
    const partnerIds: number[] = [];

    this.selectedFollowersIds.forEach((id) => {
      if (this.followerSelectedType === 'user') {
        // Usuarios: siempre usamos user.id (userId). Nunca partnerId.
        userIds.push(id);
      } else {
        // Contactos: siempre son partnerIds (contact.id es un partnerId)
        partnerIds.push(id);
      }
    });

    this.addingFollowers = true;

    // Si solo hay un seguidor, usar el endpoint de un solo seguidor
    const totalFollowers = userIds.length + partnerIds.length;

    if (totalFollowers === 1) {
      // Usar endpoint de un solo seguidor
      const singlePayload: any = {
        entityType: entityType,
        entityId: opportunityId,
      };

      if (userIds.length === 1) {
        singlePayload.userId = userIds[0];
      } else if (partnerIds.length === 1) {
        singlePayload.partnerId = partnerIds[0];
      }

      this.communicationService
        .addMailFollower(singlePayload)
        .pipe(
          finalize(() => {
            this.addingFollowers = false;
          }),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe({
          next: () => {
            // Después de añadir, hacer un GET para obtener todos los seguidores actualizados
            this.loadMailFollowers();

            // Mostrar notificación de éxito
            this.toastConfig.icon = 'check_circle';
            this.toastConfig.iconColor = '#6CDA75';
            this.toastConfig.content = 'Se añadió 1 seguidor correctamente.';
            this.toastConfig.showUndo = false;
            this.notRef = this.notificationService.create(
              '',
              '',
              this.notificationTmpl,
              {
                nzPlacement: 'bottomRight',
                nzDuration: 3000,
              },
            );

            // Cerrar modal
            this.closeAddFollowerModal();
          },
          error: (error) => {
            console.error('Error al añadir seguidor:', error);
            this.toastConfig.icon = 'error';
            this.toastConfig.iconColor = '#ff4d4f';
            this.toastConfig.content =
              'Error al añadir seguidor. Por favor, intenta nuevamente.';
            this.toastConfig.showUndo = false;
            this.notRef = this.notificationService.create(
              '',
              '',
              this.notificationTmpl,
              {
                nzPlacement: 'bottomRight',
                nzDuration: 3000,
              },
            );
          },
        });
      return;
    }

    // Si hay múltiples seguidores, usar add-many
    const payload: any = {
      entityType: entityType,
      entityId: opportunityId,
    };

    if (userIds.length > 0) {
      payload.userIds = userIds;
    }
    if (partnerIds.length > 0) {
      payload.partnerIds = partnerIds;
    }

    this.communicationService
      .addManyMailFollowers(payload)
      .pipe(
        finalize(() => {
          this.addingFollowers = false;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          // Después de añadir, hacer un GET para obtener todos los seguidores actualizados
          this.loadMailFollowers();

          // Mostrar notificación de éxito
          this.toastConfig.icon = 'check_circle';
          this.toastConfig.iconColor = '#6CDA75';
          this.toastConfig.content = `Se añadieron ${
            this.selectedFollowersIds.size
          } ${
            this.selectedFollowersIds.size === 1 ? 'seguidor' : 'seguidores'
          } correctamente.`;
          this.toastConfig.showUndo = false;
          this.notRef = this.notificationService.create(
            '',
            '',
            this.notificationTmpl,
            {
              nzPlacement: 'bottomRight',
              nzDuration: 3000,
            },
          );

          // Cerrar modal
          this.closeAddFollowerModal();
        },
        error: (error) => {
          console.error('Error al añadir seguidores:', error);
          this.toastConfig.icon = 'error';
          this.toastConfig.iconColor = '#ff4d4f';
          this.toastConfig.content =
            'Error al añadir seguidores. Por favor, intenta nuevamente.';
          this.toastConfig.showUndo = false;
          this.notRef = this.notificationService.create(
            '',
            '',
            this.notificationTmpl,
            {
              nzPlacement: 'bottomRight',
              nzDuration: 3000,
            },
          );
        },
      });
  }

  // Abrir modal de confirmación para eliminar seguidor
  openRemoveFollowerConfirmation(userId: number, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }

    if (this.isEditModeEnabled) {
      return;
    }

    this.pendingRemoveFollowerId = userId;
    const user = this.opportunityUsers.find((u) => u.id === userId);
    const userName = user
      ? `${user.name} ${user.surName || ''}`.trim()
      : 'este seguidor';

    this.removeFollowerConfirmationModal.config = {
      title: 'Dejar de seguir',
      message: `¿Estás seguro que deseas que ${userName} deje de seguir esta oportunidad?`,
      confirmText: 'Dejar de seguir',
      cancelText: 'Cancelar',
      confirmButtonType: 'primary',
      confirmButtonDanger: true,
    };
    this.removeFollowerConfirmationModal.open();
  }

  // Confirmar eliminación de seguidor
  onRemoveFollowerConfirmed(): void {
    if (this.pendingRemoveFollowerId === null) {
      return;
    }

    const followerId = this.pendingRemoveFollowerId;
    const opportunityId = this.opportunity?.id || this.opportunity?.originalId;
    if (!opportunityId) {
      console.warn('No se encontró el ID de la oportunidad');
      this.pendingRemoveFollowerId = null;
      return;
    }

    // Encontrar el seguidor para determinar si es userId o partnerId
    const follower = this.opportunityUsers.find((u) => u.id === followerId);
    if (!follower) {
      console.warn(
        'No se encontró el seguidor a eliminar. ID buscado:',
        followerId,
      );
      console.warn(
        'Seguidores disponibles:',
        this.opportunityUsers.map((u) => ({
          id: u.id,
          name: u.name,
          isMailFollower: u.isMailFollower,
        })),
      );
      this.pendingRemoveFollowerId = null;
      return;
    }

    const entityType = PARENT_ENTITY_TYPE.OPPORTUNITY; // 49 para Lead/Opportunity

    // Verificar que el seguidor sea un mailFollower (no comercial/contacto de la oportunidad)
    if (follower.isSystemUser) {
      console.warn(
        'No se puede eliminar el comercial o contacto de la oportunidad desde seguidores',
      );
      this.toastConfig.icon = 'error';
      this.toastConfig.iconColor = '#ff4d4f';
      this.toastConfig.content =
        'El comercial y contacto de la oportunidad no se pueden eliminar desde seguidores.';
      this.toastConfig.showUndo = false;
      this.notRef = this.notificationService.create(
        '',
        '',
        this.notificationTmpl,
        {
          nzPlacement: 'bottomRight',
          nzDuration: 3000,
        },
      );
      this.pendingRemoveFollowerId = null;
      return;
    }

    // Verificar que el seguidor sea un mailFollower
    if (!follower.isMailFollower) {
      console.warn(
        'No se puede eliminar: el seguidor no es un mailFollower válido',
      );
      this.pendingRemoveFollowerId = null;
      return;
    }

    // Determinar si es userId o partnerId basado en el mailFollower
    // IMPORTANTE: Usar los IDs del mailFollower (follower.userId o follower.partnerId)
    // NO usar los IDs de los objetos anidados
    const payload: any = {
      entityType: entityType,
      entityId: opportunityId,
    };

    // Si el mailFollower tiene userId, usar userId (aunque tenga partner)
    // Si solo tiene partnerId, usar partnerId
    if (follower.userId) {
      payload.userId = follower.userId;
      // NO incluir partnerId si tiene userId
    } else if (follower.partnerId) {
      payload.partnerId = follower.partnerId;
      // NO incluir userId si tiene partnerId
    } else {
      console.error(
        'El seguidor no tiene userId ni partnerId válidos:',
        follower,
      );
      this.pendingRemoveFollowerId = null;
      return;
    }

    this.communicationService
      .deleteMailFollower(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          // Después de eliminar, GET para refrescar lista (y modal "Ver todos" si está abierto)
          this.loadMailFollowers();

          // Mostrar notificación de éxito
          this.toastConfig.icon = 'check_circle';
          this.toastConfig.iconColor = '#6CDA75';
          this.toastConfig.content = 'Seguidor eliminado correctamente.';
          this.toastConfig.showUndo = false;
          this.notRef = this.notificationService.create(
            '',
            '',
            this.notificationTmpl,
            {
              nzPlacement: 'bottomRight',
              nzDuration: 3000,
            },
          );

          this.pendingRemoveFollowerId = null;
        },
        error: (error) => {
          console.error('Error al eliminar seguidor:', error);
          this.toastConfig.icon = 'error';
          this.toastConfig.iconColor = '#ff4d4f';
          this.toastConfig.content =
            'Error al eliminar seguidor. Por favor, intenta nuevamente.';
          this.toastConfig.showUndo = false;
          this.notRef = this.notificationService.create(
            '',
            '',
            this.notificationTmpl,
            {
              nzPlacement: 'bottomRight',
              nzDuration: 3000,
            },
          );
          this.pendingRemoveFollowerId = null;
        },
      });
  }

  // Obtener tooltip con los nombres de los seguidores restantes
  getRemainingFollowersTooltip(): string {
    if (this.opportunityUsers.length <= 2) {
      return '';
    }
    const remainingUsers = this.opportunityUsers.slice(2);
    return remainingUsers
      .map((user) => `${user.name} ${user.surName || ''}`.trim())
      .join(', ');
  }

  // Abrir modal para ver todos los seguidores
  openViewAllFollowersModal(): void {
    if (this.isEditModeEnabled) {
      return;
    }

    this.viewAllFollowersSearchText = '';
    this.filteredViewAllFollowers = [...this.opportunityUsers];

    this.viewAllFollowersModalRef = this.modalService.create({
      nzTitle: undefined,
      nzContent: this.viewAllFollowersModalTmpl,
      nzFooter: null,
      nzClosable: false,
      nzWidth: 600,
      nzCentered: true,
    });
  }

  // Cerrar modal de ver todos los seguidores
  closeViewAllFollowersModal(): void {
    if (this.viewAllFollowersModalRef) {
      this.viewAllFollowersModalRef.close();
      this.viewAllFollowersModalRef = null;
    }
    this.viewAllFollowersSearchText = '';
  }

  // Manejar cambio en el buscador del modal de ver todos
  onViewAllFollowersSearchChange(searchText: string): void {
    this.viewAllFollowersSearchText = searchText;
    if (!searchText || searchText.trim() === '') {
      this.filteredViewAllFollowers = [...this.opportunityUsers];
    } else {
      const searchLower = searchText.toLowerCase().trim();
      this.filteredViewAllFollowers = this.opportunityUsers.filter((user) => {
        const name = (user.name || '').toLowerCase();
        const surName = (user.surName || '').toLowerCase();
        const email = (user.email || '').toLowerCase();
        return (
          name.includes(searchLower) ||
          surName.includes(searchLower) ||
          email.includes(searchLower) ||
          `${name} ${surName}`.trim().includes(searchLower)
        );
      });
    }
  }

  // Mapear la respuesta del backend de mailFollowers a opportunityUsers
  mapMailFollowersToUsers(mailFollowers: any[]): any[] {
    return mailFollowers
      .map((follower: any) => {
        // Usar IDs del mailFollower (follower.userId / follower.partnerId). Fallback a anidados si faltan.
        const uid = follower.userId ?? follower.user?.id;
        const hasUser = !!follower.user;
        const hasPartner = !!follower.partner;

        if (hasUser) {
          // Es un usuario (con o sin partner). Siempre usar userId para eliminar.
          const partner = follower.user?.partner;
          const displayId = partner?.id ?? follower.user.id;
          return {
            id: displayId,
            mailFollowerId: follower.id,
            userId: uid, // Siempre userId para eliminar (fallback: follower.userId ?? follower.user.id)
            name: partner?.name ?? follower.user?.email ?? 'Usuario',
            surName: partner?.surName ?? '',
            avatar: partner?.avatar,
            email: follower.user?.email,
            isUser: true,
            isMailFollower: true,
          };
        }
        if (
          hasPartner &&
          (follower.partnerId != null || follower.partner?.id != null)
        ) {
          // Es un partner/contacto. Usar partnerId para eliminar.
          const pid = follower.partnerId ?? follower.partner?.id;
          return {
            id: follower.partner.id,
            mailFollowerId: follower.id,
            partnerId: pid,
            name: follower.partner.name,
            surName: follower.partner.surName,
            avatar: follower.partner.avatar,
            email: follower.partner.email,
            isUser: false,
            isMailFollower: true,
          };
        }
        return null;
      })
      .filter((user: any) => user !== null);
  }

  // Cargar seguidores desde el backend
  private isLoadingMailFollowers = false; // Bandera para evitar bucles infinitos

  loadMailFollowers(): void {
    const opportunityId = this.opportunity?.id || this.opportunity?.originalId;
    if (!opportunityId || this.isLoadingMailFollowers) {
      return;
    }

    this.isLoadingMailFollowers = true;
    const entityType = PARENT_ENTITY_TYPE.OPPORTUNITY; // 49 para Lead/Opportunity

    this.communicationService
      .getMailFollowers(entityType, opportunityId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isLoadingMailFollowers = false;
        }),
      )
      .subscribe({
        next: (response) => {
          // Mapear la respuesta del backend a opportunityUsers
          const mailFollowers = this.mapMailFollowersToUsers(
            response.data?.rows || [],
          );

          // Obtener participantes del sistema (marcados como no eliminables).
          // El contacto no se incluye aquí; sólo debe aparecer si existe como MailFollower real.
          const systemUsers = this.getOpportunityUsers();

          // Combinar: primero sistema (comercial), luego mailFollowers reales.
          // Filtrar duplicados: si un mailFollower tiene el mismo id que un systemUser, no incluirlo dos veces
          const systemUserIds = new Set(systemUsers.map((u) => u.id));
          const uniqueMailFollowers = mailFollowers.filter(
            (mf) => !systemUserIds.has(mf.id),
          );

          this.opportunityUsers = [...systemUsers, ...uniqueMailFollowers];

          // Si el modal "Ver todos" está abierto, refrescar la lista filtrada
          if (this.viewAllFollowersModalRef) {
            this.onViewAllFollowersSearchChange(
              this.viewAllFollowersSearchText,
            );
          }

          Promise.resolve().then(() => {
            this.cdr.markForCheck();
            this.cdr.detectChanges();
          });
        },
        error: (error) => {
          console.error('Error al cargar seguidores:', error);
          // En caso de error, usar el método anterior
          this.opportunityUsers = this.getOpportunityUsers();
          // NO llamar updateComputedProperties() aquí para evitar bucle infinito
        },
      });
  }

  // Restaurar lead/oportunidad
  recoverLead(): void {
    const opportunityId = this.opportunity?.id || this.opportunity?.originalId;
    if (!opportunityId) {
      console.warn('No se encontró el ID de la oportunidad/lead');
      return;
    }

    this.recoveringLead = true;
    this.opportunityService
      .recoverLead(opportunityId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.recoveringLead = false;
        }),
      )
      .subscribe({
        next: (response: PostResponse) => {
          // Mostrar notificación de éxito
          this.toastConfig.icon = 'check_circle';
          this.toastConfig.iconColor = '#6CDA75';
          this.toastConfig.content = this.isLead
            ? 'El lead ha sido restaurado.'
            : 'La oportunidad ha sido restaurada.';
          this.toastConfig.showUndo = false;
          this.notRef = this.notificationService.create(
            '',
            '',
            this.notificationTmpl,
            {
              nzPlacement: 'bottomRight',
              nzDuration: 3000,
            },
          );

          // Actualizar la oportunidad con la respuesta del backend si está disponible
          if (response?.data) {
            const updatedOpportunity =
              this.opportunitiesUtilsService.mapOpportunityData(response.data);
            if (updatedOpportunity) {
              this.opportunity = {
                ...this.opportunity,
                ...updatedOpportunity,
                documents: this.opportunity?.documents || [],
                chat: this.opportunity?.chat,
              };
              // Actualizar isLost después de actualizar la oportunidad
              this.updateIsLost();
              // Actualizar isWon después de actualizar la oportunidad
              this.updateIsWon();
              // Actualizar canCreateQuote después de actualizar la oportunidad
              this.updateCanCreateQuote();
              // Emitir evento para actualizar el componente padre
              this.updateEvent.emit(this.opportunity);
              this.cdr.detectChanges();
            }
          }
        },
        error: (error) => {
          console.error('Error al restaurar:', error);
          this.toastConfig.icon = 'error';
          this.toastConfig.iconColor = '#FF4D4F';
          this.toastConfig.content = this.isLead
            ? 'Error al restaurar el lead.'
            : 'Error al restaurar la oportunidad.';
          this.toastConfig.showUndo = false;
          this.notRef = this.notificationService.create(
            '',
            '',
            this.notificationTmpl,
            {
              nzPlacement: 'bottomRight',
              nzDuration: 3000,
            },
          );
        },
      });
  }

  // --- SourTrack sync (pending state, manual retry, "choose a quotation") ---
  syncingSourTrack = false;

  get sourTrackStatus(): string | null {
    return this.opportunity?.sourTrackSyncStatus ?? null;
  }

  get sourTrackDetail(): string | null {
    return this.opportunity?.sourTrackSyncStatusDescription ?? null;
  }

  get sourTrackCanRetry(): boolean {
    return canRetrySourTrackSync(this.sourTrackStatus, this.isWon);
  }

  retrySourTrackSync(): void {
    const leadId = this.opportunity?.id || this.opportunity?.originalId;
    if (!leadId || this.syncingSourTrack) return;

    this.syncingSourTrack = true;
    this.sourTrackSyncFlow
      .sync(leadId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.syncingSourTrack = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe((outcome) => {
        this.applySourTrackOutcome(outcome);
        // A Warning with no quotation in Pedido de venta means the user has to approve one. The local
        // list may be stale (e.g. an approval made elsewhere), so decide on a fresh one.
        if (outcome.severity === 'warning') {
          this.refreshQuotationsForSourTrack()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((quotations) => {
              if (!hasSendableQuotation(quotations)) {
                this.openSourTrackQuotationPicker();
              }
            });
        }
      });
  }

  /** Re-reads the opportunity's quotations; falls back to the current list if it cannot. */
  private refreshQuotationsForSourTrack(): Observable<any[]> {
    const opportunityId = this.opportunity?.id || this.opportunity?.originalId;
    const companyId = this.getOpportunityCompanyId();
    if (!opportunityId || companyId === null) return of(this.budgetList);

    return this.opportunityService
      .getSaleOrders(
        {
          startRow: 0,
          endRow: 100,
          sortDirection: 'desc',
          sortProperty: 'budgetDate',
          leadId: opportunityId,
        },
        undefined,
        companyId,
      )
      .pipe(
        map((response: any) => {
          this.budgetList = response?.data?.rows || [];
          this.sortBudgets();
          this.cdr.detectChanges();
          return this.budgetList;
        }),
        catchError(() => of(this.budgetList)),
      );
  }

  approveAndSyncSourTrack(quotationId: number): void {
    const leadId = this.opportunity?.id || this.opportunity?.originalId;
    if (!leadId || this.syncingSourTrack) return;

    this.syncingSourTrack = true;
    const quotation = (this.budgetList || []).find((q: any) => q?.id === quotationId);
    this.sourTrackSyncFlow
      .approveAndSync(quotationId, leadId, quotation?.budgetAdopted === true)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.syncingSourTrack = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe((outcome) => {
        if (outcome.approved) this.loadBudgets();
        this.applySourTrackOutcome(outcome);
      });
  }

  private openSourTrackQuotationPicker(): void {
    openSourTrackQuotationPicker(
      this.modalService,
      listCandidateQuotations(this.budgetList),
      (quotationId) => this.approveAndSyncSourTrack(quotationId),
    );
  }

  /** Re-reads only the sync marker, so unsaved edits on the ficha survive. */
  private refreshSourTrackMarker(leadId: number): void {
    this.sourTrackSyncFlow
      .readMarker(leadId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((lead) => {
        if (lead && lead.sourTrackSyncStatus !== undefined) {
          this.opportunity = {
            ...this.opportunity,
            sourTrackSyncStatus: lead.sourTrackSyncStatus,
            sourTrackSyncStatusDescription: lead.sourTrackSyncStatusDescription,
            isSourTrackIntegrated: lead.isSourTrackIntegrated,
          };
          this.cdr.detectChanges();
        }
      });
  }

  /** Refreshes only the sync marker from the response, so unsaved edits on the ficha survive. */
  private applySourTrackOutcome(outcome: SourTrackFlowOutcome): void {
    const lead = outcome.lead;
    if (lead && lead.sourTrackSyncStatus !== undefined) {
      this.opportunity = {
        ...this.opportunity,
        sourTrackSyncStatus: lead.sourTrackSyncStatus,
        sourTrackSyncStatusDescription: lead.sourTrackSyncStatusDescription,
        isSourTrackIntegrated: lead.isSourTrackIntegrated,
      };
    }
    this.showSourTrackToast(outcome.severity, outcome.message);
  }

  private showSourTrackToast(severity: SourTrackSeverity, message: string): void {
    const style = {
      success: { icon: 'check_circle', iconColor: '#6CDA75' },
      warning: { icon: 'warning', iconColor: '#FAAD14' },
      error: { icon: 'dangerous', iconColor: '#CB2A2F' },
    }[severity];
    this.toastConfig = { ...style, content: message, showUndo: false };
    this.notRef = this.notificationService.create(
      'blank',
      '',
      this.notificationTmpl,
      { nzPlacement: 'bottomRight', nzDuration: 6000 },
    );
  }

  // Mark opportunity as won
  markAsWon(): void {
    const opportunityId = this.opportunity?.id || this.opportunity?.originalId;
    if (!opportunityId) {
      console.warn('No se encontró el ID de la oportunidad');
      return;
    }

    this.loading = true;
    this.opportunityService
      .markOpportunityAsWon(opportunityId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (response: PostResponse) => {
          // Mostrar notificación de éxito
          this.toastConfig.icon = 'check_circle';
          this.toastConfig.iconColor = '#6CDA75';
          this.toastConfig.content =
            'La oportunidad ha sido marcada como ganada.';
          this.toastConfig.showUndo = false;
          this.notRef = this.notificationService.create(
            '',
            '',
            this.notificationTmpl,
            {
              nzPlacement: 'bottomRight',
              nzDuration: 3000,
            },
          );

          // Actualizar la oportunidad con la respuesta del backend si está disponible
          if (response?.data) {
            const updatedOpportunity =
              this.opportunitiesUtilsService.mapOpportunityData(response.data);
            if (updatedOpportunity) {
              // Actualizar específicamente el stageId desde la respuesta
              // La respuesta tiene data.stageId y data.stage con la información de la etapa
              const newStageId =
                response.data.stageId || response.data.stage?.id;

              this.opportunity = {
                ...this.opportunity,
                ...updatedOpportunity,
                // Asegurar que el stageId se actualice desde la respuesta
                stageId:
                  newStageId !== undefined
                    ? newStageId
                    : this.opportunity.stageId,
                documents: this.opportunity?.documents || [],
                chat: this.opportunity?.chat,
              };

              // Actualizar isWon después de marcar como ganada
              this.updateIsWon();
              // Actualizar canCreateQuote después de actualizar la etapa
              this.updateCanCreateQuote();
              //actualizar etapas
              this.updateCurrentStageName();
              // Emitir evento para actualizar el componente padre
              this.updateEvent.emit(this.opportunity);
              this.cdr.detectChanges();
            }
          }
        },
        error: (error) => {
          console.error('Error al marcar la oportunidad como ganada:', error);
          this.toastConfig.icon = 'error';
          this.toastConfig.iconColor = '#FF4D4F';
          this.toastConfig.content =
            'Error al marcar la oportunidad como ganada.';
          this.toastConfig.showUndo = false;
          this.notRef = this.notificationService.create(
            '',
            '',
            this.notificationTmpl,
            {
              nzPlacement: 'bottomRight',
              nzDuration: 3000,
            },
          );
          this.loading = false;
          this.cdr.detectChanges();
        },
      });
  }

  // Abrir drawer para registrar nota
  openRegisterNoteDrawer(): void {
    console.log('Registrar Notas');
    // TODO: Implementar drawer para registrar nota
    // Por ahora, mostrar notificación
    /*  this.notificationService.info(
      'Registrar nota',
      'La funcionalidad para registrar notas estará disponible próximamente.'

    ); */
  }

  // Cargar actividades de la oportunidad, lead o budget
  loadActivities(): void {
    let entityId: number;
    let entityType: 'Lead' | 'Opportunity' | 'SaleOrder';
    let activityObservable: Observable<any>;

    // Si estamos en modo budget, cargar actividades del presupuesto (SaleOrder)
    if (this.viewMode === 'budget' && this.budget?.id) {
      entityId = this.budget.id;
      entityType = 'SaleOrder';

      this.loadingActivities = true;
      activityObservable = this.activityService.getActivitiesByParentEntity(
        entityType,
        entityId,
        0,
        100,
        'desc',
        'dateCreated',
      );
    } else {
      // Modo normal: cargar actividades de la oportunidad o lead
      const opportunityId =
        this.opportunity?.id || this.opportunity?.originalId;
      if (!opportunityId) return;

      entityId = opportunityId;
      entityType = this.isLead ? 'Lead' : 'Opportunity';

      this.loadingActivities = true;
      activityObservable = this.activityService.getActivitiesByParentEntity(
        entityType,
        entityId,
        0,
        100,
        'desc',
        'dateCreated',
      );
    }

    // Timeout 60s: en dev el backend a veces no responde a GET /api/activity?parentEntityType=SaleOrder&parentEntityId=...
    const ACTIVITIES_REQUEST_TIMEOUT_MS = 60_000;

    activityObservable
      .pipe(
        timeout(ACTIVITIES_REQUEST_TIMEOUT_MS),
        takeUntilDestroyed(this.destroyRef),
        catchError((err) => {
          console.error('Error al cargar actividades:', err);
          if (
            err?.name === 'TimeoutError' ||
            err?.message?.includes('Timeout')
          ) {
            this.notificationService.create(
              'error',
              'Tiempo de espera agotado',
              'La carga de actividades tardó demasiado. Compruebe la conexión o intente de nuevo.',
            );
          } else {
            this.notificationService.create(
              'error',
              'Error al cargar actividades',
              err?.error?.message ||
                err?.message ||
                'No se pudieron cargar las actividades.',
            );
          }
          return of({ data: { rows: [] } });
        }),
        finalize(() => {
          this.loadingActivities = false;
          // Verificar que la vista no esté destruida antes de llamar detectChanges
          try {
            this.cdr.detectChanges();
          } catch (error) {
            // Ignorar errores si la vista ya está destruida
            if (
              error instanceof Error &&
              !error.message.includes('destroyed')
            ) {
              console.error('Error en detectChanges:', error);
            }
          }
        }),
      )
      .subscribe({
        next: (response) => {
          let activitiesData: Activity[] = [];

          if (Array.isArray(response)) {
            activitiesData = response;
          } else if (response.data?.rows) {
            activitiesData = response.data.rows;
          } else if (response.data) {
            activitiesData = Array.isArray(response.data) ? response.data : [];
          }

          // Actualizar el objeto user de cada actividad con los datos correctos de la lista de usuarios
          this.enrichActivitiesWithUserData(activitiesData);

          this.activities = activitiesData;

          this.groupActivitiesByDate();
          this.activitiesLoaded = true;

          // Forzar detección de cambios para asegurar que el template se actualice
          // Verificar que la vista no esté destruida antes de llamar detectChanges
          try {
            this.cdr.detectChanges();
          } catch (error) {
            // Ignorar errores si la vista ya está destruida
            if (
              error instanceof Error &&
              !error.message.includes('destroyed')
            ) {
              console.error('Error en detectChanges:', error);
            }
          }
        },
        error: (e) => {
          console.error('Error al cargar actividades:', e);
          this.activities = [];
          this.activitiesGroupedByDate = [];
        },
      });
  }

  // (removido) onSaleOrderLineUpdated ya no es necesario: recargamos al salir de modo budget

  // Enriquecer actividades con datos actualizados del usuario
  // IMPORTANTE: Siempre actualizamos desde la lista de usuarios porque el backend puede tener datos desactualizados
  private enrichActivitiesWithUserData(activities: Activity[]): void {
    const users = this.globalValuesService.users$.value;

    activities.forEach((activity) => {
      if (!activity.userId) return;

      const originalPartnerName = activity.user?.partner?.name;
      const originalUserId = activity.userId;
      const originalPartnerId = activity.user?.partnerId;

      // Buscar el usuario por userId (id del usuario)
      let user = users.find((u) => u.id === activity.userId);

      // Si no se encuentra, buscar por partnerId del activity-type.user si existe
      if (!user && activity.user?.partnerId) {
        const partnerId = activity.user.partnerId;
        user = users.find((u) => u.partner?.id === partnerId);
      }

      // Si aún no se encuentra, intentar buscar usando userId como si fuera partnerId
      // (porque en el drawer de actividad se guarda partner.id como userId)
      if (!user) {
        user = users.find((u) => u.partner?.id === activity.userId);
      }

      // Si se encontró el usuario, SIEMPRE actualizar el objeto user de la actividad
      // con los datos actualizados de la lista de usuarios
      if (user && user.partner) {
        const newPartnerName = user.partner.name;
        const newPartnerSurName = user.partner.surName || '';
        const fullNewName = `${newPartnerName} ${newPartnerSurName}`.trim();

        /* console.log(
          `Actualizando actividad userId: ${originalUserId}, partnerId backend: ${originalPartnerId}, nombre backend: "${originalPartnerName}", nombre actualizado: "${newPartnerName}"`
        );
 */
        // Forzar la actualización del objeto user completo creando un nuevo objeto
        // Esto asegura que Angular detecte el cambio
        activity.user = {
          id: user.id,
          email: user.email,
          roleId: user.roleId,
          partnerId: user.partner.id,
          partner: {
            id: user.partner.id,
            avatar: user.partner.avatar,
            name: user.partner.name,
            surName: user.partner.surName,
          },
        };

        // Verificar que la actualización se aplicó correctamente
        if (activity.user?.partner?.name !== newPartnerName) {
          console.error(
            `ERROR: No se actualizó correctamente. Esperado: "${newPartnerName}", Actual: "${activity.user?.partner?.name}"`,
          );
        }
      } else {
        console.warn(
          `No se encontró usuario para activity.userId: ${activity.userId}, activity.user?.partnerId: ${originalPartnerId}, nombre backend: "${originalPartnerName}"`,
        );
        // Log para debug: mostrar todos los usuarios disponibles
        console.log(
          'Usuarios disponibles:',
          users.map((u) => ({
            id: u.id,
            partnerId: u.partner?.id,
            name: u.partner?.name,
          })),
        );
      }
    });
  }

  // Agrupar actividades por fecha
  private groupActivitiesByDate(): void {
    if (!Array.isArray(this.activities) || this.activities.length === 0) {
      this.activitiesGroupedByDate = [];
      return;
    }

    const grouped: { [key: string]: Activity[] } = {};
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    this.activities.forEach((activity) => {
      // Para el divider: usar fecha de creación (createdOn)
      const dateStr = activity.createdOn;

      if (!dateStr) {
        const key = 'Sin fecha';
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(activity);
        return;
      }

      const activityDate = new Date(dateStr);
      if (Number.isNaN(activityDate.getTime())) {
        const key = 'Sin fecha';
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(activity);
        return;
      }

      activityDate.setHours(0, 0, 0, 0);
      const key = activityDate.toISOString().split('T')[0];

      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(activity);
    });

    // Convertir a array y ordenar por fecha descendente
    this.activitiesGroupedByDate = Object.keys(grouped)
      .sort((a, b) => {
        if (a === 'Sin fecha') return 1;
        if (b === 'Sin fecha') return -1;
        return new Date(b).getTime() - new Date(a).getTime();
      })
      .map((dateKey) => {
        let label = dateKey;
        if (dateKey !== 'Sin fecha') {
          const date = new Date(dateKey + 'T00:00:00'); // Crear fecha en zona local
          const todayDate = new Date();

          // Convertir a zona horaria española (CET/CEST)
          const spanishOffset = date.getTimezoneOffset() === -120 ? 120 : 60; // CEST = 120 min, CET = 60 min
          const spanishToday = new Date(
            todayDate.getTime() + spanishOffset * 60 * 1000,
          );
          spanishToday.setHours(0, 0, 0, 0);

          const spanishDate = new Date(
            date.getTime() + spanishOffset * 60 * 1000,
          );
          spanishDate.setHours(0, 0, 0, 0);

          if (spanishDate.getTime() === spanishToday.getTime()) {
            label = 'HOY';
          } else {
            label = spanishDate
              .toLocaleDateString('es-ES', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
              })
              .toUpperCase();
          }
        }
        return {
          date: dateKey,
          label: label,
          activities: grouped[dateKey],
        };
      });
  }

  // Obtener icono según el tipo de actividad
  getActivityIcon(activity: Activity): string {
    // Primero intentar usar el icono del activityType si existe
    if (activity.activityType?.icon) {
      return activity.activityType.icon;
    }

    // Fallback: determinar icono por nombre del tipo
    const typeName = activity.activityType?.name?.toLowerCase() || '';

    if (
      typeName.includes('correo') ||
      typeName.includes('email') ||
      typeName.includes('mail')
    ) {
      return 'mail';
    }
    if (
      typeName.includes('llamada') ||
      typeName.includes('call') ||
      typeName.includes('teléfono')
    ) {
      return 'call';
    }
    if (
      typeName.includes('reunión') ||
      typeName.includes('meeting') ||
      typeName.includes('calendario')
    ) {
      return 'groups';
    }
    if (typeName.includes('tarea') || typeName.includes('task')) {
      return 'task_alt';
    }
    return 'event_note';
  }

  // Obtener color del icono según el tipo de actividad
  getActivityIconColor(activity: Activity): string {
    // Icono blanco cuando tiene fondo de color
    if (
      activity.hasExpired ||
      activity.state === 'Done' ||
      activity.state === 'Cancelled' ||
      activity.state === 'InProgress' ||
      activity.state === 'Planned'
    ) {
      return '#fff';
    }
    return activity.activityType?.color || '#666';
  }

  // Obtener clase del wrapper del icono según estado
  getActivityIconWrapperClass(activity: Activity): string {
    if (
      activity.hasExpired &&
      activity.state !== 'Done' &&
      activity.state !== 'Cancelled'
    ) {
      return 'icon-wrapper-expired';
    }
    switch (activity.state) {
      case 'Done':
        return 'icon-wrapper-completed';
      case 'Cancelled':
        return 'icon-wrapper-cancelled';
      case 'InProgress':
        return 'icon-wrapper-in-progress';
      default:
        return 'icon-wrapper-pending';
    }
  }

  // Obtener estado formateado
  getActivityStatusClass(state: string): string {
    switch (state) {
      case 'Done':
        return 'status-completed';
      case 'Cancelled':
        return 'status-cancelled';
      case 'InProgress':
        return 'status-in-progress';
      default:
        return 'status-pending';
    }
  }

  getActivityStatusLabel(state: string): string {
    switch (state) {
      case 'Done':
        return 'Hecho';
      case 'Cancelled':
        return 'Cancelada';
      case 'InProgress':
        return 'En progreso';
      case 'Planned':
        return 'Pendiente';
      default:
        return state;
    }
  }

  // Obtener iniciales del usuario desde partner
  getUserInitials(activity: Activity | null | undefined): string {
    if (!activity) return '?';
    // Prioridad 1: Usar directamente los datos que vienen del backend en activity-type.user?.partner
    if (activity.user?.partner) {
      const firstName = activity.user.partner.name || '';
      const lastName = activity.user.partner.surName || '';
      // Si solo hay firstName, usar las primeras 2 letras del nombre
      if (firstName && !lastName) {
        return firstName.substring(0, 2).toUpperCase();
      }
      const initials = (firstName.charAt(0) + lastName.charAt(0)).toUpperCase();
      if (initials && initials.length >= 2) return initials;
      // Si solo hay una letra, usar la primera del nombre
      if (firstName) return firstName.charAt(0).toUpperCase();
    }

    // Prioridad 2: Buscar en la lista actualizada de usuarios por partnerId
    // IMPORTANTE: El userId guardado en la actividad puede ser un partnerId cuando viene del drawer
    // Por eso buscamos PRIMERO por partnerId y luego por userId como fallback
    const users = this.globalValuesService.users$.value;
    let user = null;

    // Primero buscar por partnerId del activity-type.user (más confiable)
    if (activity.user?.partnerId) {
      const partnerId = activity.user.partnerId;
      user = users.find((u) => u.partner?.id === partnerId);
    }

    // Si no se encontró, buscar usando userId como si fuera partnerId
    // porque en el drawer de actividad se guarda partner.id como userId
    if (!user) {
      const userId = activity.userId || activity.user?.id;
      if (userId) {
        user = users.find((u) => u.partner?.id === userId);
      }
    }

    // Como último recurso, buscar por user.id (pero esto raramente coincidirá)
    if (!user) {
      const userId = activity.userId || activity.user?.id;
      if (userId) {
        user = users.find((u) => u.id === userId);
      }
    }

    if (user?.partner) {
      const firstName = user.partner.name || '';
      const lastName = user.partner.surName || '';
      if (firstName && !lastName) {
        return firstName.substring(0, 2).toUpperCase();
      }
      const initials = (firstName.charAt(0) + lastName.charAt(0)).toUpperCase();
      if (initials && initials.length >= 2) return initials;
      if (firstName) return firstName.charAt(0).toUpperCase();
    }

    return 'U';
  }

  // Obtener nombre completo del usuario desde partner
  getUserFullName(activity: Activity): string {
    // Prioridad 1: Usar directamente los datos que vienen del backend en activity-type.user?.partner
    if (activity.user?.partner) {
      const firstName = activity.user.partner.name || '';
      const lastName = activity.user.partner.surName || '';
      const fullName = `${firstName} ${lastName}`.trim();
      if (fullName) return fullName;
      // Si solo hay nombre o solo apellido
      if (firstName) return firstName;
      if (lastName) return lastName;
    }

    // Prioridad 2: Buscar en la lista actualizada de usuarios por partnerId
    // IMPORTANTE: El userId guardado en la actividad puede ser un partnerId cuando viene del drawer
    // Por eso buscamos PRIMERO por partnerId y luego por userId como fallback
    const users = this.globalValuesService.users$.value;
    let user = null;

    // Primero buscar por partnerId del activity-type.user (más confiable)
    if (activity.user?.partnerId) {
      const partnerId = activity.user.partnerId;
      user = users.find((u) => u.partner?.id === partnerId);
    }

    // Si no se encontró, buscar usando userId como si fuera partnerId
    // porque en el drawer de actividad se guarda partner.id como userId
    if (!user) {
      const userId = activity.userId || activity.user?.id;
      if (userId) {
        user = users.find((u) => u.partner?.id === userId);
      }
    }

    // Como último recurso, buscar por user.id (pero esto raramente coincidirá)
    if (!user) {
      const userId = activity.userId || activity.user?.id;
      if (userId) {
        user = users.find((u) => u.id === userId);
      }
    }

    if (user?.partner) {
      const firstName = user.partner.name || '';
      const lastName = user.partner.surName || '';
      const fullName = `${firstName} ${lastName}`.trim();
      if (fullName) return fullName;
      if (firstName) return firstName;
      if (lastName) return lastName;
    }

    return 'Usuario';
  }

  // Obtener iniciales desde un objeto de usuario directamente
  getUserInitialsFromUser(user: any): string {
    if (!user) return 'U';

    const firstName = user.name || '';
    const lastName = user.surName || '';

    // Si solo hay firstName, usar las primeras 2 letras del nombre
    if (firstName && !lastName) {
      return firstName.substring(0, 2).toUpperCase();
    }

    // Combinar iniciales
    const initials = (firstName.charAt(0) + lastName.charAt(0)).toUpperCase();
    if (initials && initials.length >= 2) return initials;

    // Si solo hay una letra, usar la primera del nombre
    if (firstName) return firstName.charAt(0).toUpperCase();

    return 'U';
  }

  // Formatear hora de la actividad
  getActivityTime(activity: Activity): string {
    const dateStr = activity.dateCreated || activity.dateDeadLine;
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  // Formatear fecha de vencimiento
  getActivityDueDate(activity: Activity): string {
    if (!activity.dateDeadLine) return '';
    const date = new Date(activity.dateDeadLine);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }

  // Propiedades para el modal de completación
  completionModalActivity: Activity | null = null;
  completionNote: string = '';
  currentCompletionModalRef: NzModalRef | null = null;

  // Propiedades para el modal de añadir seguidor
  addFollowerModalRef: NzModalRef | null = null;
  viewAllFollowersModalRef: NzModalRef | null = null;
  followerSelectedType: 'contact' | 'user' = 'user';
  followerSegmentedOptions = [
    { label: 'Usuarios', value: 'user', icon: 'usergroup-delete' },
    { label: 'Contactos', value: 'contact', icon: 'contacts' },
  ];
  followerSearchText: string = '';
  viewAllFollowersSearchText: string = '';
  selectedFollowersIds: Set<number> = new Set();
  allContacts: any[] = [];
  allUsers: any[] = [];
  filteredContacts: any[] = [];
  filteredUsers: any[] = [];
  followersAccordionActive: string[] = [];
  filteredViewAllFollowers: any[] = [];
  addingFollowers: boolean = false;

  // Propiedades para el modal de asociar proyecto
  linkProjectModalRef: NzModalRef | null = null;
  availableProjectsForLink: any[] = [];
  loadingLinkProjects: boolean = false;
  linkProjectSelectedId: number | null = null;
  linkingProjectId: number | null = null;

  // Método para manejar el cambio en la nota de completación
  onCompletionNoteChange(event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    this.completionNote = target?.value || '';
  }

  // Método para cerrar el modal de completación y abrir nueva actividad
  closeCompletionModal(): void {
    const modalRef = this.currentCompletionModalRef;
    const activity = this.completionModalActivity;
    const note = this.completionNote;
    if (modalRef) {
      modalRef.close();
      setTimeout(() => {
        this.currentCompletionModalRef = null;
        this.completionModalActivity = null;
        this.completionNote = '';
      }, 0);
    } else {
      this.currentCompletionModalRef = null;
      this.completionModalActivity = null;
      this.completionNote = '';
    }
    if (activity) {
      this.executeMarkAsCompleted(activity, note);
      this.openCreateActivityDrawer();
    }
  }

  // Método para cancelar el modal de completación (sin abrir drawer)
  cancelCompletionModal(): void {
    const modalRef = this.currentCompletionModalRef;
    if (modalRef) {
      modalRef.close();
      setTimeout(() => {
        this.currentCompletionModalRef = null;
        this.completionModalActivity = null;
        this.completionNote = '';
      }, 0);
    } else {
      this.currentCompletionModalRef = null;
      this.completionModalActivity = null;
      this.completionNote = '';
    }
  }

  // Método para confirmar la completación de la actividad
  confirmCompleteActivity(): void {
    const modalRef = this.currentCompletionModalRef;
    const activity = this.completionModalActivity;
    const note = this.completionNote;
    if (modalRef) {
      modalRef.close();
      setTimeout(() => {
        this.currentCompletionModalRef = null;
        this.completionModalActivity = null;
        this.completionNote = '';
      }, 0);
    } else {
      this.currentCompletionModalRef = null;
      this.completionModalActivity = null;
      this.completionNote = '';
    }
    if (activity) this.executeMarkAsCompleted(activity, note);
  }

  // Marcar actividad como completada
  markActivityAsCompleted(activity: Activity): void {
    if (!activity.id) return;

    this.completionModalActivity = activity;
    this.completionNote = '';

    const modalRef: NzModalRef = this.modalService.create({
      nzTitle: 'Marcar actividad como completada',
      nzContent: this.completionModalTmpl,
      nzWidth: 600,
      nzBodyStyle: { minHeight: '300px' },
      nzMaskClosable: false,
      nzCentered: true,
      nzFooter: null,
    });

    // Guardar referencia del modal para los métodos de los botones
    this.currentCompletionModalRef = modalRef;
  }

  private executeMarkAsCompleted(activity: Activity, note?: string): void {
    // Determinar parentEntityType y parentEntityId según el modo de vista
    // Si estamos en modo budget, usar el presupuesto (SaleOrder) con el nombre como string
    // Si no, usar los valores de la actividad o la oportunidad como fallback
    let parentEntityType: number | string;
    let parentEntityId: number;

    if (this.viewMode === 'budget' && this.budget?.id) {
      // Modo budget: usar el presupuesto (SaleOrder) con el nombre como string
      parentEntityType = 'SaleOrder';
      parentEntityId = this.budget.id;
    } else {
      // Modo normal: usar los valores de la actividad o la oportunidad como fallback
      parentEntityType = activity.parentEntityType ?? PARENT_ENTITY_TYPE.LEAD;
      parentEntityId =
        activity.parentEntityId ??
        (this.opportunity?.id || this.opportunity?.originalId || 0);
    }

    this.activityService
      .markAsDone({
        id: activity.id!,
        parentEntityType: parentEntityType as any, // El backend acepta string 'SaleOrder' o número
        parentEntityId: parentEntityId,
        doneNote: note || '',
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.toastConfig = {
            icon: 'check_circle',
            iconColor: '#6CDA75',
            content: 'Actividad marcada como completada.',
            showUndo: false,
          };
          this.showNotification('Actividad marcada como completada.');
          this.loadActivities();
        },
        error: (e) => {
          console.error('Error al marcar actividad:', e);
        },
      });
  }

  // Cancelar actividad
  cancelActivityAction(activity: Activity): void {
    if (!activity.id) return;

    this.modalService.confirm({
      nzTitle: '¿Cancelar actividad?',
      nzContent: `¿Estás seguro de que deseas cancelar esta actividad? Esta acción no se puede deshacer.`,
      nzOkText: 'Sí, cancelar',
      nzOkType: 'primary',
      nzOkDanger: true,
      nzCancelText: 'No, volver',
      nzOnOk: () => {
        this.executeCancelActivity(activity);
      },
    });
  }

  private executeCancelActivity(activity: Activity): void {
    // Determinar parentEntityType y parentEntityId según el modo de vista
    // Si estamos en modo budget, usar el presupuesto (SaleOrder) con el nombre como string
    // Si no, usar los valores de la actividad o la oportunidad como fallback
    let parentEntityType: number | string;
    let parentEntityId: number;

    if (this.viewMode === 'budget' && this.budget?.id) {
      // Modo budget: usar el presupuesto (SaleOrder) con el nombre como string
      parentEntityType = 'SaleOrder';
      parentEntityId = this.budget.id;
    } else {
      // Modo normal: usar los valores de la actividad o la oportunidad como fallback
      parentEntityType = activity.parentEntityType ?? PARENT_ENTITY_TYPE.LEAD;
      parentEntityId =
        activity.parentEntityId ??
        (this.opportunity?.id || this.opportunity?.originalId || 0);
    }

    this.activityService
      .cancelActivity({
        id: activity.id!,
        parentEntityType: parentEntityType as any, // El backend acepta string 'SaleOrder' o número
        parentEntityId: parentEntityId,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.toastConfig = {
            icon: 'check_circle',
            iconColor: '#6CDA75',
            content: 'Actividad cancelada.',
            showUndo: false,
          };
          this.showNotification('Actividad cancelada.');
          this.loadActivities();
        },
        error: (e) => {
          console.error('Error al cancelar actividad:', e);
        },
      });
  }

  // Helper method to get default budget name
  getDefaultBudgetName(sequenceNumber?: number): string {
    let opportunityName = this.opportunity?.oportunidad || '';

    // Recortar el nombre si es demasiado largo (máximo 40 caracteres)
    const maxLength = 40;
    if (opportunityName.length > maxLength) {
      opportunityName = opportunityName.substring(0, maxLength) + '...';
    }

    const suffix = sequenceNumber ? ` #${sequenceNumber}` : '';

    if (opportunityName && opportunityName.trim() !== '') {
      return `Presupuesto ${opportunityName}${suffix}`.trim();
    } else {
      return `Presupuesto${suffix}`;
    }
  }

  private getNextBudgetNumber(): number {
    const baseName = this.getDefaultBudgetName();
    let maxNumber = 0;

    this.budgetList.forEach(budget => {
      if (budget.name?.startsWith(baseName)) {
        const match = budget.name.match(/#(\d+)$/);
        if (match) {
          maxNumber = Math.max(maxNumber, parseInt(match[1], 10));
        } else {
          maxNumber = Math.max(maxNumber, 1);
        }
      }
    });

    return maxNumber + 1;
  }

  // Create budget from opportunity
  createBudget(): void {
    const payload = {
      name: this.getDefaultBudgetName(this.getNextBudgetNumber()),
      isActive: false,
      leadId: this.opportunity.id,
      teamId: this.opportunity.teamId,
      // CEC-565: ver la nota del otro alta de este mismo fichero.
      currencyId: resolveNewQuotationCurrencyId(
        [this.opportunity.currencyId, this.opportunity?.company?.currencyId],
        this.globalValuesService.currencies$.value,
      ),
      partnerId: this.opportunity.partnerId,
      expirationDate: new Date(
        new Date().setMonth(new Date().getMonth() + 1),
      ).toISOString(),
    };

    // Crear presupuesto usando el servicio
    this.opportunityService
      .createSaleOrder(payload)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          //this.creatingBudget = false;
        }),
      )
      .subscribe({
        next: (response: any) => {
          this.toastConfig = {
            icon: 'check_circle',
            iconColor: '#6CDA75',
            content: 'Presupuesto creado correctamente.',
            showUndo: false,
          };

          this.notRef = this.notificationService.create(
            'blank',
            '',
            this.notificationTmpl,
            { nzPlacement: 'bottomRight' },
          );

          // Usar el objeto completo de la respuesta en lugar de solo el ID
          if (response?.data) {
            // Agregar el presupuesto a la lista si no está
            const existingIndex = this.budgetList.findIndex(
              (b) => b.id === response.data.id,
            );
            if (existingIndex === -1) {
              this.budgetList.unshift(response.data);
            } else {
              // Actualizar si ya existe
              this.budgetList[existingIndex] = response.data;
            }

            // Abrir la vista con el objeto completo
            this.openBudgetView(response.data);
          }
        },
        error: (error) => {
          console.error('Error creating budget:', error);
        },
      });
  }

  // Create project from opportunity
  createProject(): void {
    combineLatest({
      isMobile: this.screenService.isMobile$,
      isMedium: this.screenService.isMedium$,
    })
      .pipe(take(1))
      .subscribe(({ isMobile, isMedium }) => {
        const createProjectDrawer = this.drawerService.create({
          nzWidth: 793,
          nzHeight: '98%',
          nzPlacement: isMobile ? 'bottom' : 'right',
          nzClosable: false,
          nzMaskClosable: false,
          nzContent: CreateProjectDrawerComponent,
          nzData: {
            opportunityId: this.opportunity?.id || this.opportunity?.originalId,
            leadId: this.opportunity?.id || this.opportunity?.originalId, // Identificador de la oportunidad (Lead u Opportunity)
          },
        });

        createProjectDrawer.afterOpen.pipe(take(1)).subscribe(() => {
          const contentComponent = createProjectDrawer.getContentComponent();
          if (contentComponent && contentComponent.createEvent) {
            contentComponent.createEvent.pipe(take(1)).subscribe(() => {
              // El proyecto se creó exitosamente - Recargar la oportunidad para mostrar el nuevo proyecto
              createProjectDrawer.close();
              this.reloadOpportunity();
            });
          }
        });
      });
  }

  // Create activity-type drawer
  openCreateActivityDrawer(): void {
    combineLatest({
      isMobile: this.screenService.isMobile$,
      isMedium: this.screenService.isMedium$,
    })
      .pipe(take(1))
      .subscribe(({ isMobile, isMedium }) => {
        // Preparar los datos según el modo de vista
        let drawerData: any = {};

        if (this.viewMode === 'budget' && this.budget?.id) {
          // Modo budget: pasar saleOrderId (budget.id) para usar el presupuesto como entidad padre
          drawerData.saleOrderId = this.budget.id;
          drawerData.opportunityId = undefined;
          drawerData.leadId = undefined;
        } else {
          // Modo normal
          drawerData.opportunityId = this.isLead
            ? undefined
            : this.opportunity?.id || this.opportunity?.originalId;
          drawerData.leadId = this.isLead
            ? this.opportunity?.id || this.opportunity?.originalId
            : undefined;
        }

        const createProjectDrawer = this.drawerService.create({
          nzWidth: 793,
          nzHeight: '98%',
          nzPlacement: isMobile ? 'bottom' : 'right',
          nzClosable: false,
          nzMaskClosable: false,
          nzContent: OportunitiesDetailsActivityDrawerComponent,
          nzData: drawerData,
        });

        createProjectDrawer.afterOpen.pipe(take(1)).subscribe(() => {
          const contentComponent = createProjectDrawer.getContentComponent();

          // Escuchar cuando se crea una actividad y se cierra el drawer
          if (contentComponent && contentComponent.createEvent) {
            contentComponent.createEvent.pipe(take(1)).subscribe(() => {
              this.loadActivities();
              createProjectDrawer.close();
            });
          }

          // Escuchar cuando se crea una actividad pero el drawer permanece abierto (scheduleAndNext)
          if (contentComponent && contentComponent.activityScheduled) {
            contentComponent.activityScheduled.subscribe(() => {
              this.loadActivities();
            });
          }
        });
      });
  }

  // Create budget/quote drawer (Nuevo Presupuesto)
  openCreateBudgetDrawer(): void {
    combineLatest({
      isMobile: this.screenService.isMobile$,
      isMedium: this.screenService.isMedium$,
    })
      .pipe(take(1))
      .subscribe(({ isMobile, isMedium }) => {
        const createBudgetDrawer = this.drawerService.create({
          nzWidth: isMobile ? '100%' : isMedium ? '80%' : 793,
          nzHeight: '98%',
          nzPlacement: isMobile ? 'bottom' : 'right',
          nzClosable: false,
          nzMaskClosable: false,
          nzContent: CreateProjectDrawerComponent,
          nzData: {
            opportunityId: this.opportunity?.id || this.opportunity?.originalId,
            leadId: this.opportunity?.id || this.opportunity?.originalId, // Identificador de la oportunidad (Lead u Opportunity)
            opportunity: this.opportunity,
            type: 'budget', // Para indicar que es un presupuesto
          },
        });

        createBudgetDrawer.afterOpen.pipe(take(1)).subscribe(() => {
          const contentComponent = createBudgetDrawer.getContentComponent();
          if (contentComponent && contentComponent.createEvent) {
            contentComponent.createEvent.pipe(take(1)).subscribe(() => {
              // El presupuesto se creó exitosamente
              this.toastConfig = {
                icon: 'check_circle',
                iconColor: '#6CDA75',
                content: 'Presupuesto creado exitosamente.',
                showUndo: false,
              };
              this.showNotification();
              createBudgetDrawer.close();
            });
          }
        });
      });
  }

  // Create project drawer (Nuevo Proyecto)
  openCreateProjectDrawer(): void {
    combineLatest({
      isMobile: this.screenService.isMobile$,
      isMedium: this.screenService.isMedium$,
    })
      .pipe(take(1))
      .subscribe(({ isMobile, isMedium }) => {
        const createProjectDrawer = this.drawerService.create({
          nzWidth: isMobile ? '100%' : isMedium ? '80%' : 793,
          nzHeight: '98%',
          nzPlacement: isMobile ? 'bottom' : 'right',
          nzClosable: false,
          nzMaskClosable: false,
          nzContent: CreateProjectDrawerComponent,
          nzData: {
            opportunityId: this.opportunity?.id || this.opportunity?.originalId,
            leadId: this.opportunity?.id || this.opportunity?.originalId, // Identificador de la oportunidad (Lead u Opportunity)
            opportunity: this.opportunity,
            type: 'project', // Para indicar que es un proyecto
          },
        });

        createProjectDrawer.afterOpen.pipe(take(1)).subscribe(() => {
          const contentComponent = createProjectDrawer.getContentComponent();
          if (contentComponent && contentComponent.createEvent) {
            contentComponent.createEvent.pipe(take(1)).subscribe(() => {
              // El proyecto se creó exitosamente - Recargar la oportunidad para mostrar el nuevo proyecto
              this.toastConfig = {
                icon: 'check_circle',
                iconColor: '#6CDA75',
                content: 'Proyecto creado exitosamente.',
                showUndo: false,
              };
              this.showNotification();
              createProjectDrawer.close();
              this.reloadOpportunity();
            });
          }
        });
      });
  }

  // Edit activity-type drawer
  openEditActivityDrawer(activity: Activity): void {
    combineLatest({
      isMobile: this.screenService.isMobile$,
      isMedium: this.screenService.isMedium$,
    })
      .pipe(take(1))
      .subscribe(({ isMobile, isMedium }) => {
        // Preparar los datos según el modo de vista
        let drawerData: any = {
          activity: activity, // Pasar la actividad a editar
        };

        if (this.viewMode === 'budget' && this.budget?.id) {
          // Modo budget: pasar saleOrderId (budget.id) para usar el presupuesto como entidad padre
          drawerData.saleOrderId = this.budget.id;
          drawerData.opportunityId = undefined;
          drawerData.leadId = undefined;
        } else {
          // Modo normal
          drawerData.opportunityId = this.isLead
            ? undefined
            : this.opportunity?.id || this.opportunity?.originalId;
          drawerData.leadId = this.isLead
            ? this.opportunity?.id || this.opportunity?.originalId
            : undefined;
        }

        const editActivityDrawer = this.drawerService.create({
          nzWidth: 793,
          nzHeight: '98%',
          nzPlacement: isMobile ? 'bottom' : 'right',
          nzClosable: false,
          nzMaskClosable: false,
          nzContent: OportunitiesDetailsActivityDrawerComponent,
          nzData: drawerData,
        });

        editActivityDrawer.afterOpen.pipe(take(1)).subscribe(() => {
          const contentComponent = editActivityDrawer.getContentComponent();
          if (contentComponent && contentComponent.createEvent) {
            contentComponent.createEvent.pipe(take(1)).subscribe(() => {
              // La actividad se actualizó exitosamente, recargar lista
              this.loadActivities();
              editActivityDrawer.close();
            });
          }
        });
      });
  }

  // Create new opportunity
  // Alternar visibilidad del panel de actividades en móvil
  toggleActivitiesPanel(): void {
    this.showActivitiesPanel = !this.showActivitiesPanel;
  }

  createNewOpportunity(): void {
    combineLatest({
      isMobile: this.screenService.isMobile$,
      isMedium: this.screenService.isMedium$,
    })
      .pipe(take(1))
      .subscribe(({ isMobile, isMedium }) => {
        const addOpportunityDrawer = this.drawerService.create({
          nzWidth: 793,
          nzHeight: '98%',
          nzPlacement: isMobile ? 'bottom' : 'right',
          nzClosable: false,
          nzMaskClosable: false,
          nzMask: true,
          nzContent: OportunitiesAddDrawerComponent,
          nzData: {
            flowId: undefined,
            readonly: false,
          },
        });

        // Recargar oportunidades cuando el drawer se cierre
        addOpportunityDrawer.afterClose.subscribe((result: any) => {
          if (result) {
            // Si hay un resultado, significa que se creó exitosamente
            // Emitir evento para que el componente padre recargue las oportunidades
            this.createNewOpportunityEvent.emit();
          }
        });
      });
  }

  // Handle dropdown action from split button
  onSplitButtonDropdownAction(action: string): void {
    if (action === 'newOpportunity') {
      this.createNewOpportunity();
    } else if (action === 'newProject') {
      this.createProject();
    } else if (action === 'newBudget') {
      this.openBudgetDrawer();
    }
  }

  // Open budget drawer (crea un presupuesto temporal y abre en modo edición)
  openBudgetDrawer(): void {
    console.log('✅ opportunity', this.opportunity);
    // Crear un objeto de presupuesto temporal (sin ID) en lugar de crear en el backend
    const tempBudget = {
      id: null, // Sin ID = presupuesto nuevo no guardado
      name: this.getDefaultBudgetName(this.getNextBudgetNumber()),
      isActive: false,
      leadId: this.opportunity?.id || this.opportunity?.originalId,
      teamId: this.opportunity?.teamId,
      currencyId: this.opportunity?.company?.currencyId || 1,
      partnerId: this.opportunity?.partnerId,
      expirationDate: new Date(
        new Date().setMonth(new Date().getMonth() + 1),
      ).toISOString(),
      budgetDate: new Date().toISOString(),
      driveUrl: '',
      terms: '',
      quotationLines: [],
      budgetLines: [],
    };

    // NO agregar el presupuesto temporal a budgetList hasta que se guarde exitosamente
    // Esto evita que aparezca en el tab de "Presupuestos" antes de confirmar su creación

    // Abrir la vista con el presupuesto temporal en modo edición
    this.viewMode = 'budget';
    this.budget = tempBudget;
    this.isBudgetCurrencyConversionValid = true;
    this.isBudgetEditModeEnabled = true; // Activar modo edición automáticamente
    this.budgetEditModeAction = 'none';

    // Actualizar propiedades computadas
    this.updateComputedProperties();

    // Forzar detección de cambios
    this.cdr.detectChanges();
  }

  // Convert lead to opportunity
  convertToOpportunity(): void {
    const leadId = this.opportunity?.id || this.opportunity?.originalId;
    if (!leadId) {
      console.warn('No se encontró el ID del lead');
      return;
    }

    // Abrir el drawer de conversión primero
    combineLatest({
      isMobile: this.screenService.isMobile$,
      isMedium: this.screenService.isMedium$,
    })
      .pipe(take(1))
      .subscribe(({ isMobile, isMedium }) => {
        const convertDrawer = this.drawerService.create({
          nzWidth: 793,
          nzHeight: '98%',
          nzPlacement: isMobile ? 'bottom' : 'right',
          nzClosable: false,
          nzMaskClosable: false,
          nzContent: ConvertLeadDrawerComponent,
          nzData: {
            leadId: leadId,
            lead: this.opportunity, // Pasar el objeto completo del Lead para acceder a teamId y userId
          },
        });

        // Escuchar el evento de conversión
        convertDrawer.afterOpen.pipe(take(1)).subscribe(() => {
          const contentComponent = convertDrawer.getContentComponent();
          if (contentComponent && contentComponent.convertEvent) {
            contentComponent.convertEvent
              .pipe(take(1))
              .subscribe((convertData) => {
                // Cerrar el drawer de conversión
                convertDrawer.close();

                // Primero, realizar la conversión (POST)
                this.loading = true;
                this.opportunityService
                  .convertLead(
                    leadId,
                    convertData.currencyId,
                    convertData.expectedClosingDate,
                  )
                  .pipe(
                    takeUntilDestroyed(this.destroyRef),
                    finalize(() => {
                      this.loading = false;
                    }),
                  )
                  .subscribe({
                    next: (response) => {
                      if (response.success && response.data) {
                        // Si la conversión es exitosa, entonces actualizar la entidad (PATCH)

                        // Construir el payload completo con todas las propiedades
                        const opportunityName = this.getOpportunityName();

                        // Helper para obtener valor de ID (null si es 0 o no existe)
                        const getIdValue = (value: any): number | null => {
                          return value && value !== 0 ? value : null;
                        };

                        // CRÍTICO: Calcular stageId correcto para el equipo seleccionado en conversión.
                        // Si el usuario cambió el equipo de ventas, el stageId del lead pertenece al equipo anterior.
                        // Debemos usar el primer stage del NUEVO equipo (convertData.teamId).
                        const teamIdForPayload = getIdValue(convertData.teamId);
                        let stageIdForPayload = getIdValue(
                          this.opportunity?.stageId,
                        );
                        if (teamIdForPayload) {
                          const stagesForNewTeam =
                            this.getStagesForTeamId(teamIdForPayload);
                          if (stagesForNewTeam.length > 0) {
                            const stageBelongsToNewTeam = stageIdForPayload
                              ? stagesForNewTeam.some(
                                  (s: any) => s.id === stageIdForPayload,
                                )
                              : false;
                            if (!stageBelongsToNewTeam) {
                              stageIdForPayload = stagesForNewTeam[0].id;
                            }
                          }
                        }

                        // Construir el payload: no enviar companyId/partnerId 0 (el backend falla: lead no existe para company 0)
                        const patchCompanyId =
                          this.opportunity?.companyId != null &&
                          this.opportunity.companyId !== 0
                            ? this.opportunity.companyId
                            : undefined;
                        const patchPartnerId =
                          this.opportunity?.partnerId != null &&
                          this.opportunity.partnerId !== 0
                            ? this.opportunity.partnerId
                            : undefined;
                        const patchPayload: any = {
                          id: leadId,
                          ...(patchCompanyId != null
                            ? { companyId: patchCompanyId }
                            : {}),
                          name:
                            opportunityName ||
                            this.opportunity?.name ||
                            this.opportunity?.oportunidad ||
                            this.opportunity?.originalOpportunityName ||
                            '',
                          type: this.opportunity?.type || 'Lead',
                          opportunityType: convertData.opportunityType,
                          priority: this.opportunity?.priority || null,
                          expectedRevenue:
                            this.opportunity?.expectedRevenue?.parsedValue ??
                            (this.opportunity?.expectedRevenue?.source
                              ? parseFloat(
                                  this.opportunity.expectedRevenue.source,
                                )
                              : null) ??
                            this.opportunity?.expectedRevenue ??
                            0,
                          probability: this.opportunity?.probability || 0,
                          expectedClosingDate:
                            convertData.expectedClosingDate ||
                            (this.opportunity?.expectedClosingDate
                              ? typeof this.opportunity.expectedClosingDate ===
                                'string'
                                ? this.opportunity.expectedClosingDate
                                : new Date(
                                    this.opportunity.expectedClosingDate,
                                  ).toISOString()
                              : null),
                          source: this.opportunity?.source || null,
                          description: this.opportunity?.description || null,
                          email: this.opportunity?.email || null,
                          phone: this.opportunity?.phone || null,
                          mobile: this.opportunity?.mobile || null,
                          street: this.opportunity?.street || null,
                          street2: this.opportunity?.street2 || null,
                          city: this.opportunity?.city || null,
                          province: this.opportunity?.province || null,
                          zip: this.opportunity?.zip || null,
                          website: this.opportunity?.website || null,
                          clientCompany:
                            this.opportunity?.clientCompany || null,
                          intermediaryCompanyId: getIdValue(
                            this.opportunity?.intermediaryCompanyId,
                          ),
                          jobTitle: this.opportunity?.jobTitle || null,
                          isActive:
                            this.opportunity?.isActive !== undefined
                              ? this.opportunity.isActive
                              : true,
                          dateConversion: this.opportunity?.dateConversion
                            ? typeof this.opportunity.dateConversion ===
                              'string'
                              ? this.opportunity.dateConversion
                              : new Date(
                                  this.opportunity.dateConversion,
                                ).toISOString()
                            : null,
                          dateWon: this.opportunity?.dateWon
                            ? typeof this.opportunity.dateWon === 'string'
                              ? this.opportunity.dateWon
                              : new Date(this.opportunity.dateWon).toISOString()
                            : null,
                          dateLost: this.opportunity?.dateLost
                            ? typeof this.opportunity.dateLost === 'string'
                              ? this.opportunity.dateLost
                              : new Date(
                                  this.opportunity.dateLost,
                                ).toISOString()
                            : null,
                          dateLastActivity: this.opportunity?.dateLastActivity
                            ? typeof this.opportunity.dateLastActivity ===
                              'string'
                              ? this.opportunity.dateLastActivity
                              : new Date(
                                  this.opportunity.dateLastActivity,
                                ).toISOString()
                            : null,
                          dateNextActivity: this.opportunity?.dateNextActivity
                            ? typeof this.opportunity.dateNextActivity ===
                              'string'
                              ? this.opportunity.dateNextActivity
                              : new Date(
                                  this.opportunity.dateNextActivity,
                                ).toISOString()
                            : null,
                          ...(patchPartnerId != null
                            ? { partnerId: patchPartnerId }
                            : {}),
                          userId: getIdValue(convertData.userId),
                          teamId: getIdValue(convertData.teamId),
                          stageId: stageIdForPayload,
                          lostReasonId: getIdValue(
                            this.opportunity?.lostReasonId,
                          ),
                          countryId: getIdValue(this.opportunity?.countryId),
                          stateId: getIdValue(this.opportunity?.stateId),
                          chatId: getIdValue(this.opportunity?.chatId),
                          costCenterId: getIdValue(convertData.costCenterId),
                        };

                        // Agregar currencyId si existe en opportunity, o tomarlo de la company asociada
                        let currencyIdValue =
                          this.opportunity?.currencyId || null;
                        if (!currencyIdValue && this.opportunity?.companyId) {
                          const company = this.companies.find(
                            (c: any) => c.id === this.opportunity.companyId,
                          );
                          currencyIdValue = company?.currencyId || null;
                        }
                        if (
                          currencyIdValue !== null &&
                          currencyIdValue !== undefined
                        ) {
                          patchPayload.currencyId = currencyIdValue;
                        }

                        // Agregar tagIds si existen
                        if (this.opportunity?.tags) {
                          let tagIds: number[] = [];
                          if (Array.isArray(this.opportunity.tags)) {
                            if (
                              this.opportunity.tags.length > 0 &&
                              typeof this.opportunity.tags[0] === 'number'
                            ) {
                              tagIds = this.opportunity.tags;
                            } else {
                              tagIds = this.opportunity.tags
                                .map((tag: any) => tag.id || tag)
                                .filter(Boolean);
                            }
                          }
                          if (tagIds.length > 0) {
                            patchPayload.tagIds = tagIds;
                          }
                        }

                        // Ahora actualizar la entidad con el PATCH
                        this.loading = true;
                        this.opportunityService
                          .patchOpportunity(patchPayload)
                          .pipe(
                            takeUntilDestroyed(this.destroyRef),
                            finalize(() => {
                              this.loading = false;
                            }),
                          )
                          .subscribe({
                            next: (rsp) => {
                              // Mapear la nueva oportunidad
                              const newOpportunity =
                                this.opportunitiesUtilsService.mapOpportunityData(
                                  rsp.data,
                                );

                              // ACTUALIZAR EL ESTADO SIN CERRAR EL DRAWER
                              this.opportunity = newOpportunity;
                              this.isLead = false;
                              this.viewMode = 'opportunity';

                              // Actualizar propiedades
                              this.updateCanCreateQuote();
                              this.updateComputedProperties();
                              this.updateStagesForTeam();
                              this.updateCurrentStageName();

                              // Cargar presupuestos ahora que es una oportunidad
                              this.loadBudgets();

                              // Actualizar la URL para reflejar el cambio
                              this.updateUrlFromState();

                              // Mostrar notificación
                              this.toastConfig = {
                                icon: 'check_circle',
                                iconColor: '#6CDA75',
                                content:
                                  'Lead convertido en oportunidad exitosamente.',
                                showUndo: false,
                              };
                              this.notRef = this.notificationService.create(
                                'blank',
                                '',
                                this.notificationTmpl,
                                { nzPlacement: 'bottomRight' },
                              );

                              // Emitir eventos para actualizar la tabla (eliminar lead, agregar oportunidad)
                              this.deleteEvent.emit(leadId);
                              this.updateEvent.emit(newOpportunity);

                              // Forzar detección de cambios
                              this.cdr.detectChanges();
                            },
                            error: (error) => {
                              this.toastConfig = {
                                icon: 'dangerous',
                                iconColor: '#CB2A2F',
                                content:
                                  error.error?.message ||
                                  'Error al actualizar el lead convertido.',
                                showUndo: false,
                              };
                              this.notRef = this.notificationService.create(
                                'blank',
                                '',
                                this.notificationTmpl,
                                { nzPlacement: 'bottomRight' },
                              );
                            },
                          });
                      } else {
                        this.toastConfig = {
                          icon: 'dangerous',
                          iconColor: '#CB2A2F',
                          content:
                            response.message || 'Error al convertir el lead.',
                          showUndo: false,
                        };
                        this.notRef = this.notificationService.create(
                          'blank',
                          '',
                          this.notificationTmpl,
                          { nzPlacement: 'bottomRight' },
                        );
                      }
                    },
                    error: (error) => {
                      this.toastConfig = {
                        icon: 'dangerous',
                        iconColor: '#CB2A2F',
                        content:
                          error.error?.message || 'Error al convertir el lead.',
                        showUndo: false,
                      };
                      this.notRef = this.notificationService.create(
                        'blank',
                        '',
                        this.notificationTmpl,
                        { nzPlacement: 'bottomRight' },
                      );
                    },
                  });
              });
          }
        });
      });
  }
  private showNotification(message?: string): void {
    // Solo configurar toastConfig si se proporciona un mensaje o si no está configurado
    if (message || !this.toastConfig.content) {
      this.toastConfig = {
        icon: 'check_circle',
        iconColor: '#6CDA75',
        content: message || 'Actividad programada exitosamente.',
        showUndo: false,
      };
    }
    this.notRef = this.notificationService.create(
      'blank',
      '',
      this.notificationTmpl,
      { nzPlacement: 'bottomRight' },
    );
  }

  // Request documents for lead
  requestDocuments(): void {
    // TODO: Implementar lógica para solicitar documentos necesarios
  }

  /**
   * Une la respuesta de getOpportunityById con el opportunity actual para no perder id/originalId
   * ni objetos expandidos (partner, user, team, etc.) que el API a veces no devuelve.
   */
  private mergeOpportunityFromApi(updatedOpportunity: any, existing: any): any {
    if (!updatedOpportunity) return existing;

    const merged = {
      ...updatedOpportunity,
      id: updatedOpportunity.id ?? existing?.id,
      originalId: updatedOpportunity.originalId ?? existing?.originalId,
      // Para leads no sobrescribir name con el del contacto (API a veces devuelve partner.name en name)
      name:
        existing?.type === 'Lead' &&
        existing?.name &&
        !String(existing.name).includes('@')
          ? existing.name
          : (updatedOpportunity.name ?? existing?.name),
      oportunidad: updatedOpportunity.oportunidad ?? existing?.oportunidad,
      originalOpportunityName:
        updatedOpportunity.originalOpportunityName ??
        existing?.originalOpportunityName,
      teamId: updatedOpportunity.teamId ?? existing?.teamId,
      stageId: updatedOpportunity.stageId ?? existing?.stageId,
      userId: updatedOpportunity.userId ?? existing?.userId,
      costCenterId: updatedOpportunity.costCenterId ?? existing?.costCenterId,
      companyId: updatedOpportunity.companyId ?? existing?.companyId,
      partnerId: updatedOpportunity.partnerId ?? existing?.partnerId,
      priority: updatedOpportunity.priority ?? existing?.priority,
      intermediaryCompanyId:
        updatedOpportunity.intermediaryCompanyId ??
        existing?.intermediaryCompanyId,
      clientCompanyId:
        (updatedOpportunity as any)?.clientCompanyId ??
        (existing as any)?.clientCompanyId,
      clientCompany:
        (updatedOpportunity as any)?.clientCompany ??
        (existing as any)?.clientCompany,
      partner: updatedOpportunity.partner ?? existing?.partner,
      user: updatedOpportunity.user ?? existing?.user,
      team: updatedOpportunity.team ?? existing?.team,
      company: updatedOpportunity.company ?? existing?.company,
      costCenter: updatedOpportunity.costCenter ?? existing?.costCenter,
      stage: updatedOpportunity.stage ?? existing?.stage,
      intermediaryCompany:
        updatedOpportunity.intermediaryCompany ?? existing?.intermediaryCompany,
      contactPartner:
        updatedOpportunity.contactPartner ?? existing?.contactPartner,
      comercialUser:
        updatedOpportunity.comercialUser ?? existing?.comercialUser,
      // Preservar chat y chatId del backend (son importantes para mostrar mensajes)
      chat: updatedOpportunity.chat ?? existing?.chat,
      chatId: updatedOpportunity.chatId ?? existing?.chatId,
      // Preservar documents si existen
      documents: updatedOpportunity.documents ?? existing?.documents,
    };

    // Normalizar: el API devuelve "partner" y "user", pero los componentes esperan "contactPartner" y "comercialUser"
    if (!merged.contactPartner && merged.partner) {
      merged.contactPartner = merged.partner;
    }
    if (!merged.comercialUser && merged.user?.partner) {
      merged.comercialUser = merged.user.partner;
    }

    return merged;
  }

  // Recargar la oportunidad completa desde el backend
  private reloadOpportunity(): void {
    const opportunityId = this.opportunity?.id || this.opportunity?.originalId;
    if (!opportunityId) {
      console.warn('No se puede recargar la oportunidad: ID no disponible');
      return;
    }

    // Usar getOpportunityById que funciona tanto para oportunidades como para leads
    this.opportunityService
      .getOpportunityById(opportunityId, undefined, undefined, true)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          let updatedOpportunity = null;

          if (
            response?.data?.rows &&
            Array.isArray(response.data.rows) &&
            response.data.rows.length > 0
          ) {
            updatedOpportunity = response.data.rows[0];
          } else if (
            response?.data &&
            Array.isArray(response.data) &&
            response.data.length > 0
          ) {
            updatedOpportunity = response.data[0];
          } else if (
            response?.data &&
            typeof response.data === 'object' &&
            !Array.isArray(response.data)
          ) {
            updatedOpportunity = response.data;
          }

          if (updatedOpportunity) {
            // Mapear la oportunidad usando el servicio de utilidades
            const mappedOpportunity =
              this.opportunitiesUtilsService.mapOpportunityData(
                updatedOpportunity,
              );

            if (mappedOpportunity) {
              // Actualizar la oportunidad con los valores del backend
              // Asegurar que projects se actualice correctamente desde el backend
              const updatedProjects =
                mappedOpportunity.projects || updatedOpportunity.projects || [];

              this.opportunity = {
                ...mappedOpportunity,
                projects: updatedProjects, // Asegurar que projects se actualice
                documents: this.opportunity?.documents || [],
                chat: this.opportunity?.chat,
              };

              // Recargar el chat para obtener los mensajes actualizados del backend (incluidos los automáticos)
              if (this.opportunity?.chatId) {
                this.loadOpportunityChat();
              }

              // Actualizar propiedades computadas
              this.updateComputedProperties();

              // Emitir evento de actualización para notificar a componentes padre
              this.updateEvent.emit({
                ...this.opportunity,
                skipReload: true,
              });
            }
          }

          // Forzar detección de cambios
          this.cdr.detectChanges();
        },
        error: (error: any) => {
          console.error('Error al recargar la oportunidad:', error);
          // Al menos actualizar propiedades computadas
          this.updateComputedProperties();
          this.cdr.detectChanges();
        },
      });
  }

  ngOnDestroy(): void {
    this.drawerEscapeBlocker.unblock();
  }
}
